import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import "../../../../App.css";
import Navbar from "../../../ui/Navbar";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import FileUploadSection from "../../../ui/FileUploadSection";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalError,
  swalSuccess,
  swalConfirm,
  swalWarning,
} from "../../../../utils/swalAlert";
import Swal from "sweetalert2";
import { fetchWithAuth } from "../../../../utils/api";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import QuestionDiagramResizeModal from "../../../ui/QuestionDiagramResizeModal";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const formatLatexText = (text) => {
  if (!text || typeof text !== "string") return text;
  let processed = text;
  if (!processed.includes("$") && !processed.includes("\\(") && !processed.includes("\\[")) {
    processed = processed.split(" ").map((word) => {
      if (/(\\[a-zA-Z]+|\^|_)/.test(word)) {
        return `$${word}$`;
      }
      return word;
    }).join(" ");
  }
  processed = processed.replace(/(^|[^\\])\$([^$]+)\$/g, (match, before, math) => {
    if (math.includes('\\displaystyle')) return match;
    return `${before}$\\displaystyle ${math}$`;
  });
  processed = processed.replace(/\\\((.*?)\\\)/g, (match, math) => {
    if (math.includes('\\displaystyle')) return match;
    return `\\(\\displaystyle ${math}\\)`;
  });
  return processed;
};

const INVALID_REASON_OPTIONS = [
  { value: "Question Text Issue", label: "Question Text Issue" },
  { value: "Options Issue", label: "Options Issue" },
  { value: "Correct Options/Answer Issue", label: "Correct Options/Answer Issue" },
  { value: "Diagram Issue", label: "Diagram Issue" },
  { value: "Others", label: "Others" },
];

const LazyMarkdown = React.memo(({ children }) => (
  <ReactMarkdown
    children={children}
    remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
    rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
  />
));

// ─────────────────────────────────────────────────────────────
// Question validity helper
// A question is "invalid" when its stored correct answer doesn't
// match the answer key answer extracted from the answer key file.
// ─────────────────────────────────────────────────────────────
const isQuestionInvalid = (q) => {
  return q ? q.isInvalid === true : false;
};

export default function ManageLibrary() {
  const { isAuthenticated, primaryRole, user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Extracted Questions state ──
  const [extractedQuestions, setExtractedQuestions] = useState([]);
  const [extractedLibrary, setExtractedLibrary] = useState(null);

  // ── Libraries list state ──
  const [libraries, setLibraries] = useState([]);
  const [activeTab, setActiveTab] = useState("general");
  const [selectedTestFilter, setSelectedTestFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    setSelectedTestFilter("all");
  }, [activeTab]);

  // ── Library Extractor Token Usage state ──
  const [libraryUsageData, setLibraryUsageData] = useState([]);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [usageFromDate, setUsageFromDate] = useState("");
  const [usageToDate, setUsageToDate] = useState("");
  const [showDatePopup, setShowDatePopup] = useState(false);
  const [rangeOption, setRangeOption] = useState("Today");
  const [selectedLibraryType, setSelectedLibraryType] = useState("all");
  const [selectedTemplateId, setSelectedTemplateId] = useState("all");
  const [selectedUsageRowIds, setSelectedUsageRowIds] = useState(new Set());

  // ── Library Stats Tab State ──
  const [statsData, setStatsData] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [isRefreshingStats, setIsRefreshingStats] = useState(false);
  const [expandedTemplates, setExpandedTemplates] = useState(new Set());
  const [expandedSubjects, setExpandedSubjects] = useState(new Set());

  // ── Curriculum LOV options ──
  const [curriculumOptions, setCurriculumOptions] = useState([]);

  // ── Question Type LOV options ──
  const [questionTypeOptions, setQuestionTypeOptions] = useState([]);

  // ── Subject LOV options ──
  const [subjectOptions, setSubjectOptions] = useState([]);

  // ── Grade LOV options ──
  const [gradeOptions, setGradeOptions] = useState([]);

  // ── Standardized Test LOV options ──
  const [standardizedTestOptions, setStandardizedTestOptions] = useState([]);

  const generalLibraries = useMemo(() => {
    return libraries.filter((lib) => !lib.templateId);
  }, [libraries]);

  const templateLibraries = useMemo(() => {
    return libraries.filter((lib) => !!lib.templateId);
  }, [libraries]);

  const formattedTemplateLibraries = useMemo(() => {
    return templateLibraries.map((lib) => {
      if (
        lib.subject &&
        typeof lib.subject === "string" &&
        lib.subject.trim().toLowerCase() === "mixed"
      ) {
        let mappedSubject = null;
        const foundTest = standardizedTestOptions.find(
          (opt) =>
            opt.value === lib.templateId || opt.templateId === lib.templateId
        );
        if (foundTest && foundTest.subjects && foundTest.subjects.length > 0) {
          mappedSubject = foundTest.subjects.map((s) => s.name || s).join(", ");
        } else {
          // Fallback to statsData
          const statTemplate = statsData.find(t => t.templateId === lib.templateId);
          if (statTemplate && statTemplate.subjects) {
            const subjectsArr = Object.keys(statTemplate.subjects);
            if (subjectsArr.length > 0) {
              mappedSubject = subjectsArr.join(", ");
            }
          }
        }
        if (mappedSubject) {
          return {
            ...lib,
            subject: mappedSubject,
          };
        }
      }
      if (Array.isArray(lib.subject)) {
        return { ...lib, subject: lib.subject.join(", ") };
      }
      return lib;
    });
  }, [templateLibraries, standardizedTestOptions, statsData]);

  const filteredTemplateLibraries = useMemo(() => {
    if (selectedTestFilter === "all") {
      return formattedTemplateLibraries;
    }
    const selectedOpt = standardizedTestOptions.find(
      (opt) =>
        opt.value === selectedTestFilter ||
        opt.templateId === selectedTestFilter,
    );
    if (!selectedOpt) return [];
    return formattedTemplateLibraries.filter((lib) => {
      return (
        lib.templateId === selectedOpt.value ||
        lib.templateId === selectedOpt.templateId
      );
    });
  }, [formattedTemplateLibraries, selectedTestFilter, standardizedTestOptions]);

  // ── Library Stats ──
  const libraryStats = useMemo(() => {
    const targetLibraries =
      activeTab === "general" ? generalLibraries : filteredTemplateLibraries;
    const total = targetLibraries.length;
    const ready = targetLibraries.filter(
      (lib) => lib.progress?.isLibraryReady,
    ).length;
    const processing = targetLibraries.filter(
      (lib) => lib.status === "Requested",
    ).length;
    const failed = targetLibraries.filter(
      (lib) => lib.status === "Failed",
    ).length;
    const pendingAudit = targetLibraries.filter(
      (lib) =>
        lib.status !== "Requested" &&
        lib.status !== "Failed" &&
        !lib.progress?.isLibraryReady,
    ).length;
    return { total, ready, processing, failed, pendingAudit };
  }, [activeTab, generalLibraries, filteredTemplateLibraries]);

  const getTemplateName = useCallback(
    (row) => {
      const templateId = row.templateId;
      if (!templateId) return "—";
      const found = standardizedTestOptions.find(
        (opt) => opt.value === templateId || opt.templateId === templateId,
      );
      return found ? found.label : templateId;
    },
    [standardizedTestOptions],
  );

  // ── Library Questions state ──
  const [viewingLibrary, setViewingLibrary] = useState(null);
  const [libraryQuestions, setLibraryQuestions] = useState([]);
  const [uploadingQuestions, setUploadingQuestions] = useState({});
  const [isBulkEditMode, setIsBulkEditMode] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({});
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(new Set());
  const [showInvalidOnly, setShowInvalidOnly] = useState(false);
  const [subjectFilter, setSubjectFilter] = useState(null); // { subject, type: 'all'|'valid'|'invalid' }
  const [invalidSubFilter, setInvalidSubFilter] = useState("all"); // 'all' | 'ai' | 'user'
  const [qNumberSearchQuery, setQNumberSearchQuery] = useState("");
  const tableRef = useRef(null);

  const aiInvalidCount = useMemo(() => {
    return libraryQuestions.filter(
      (q) =>
        isQuestionInvalid(q) &&
        q.invalidReason === "Generated Solution mismatch with given answer key",
    ).length;
  }, [libraryQuestions]);

  const userInvalidCount = useMemo(() => {
    return libraryQuestions.filter(
      (q) =>
        isQuestionInvalid(q) &&
        q.invalidReason !== "Generated Solution mismatch with given answer key",
    ).length;
  }, [libraryQuestions]);

  // ── Per-subject validity stats ──
  const subjectStats = useMemo(() => {
    const map = {};
    libraryQuestions.forEach((q) => {
      const subj = q.subject || "ALL";
      if (!map[subj])
        map[subj] = { subject: subj, total: 0, valid: 0, invalid: 0, aiInvalid: 0, userInvalid: 0 };
      map[subj].total++;
      if (isQuestionInvalid(q)) {
        map[subj].invalid++;
        if (q.invalidReason === "Generated Solution mismatch with given answer key") {
          map[subj].aiInvalid++;
        } else {
          map[subj].userInvalid++;
        }
      } else {
        map[subj].valid++;
      }
    });
    return Object.values(map).sort((a, b) =>
      a.subject.localeCompare(b.subject),
    );
  }, [libraryQuestions]);

  // ── Filtered questions for table (based on invalid filter + subject filter) ──
  const questionIndexMap = useMemo(() => {
    const map = new Map();
    libraryQuestions.forEach((q, i) => {
      map.set(q, i);
      if (q && q._id) {
        map.set(q._id, i);
      }
    });
    return map;
  }, [libraryQuestions]);

  const displayedQuestions = useMemo(() => {
    let base = libraryQuestions;

    // Subject-level filter takes priority when active
    if (subjectFilter) {
      base = libraryQuestions.filter(
        (q) => (q.subject || "Unknown") === subjectFilter.subject,
      );
      if (subjectFilter.type === "invalid") {
        base = base.filter(isQuestionInvalid);
        if (invalidSubFilter === "ai") {
          base = base.filter((q) => q.invalidReason === "Generated Solution mismatch with given answer key");
        } else if (invalidSubFilter === "user") {
          base = base.filter((q) => q.invalidReason !== "Generated Solution mismatch with given answer key");
        }
      } else if (subjectFilter.type === "valid") {
        base = base.filter((q) => !isQuestionInvalid(q));
      }
    } else {
      // Global invalid-only filter
      if (showInvalidOnly) {
        base = libraryQuestions.filter(isQuestionInvalid);
        if (invalidSubFilter === "ai") {
          base = base.filter((q) => q.invalidReason === "Generated Solution mismatch with given answer key");
        } else if (invalidSubFilter === "user") {
          base = base.filter((q) => q.invalidReason !== "Generated Solution mismatch with given answer key");
        }
      }
    }

    // Filter by comma-separated list of question numbers
    if (qNumberSearchQuery.trim()) {
      const targetNumbers = qNumberSearchQuery
        .split(",")
        .map((num) => num.trim())
        .filter((num) => num !== "");

      if (targetNumbers.length > 0) {
        base = base.filter((q) => {
          const qNumStr = q.questionNumber !== undefined && q.questionNumber !== null
            ? String(q.questionNumber).trim()
            : "";
          return targetNumbers.includes(qNumStr);
        });
      }
    }

    // Inject _rowStyle for invalid rows and imageFilterStatus for filtering
    return base.map((q) => {
      const hasImage = !!(q.diagramFileURL || q.imageURL || q.svgCode);
      const isRecommended = !!q.diagramSuggested;
      let imageFilterStatus = "No Image";
      if (hasImage) {
        imageFilterStatus = "Has Image";
      } else if (isRecommended) {
        imageFilterStatus = "Recommended";
      }

      const isAIInvalid = isQuestionInvalid(q) && q.invalidReason === "Generated Solution mismatch with given answer key";

      return {
        ...q,
        _originalIndex: questionIndexMap.get(q),
        imageFilterStatus,
        ...(isQuestionInvalid(q)
          ? {
            _rowStyle: isAIInvalid
              ? {
                background: "rgba(254,202,202,0.38)",
                borderLeft: "3px solid #ef4444",
              }
              : {
                background: "rgba(255, 237, 213, 0.75)",
                borderLeft: "4px solid #f97316",
              },
          }
          : {}),
      };
    });
  }, [libraryQuestions, showInvalidOnly, subjectFilter, questionIndexMap, invalidSubFilter, qNumberSearchQuery]);

  const totalInvalid = useMemo(
    () => libraryQuestions.filter(isQuestionInvalid).length,
    [libraryQuestions],
  );

  const totalWithImage = useMemo(
    () =>
      libraryQuestions.filter(
        (q) => !!(q.diagramFileURL || q.imageURL || q.svgCode),
      ).length,
    [libraryQuestions],
  );

  const totalRecommended = useMemo(
    () =>
      libraryQuestions.filter(
        (q) =>
          q.diagramSuggested && !(q.diagramFileURL || q.imageURL || q.svgCode),
      ).length,
    [libraryQuestions],
  );

  // ── QB Type / Extract PDF Modals ──
  const [libraryCreationMode, setLibraryCreationMode] = useState(null); // "general" or "template"
  const [isQBTypeModalOpen, setIsQBTypeModalOpen] = useState(false);
  const [isExtractPdfOpen, setIsExtractPdfOpen] = useState(false);
  const [isExtractingPdf, setIsExtractingPdf] = useState(false);
  const [generatingPdfId, setGeneratingPdfId] = useState(null);
  const [generatingAnswerKeyId, setGeneratingAnswerKeyId] = useState(null);
  const [generatingAISolutionsId, setGeneratingAISolutionsId] = useState(null);
  const [extractPdfProgress, setExtractPdfProgress] = useState("");
  const [extractPdfForm, setExtractPdfForm] = useState({
    libraryName: "",
    standardizedTestId: "",
    subject: "",
    topic: "",
    grade: "",
    curriculum: "",
    numberOfQs: "",
    customInstructions: "",
    sourceTestPaper: null,
    answerKeysFile: null,
    year: "",
    isPastExam: false,
  });

  // ── Generate Similar Modals & Form State ──
  const [isGenerateSimilarOpen, setIsGenerateSimilarOpen] = useState(false);
  const [isGeneratingSimilar, setIsGeneratingSimilar] = useState(false);
  const [generateSimilarProgress, setGenerateSimilarProgress] = useState("");
  const [generateSimilarForm, setGenerateSimilarForm] = useState({
    libraryName: "",
    subject: "",
    topic: "",
    grade: "",
    curriculum: "",
    numberOfQs: "",
    customInstructions: "",
    sourceTestPaper: null,
    year: "",
  });

  // ── Solution Modal ──
  const [isSolutionModalOpen, setIsSolutionModalOpen] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState(null);

  // ── Question Preview Modal ──
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  // ── Invalidity Info Modal ──
  const [isInvalidInfoModalOpen, setIsInvalidInfoModalOpen] = useState(false);
  const [invalidInfoQuestion, setInvalidInfoQuestion] = useState(null);

  // ── Edit Modal ──
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);
  const [editForm, setEditForm] = useState({
    questionText: "",
    topic: "",
    subTopic: "",
    type: "",
    difficulty: "",
    solution: "",
    correctOptions: "",
    answerKeyCorrectOptions: "",
    subject: "",
    sectionNumber: "",
    questionNumber: "",
    correctAnswer: "",
    answerKeyCorrectAnswer: "",
    isPastExam: false,
  });
  const [initialEditForm, setInitialEditForm] = useState({ ...editForm });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // ── Edit Library Modal ──
  const [isEditLibraryModalOpen, setIsEditLibraryModalOpen] = useState(false);
  const [editLibrary, setEditLibrary] = useState(null);
  const [editLibraryForm, setEditLibraryForm] = useState({
    libraryName: "",
    subject: "",
    topic: "",
    grade: "",
    curriculum: "",
    year: "",
  });
  const [initialEditLibraryForm, setInitialEditLibraryForm] = useState({
    ...editLibraryForm,
  });
  const [isSavingLibraryEdit, setIsSavingLibraryEdit] = useState(false);

  // ── Image / Diagram Modals ──
  const [isDiagramImageModalOpen, setIsDiagramImageModalOpen] = useState(false);
  const [diagramQuestion, setDiagramQuestion] = useState(null);
  const [selectedImageQuestion, setSelectedImageQuestion] = useState(null);
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
  const [selectedUploadFile, setSelectedUploadFile] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isDeletingDiagram, setIsDeletingDiagram] = useState(false);
  const [deletingDiagramId, setDeletingDiagramId] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [isViewQuestionsModalOpen, setIsViewQuestionsModalOpen] =
    useState(false);

  // ── Progress Audit Modal State ──
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [progressLibrary, setProgressLibrary] = useState(null);
  const [newIssueText, setNewIssueText] = useState("");
  const [isSavingProgress, setIsSavingProgress] = useState(false);
  const [loadingToggles, setLoadingToggles] = useState({
    isInvalidQuestionResolved: false,
    isQuestionDiagramsReviewed: false,
    isLibraryReady: false,
  });

  // ── Invalidity Toggle Modal States ──
  const [isInvalidToggleModalOpen, setIsInvalidToggleModalOpen] = useState(false);
  const [invalidToggleQuestion, setInvalidToggleQuestion] = useState(null);
  const [invalidToggleValue, setInvalidToggleValue] = useState(false);
  const [invalidReasonValue, setInvalidReasonValue] = useState("");
  const [otherInvalidReasonValue, setOtherInvalidReasonValue] = useState("");
  const [isSavingInvalidToggle, setIsSavingInvalidToggle] = useState(false);

  // ── Download Options Modal State ──
  const [downloadModal, setDownloadModal] = useState({
    isOpen: false,
    libraryId: null,
    libraryName: null,
    type: null // "AI Solution" or "Answer Key"
  });

  // ── Answer Validation Modal States ──
  const [isValModalOpen, setIsValModalOpen] = useState(false);
  const [valJsonInput, setValJsonInput] = useState("");
  const [valCsvData, setValCsvData] = useState(null);
  const [valCsvFileName, setValCsvFileName] = useState("");
  const [valResults, setValResults] = useState(null);
  const [valError, setValError] = useState(null);
  const [valFilter, setValFilter] = useState("all"); // "all", "match", "mismatch", "missing"
  const [isValViewActive, setIsValViewActive] = useState(false);
  const [valSelectedQuestions, setValSelectedQuestions] = useState(new Set());
  const [applyAllAutoSave, setApplyAllAutoSave] = useState(true);
  const [noCropDefault, setNoCropDefault] = useState(true);
  const [removeBgDefault, setRemoveBgDefault] = useState(true);
  const [uploadProgressText, setUploadProgressText] = useState("");

  useEffect(() => {
    if (isFileUploadModalOpen) {
      setApplyAllAutoSave(true);
      setNoCropDefault(true);
      setRemoveBgDefault(true);
      setUploadProgressText("");
    }
  }, [isFileUploadModalOpen]);

  const getProgressPercentage = () => {
    if (uploadProgressText.includes("Uploading original image")) return "25%";
    if (uploadProgressText.includes("Applying no crop") || uploadProgressText.includes("Setting dimensions")) return "50%";
    if (uploadProgressText.includes("Removing background")) return "75%";
    if (uploadProgressText.includes("Saving final diagram")) return "90%";
    return "0%";
  };

  // ── Answer Validation Logic ──
  const handleValCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setValCsvData(null);
      setValCsvFileName("");
      return;
    }
    setValCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n');
      const csvData = {};
      lines.forEach((line, index) => {
        const parts = line.split(',');
        if (parts.length >= 2) {
          let qNum = parts[0].trim();
          let ans = parts.slice(1).join(',').trim();
          if (qNum.startsWith('"') && qNum.endsWith('"')) qNum = qNum.slice(1, -1);
          if (ans.startsWith('"') && ans.endsWith('"')) ans = ans.slice(1, -1);
          
          if (qNum) {
            if (index === 0 && qNum.toLowerCase().includes('question')) return;
            csvData[qNum] = ans;
          }
        }
      });
      setValCsvData(csvData);
    };
    reader.readAsText(file);
  };

  const handleValidateAnswerKey = () => {
    setValError(null);
    if (!valJsonInput.trim()) {
      setValError("Please paste a valid JSON object.");
      return;
    }

    let parsedJson;
    try {
      parsedJson = JSON.parse(valJsonInput.trim());
      if (typeof parsedJson !== "object" || parsedJson === null || Array.isArray(parsedJson)) {
        throw new Error("Answer key must be a JSON object mapping question numbers to answers.");
      }
    } catch (err) {
      setValError("Invalid JSON: " + err.message);
      return;
    }

    const normalize = (val, isMCQ) => {
      if (val === undefined || val === null) return "";
      
      const mapOption = (opt) => {
        return String(opt).trim().toLowerCase();
      };

      if (Array.isArray(val)) {
        return val.map(mapOption).sort().join(",");
      }

      const parts = String(val)
        .split(/[\s,;]+/)
        .filter(Boolean)
        .map(mapOption);

      return parts.sort().join(",");
    };

    let baseKeys = [];
    if (valCsvData) {
      baseKeys = Object.keys(valCsvData);
    } else {
      baseKeys = libraryQuestions.map((q, index) => q.questionNumber ? String(q.questionNumber) : String(index + 1));
    }

    let total = baseKeys.length;
    let matched = 0;
    let mismatched = 0;
    let missingInJson = 0;
    let missingInLibrary = 0;

    const details = baseKeys.map((qNum) => {
      const q = libraryQuestions.find((q, index) => {
        const qn = q.questionNumber ? String(q.questionNumber) : String(index + 1);
        return qn === String(qNum);
      });

      const isMCQ = q ? (q.type || "").toLowerCase() === "mcq" : false;
      
      let aiAnsRaw = "";
      let aiAnsNorm = "";

      if (valCsvData) {
        aiAnsRaw = valCsvData[qNum] !== undefined ? String(valCsvData[qNum]) : "";
        aiAnsNorm = normalize(aiAnsRaw, isMCQ);
      } else {
        if (q) {
          aiAnsRaw = isMCQ
            ? (Array.isArray(q.correctOptions) ? q.correctOptions.join(", ") : (q.correctOptions || ""))
            : String(q.correctAnswer || "").trim();
          aiAnsNorm = normalize(isMCQ ? q.correctOptions : q.correctAnswer, isMCQ);
        }
      }

      const userAnsRaw = parsedJson[qNum];
      const hasUserAns = userAnsRaw !== undefined;

      const userAnsNorm = normalize(userAnsRaw, isMCQ);

      let status = "mismatch";
      let isMatch = false;

      if (!hasUserAns) {
        missingInJson++;
        status = "missing";
      } else if (aiAnsNorm === userAnsNorm) {
        matched++;
        isMatch = true;
        status = "match";
      } else {
        mismatched++;
      }

      return {
        qNum,
        qId: q ? q._id : null,
        qText: q ? (q.questionText || "No text available") : "Not in Library",
        type: q ? (q.type || "MCQ") : "—",
        aiAns: aiAnsRaw || "—",
        userAns: hasUserAns ? String(userAnsRaw) : "—",
        isMatch,
        status,
        subject: q ? (q.subject || "—") : "—",
        topic: q ? (q.topic || "—") : "—",
        isInvalid: q ? (q.isInvalid || false) : false,
        invalidReason: q ? (q.invalidReason || "") : ""
      };
    });

    const baseKeysSet = new Set(baseKeys);
    const extraDetails = [];
    Object.keys(parsedJson).forEach((key) => {
      if (!baseKeysSet.has(key)) {
        missingInLibrary++;
        extraDetails.push({
          qNum: key,
          qId: null,
          qText: valCsvData ? "Not in CSV" : "Not in Library",
          type: "—",
          aiAns: "—",
          userAns: String(parsedJson[key]),
          isMatch: false,
          status: "extra",
          subject: "—",
          topic: "—",
          isInvalid: false,
          invalidReason: ""
        });
      }
    });

    setValResults({
      stats: {
        total,
        matched,
        mismatched,
        missingInJson,
        missingInLibrary
      },
      details: [...details, ...extraDetails]
    });
  };

  // ── Year Options (Current year and past 10 years) ──
  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => {
      const yr = String(current - i);
      return { value: yr, label: yr };
    });
  }, []);

  const valStatusMap = useMemo(() => {
    if (!valResults) return new Map();
    const map = new Map();
    valResults.details.forEach((d) => {
      if (d.qId) {
        map.set(d.qId, d.status);
      }
    });
    return map;
  }, [valResults]);

  // ─────────────────────────────────────────────────────────────
  // Breadcrumbs & create buttons for Navbar
  // ─────────────────────────────────────────────────────────────
  const breadcrumbs = [
    { name: "Home", path: "/superadmin", isLast: false },
    {
      name: "Library",
      path: "/superadmin/library",
      isLast: !viewingLibrary,
      onClick: viewingLibrary
        ? () => {
          const newParams = new URLSearchParams(searchParams);
          newParams.delete("viewLibrary");
          setSearchParams(newParams);
        }
        : undefined,
    },
    ...(viewingLibrary
      ? [
        {
          name:
            viewingLibrary.libraryName ||
            viewingLibrary.subject ||
            "Questions",
          path: "",
          isLast: true,
        },
      ]
      : []),
  ];

  const createButtons = [];

  const isSubmitDisabled = () => {
    if (isExtractingPdf) return true;
    if (!extractPdfForm.sourceTestPaper) return true;
    if (!String(extractPdfForm.libraryName || "").trim()) return true;

    if (libraryCreationMode === "template") {
      if (!extractPdfForm.standardizedTestId) return true;
    } else {
      if (!String(extractPdfForm.curriculum || "").trim()) return true;
      if (!String(extractPdfForm.grade || "").trim()) return true;
      if (!extractPdfForm.numberOfQs) return true;
    }
    return false;
  };

  // ─────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────
  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => swalSuccess("Copied to clipboard!"))
      .catch((err) => swalError("Failed to copy: " + err));
  };

  // ── Library Extractor Usage Fetch & Calculations ──
  const userDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60000);
    return localDate.toISOString().slice(0, 10);
  };

  const displayDateOnly = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(navigator.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getTimeRangeText = () => {
    if (rangeOption === "Custom") {
      return usageFromDate && usageToDate
        ? `${displayDateOnly(usageFromDate)} - ${displayDateOnly(usageToDate)}`
        : "Select Date Range";
    }
    return rangeOption;
  };

  const setTodayRange = () => {
    const today = new Date();
    const startOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    setUsageFromDate(userDate(startOfDay));
    setUsageToDate(userDate(today));
    setRangeOption("Today");
  };

  const handlePresetSelect = (option) => {
    setRangeOption(option);
    const now = new Date();
    let start, end;

    switch (option) {
      case "Today":
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = now;
        break;
      case "Last 24 Hours":
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        end = now;
        break;
      case "This Month":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case "Custom":
      default:
        return;
    }

    const formatDate = (date) => {
      const offset = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() - offset * 60000);
      return localDate.toISOString().slice(0, 10);
    };

    setUsageFromDate(formatDate(start));
    setUsageToDate(formatDate(end));
  };

  const fetchLibraryTokenUsage = async () => {
    try {
      setLoadingUsage(true);
      const params = new URLSearchParams();
      if (usageFromDate) params.append("fromDate", usageFromDate);
      if (usageToDate) {
        const endDateObj = new Date(usageToDate);
        endDateObj.setDate(endDateObj.getDate() + 1);
        const adjustedEndDate = endDateObj.toISOString().slice(0, 10);
        params.append("toDate", adjustedEndDate);
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/token-usage/get-library-usage?${params.toString()}`,
        { method: "GET", credentials: "include" },
      );
      if (!response) return;
      const data = await response.json();
      if (response.ok) {
        const mappedData = data.map((item, idx) => ({
          ...item,
          _id: item._id
            ? `${item._id}-${idx}`
            : `usage-row-${item.libraryId || "lib"}-${idx}`,
        }));
        setLibraryUsageData(mappedData);
      } else {
        throw new Error(data.message || "Failed to fetch library usage data");
      }
    } catch (err) {
      swalError(err.message || "Failed to fetch library usage data");
    } finally {
      setLoadingUsage(false);
    }
  };

  useEffect(() => {
    if (activeTab === "usages") {
      setTodayRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "usages" && usageFromDate && usageToDate) {
      fetchLibraryTokenUsage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, usageFromDate, usageToDate]);

  // Reset selected row ids when filters or data changes
  useEffect(() => {
    setSelectedUsageRowIds(new Set());
  }, [libraryUsageData, selectedLibraryType, selectedTemplateId]);

  const fetchLibraryStats = async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoadingStats(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/get-stats`,
        { method: "GET", credentials: "include" },
      );
      if (!response) return;
      const data = await response.json();
      if (response.ok) {
        setStatsData(data);
      } else {
        throw new Error(data.message || "Failed to fetch stats");
      }
    } catch (err) {
      swalError(err.message || "Failed to fetch stats");
    } finally {
      if (!isRefresh) setLoadingStats(false);
    }
  };

  useEffect(() => {
    if (activeTab === "stats") {
      fetchLibraryStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const toggleTemplate = (templateId) => {
    setExpandedTemplates((prev) => {
      const next = new Set(prev);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      return next;
    });
  };

  const toggleSubject = (templateId, subjectName) => {
    const key = `${templateId}-${subjectName}`;
    setExpandedSubjects((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const overallStats = useMemo(() => {
    let templatesCount = statsData.length;
    let librariesCount = 0;
    let totalQs = 0;
    let invalidQs = 0;
    let userInvalidQs = 0;
    let validQs = 0;
    let diagramQs = 0;

    statsData.forEach((t) => {
      librariesCount += t.libraryCount;
      t.subjects.forEach((s) => {
        totalQs += s.totalQuestions;
        invalidQs += s.invalidQuestions;
        userInvalidQs += s.userInvalidQuestions || 0;
        validQs += s.validQuestions;
        diagramQs += s.diagramQuestions;
      });
    });

    return {
      templatesCount,
      librariesCount,
      totalQs,
      invalidQs,
      userInvalidQs,
      validQs,
      diagramQs,
    };
  }, [statsData]);

  const filteredUsageData = useMemo(() => {
    return libraryUsageData
      .filter((row) => {
        const lib = libraries.find((l) => l._id === row.libraryId);
        const isTemplate = lib ? !!lib.templateId : false;

        // Filter by library type
        if (selectedLibraryType === "template" && !isTemplate) return false;
        if (selectedLibraryType === "general" && isTemplate) return false;

        // Filter by template name if selected
        if (selectedLibraryType !== "general" && selectedTemplateId !== "all") {
          if (!lib || !lib.templateId) return false;
          // Check if lib.templateId matches selectedTemplateId
          const matches =
            lib.templateId === selectedTemplateId ||
            standardizedTestOptions.find(
              (opt) => opt.value === selectedTemplateId,
            )?.templateId === lib.templateId ||
            standardizedTestOptions.find(
              (opt) => opt.templateId === selectedTemplateId,
            )?.value === lib.templateId;
          if (!matches) return false;
        }

        return true;
      })
      .map((row) => {
        const lib = libraries.find((l) => l._id === row.libraryId);
        const tName = lib ? getTemplateName(lib) : "—";
        return {
          ...row,
          templateName: tName,
        };
      });
  }, [
    libraryUsageData,
    libraries,
    selectedLibraryType,
    selectedTemplateId,
    standardizedTestOptions,
    getTemplateName,
  ]);

  const usageStats = useMemo(() => {
    let input = 0;
    let output = 0;
    let total = 0;
    let cost = 0;

    const targetRows =
      selectedUsageRowIds.size > 0
        ? filteredUsageData.filter((row) => selectedUsageRowIds.has(row._id))
        : filteredUsageData;

    targetRows.forEach((row) => {
      input += row.inputTokenCount || 0;
      output += row.outputTokenCount || 0;
      total += row.totalTokenCount || 0;
      cost += row.totalCost || 0;
    });
    return {
      input,
      output,
      total,
      cost: cost * 90,
    };
  }, [filteredUsageData, selectedUsageRowIds]);

  const handleUsageRowSelect = useCallback((e, rowId) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setSelectedUsageRowIds((prev) => {
      const next = new Set(prev);
      next.has(rowId) ? next.delete(rowId) : next.add(rowId);
      return next;
    });
  }, []);

  const handleUsageSelectAll = useCallback(
    (visibleItemIds, isCurrentlyChecked) => {
      setSelectedUsageRowIds((prev) => {
        const next = new Set(prev);
        if (isCurrentlyChecked) {
          visibleItemIds.forEach((id) => next.delete(id));
        } else {
          visibleItemIds.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [],
  );

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-IN").format(num || 0);
  };

  const usageTableColumns = [
    {
      key: "libraryName",
      label: "Library Name [ID]",
      sortable: true,
      render: (row) => (
        <span>
          {row.libraryName || "Deleted Library"}{" "}
          <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
            [{row.libraryId}]
          </span>
        </span>
      ),
    },

    {
      key: "questionCount",
      label: "Questions Extracted",
      sortable: true,
      render: (row) => <span>{formatNumber(row.questionCount)}</span>,
    },
    {
      key: "createdAt",
      label: "Date",
      sortable: true,
      render: (row) => (
        <span>
          {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "—"}
        </span>
      ),
    },
    {
      key: "cachedInputTokenCount",
      label: "Cached Input Tokens",
      sortable: true,
      render: (row) => (
        <span className="token-count input">
          {formatNumber(row.cachedInputTokenCount)}
        </span>
      ),
    },
    {
      key: "inputTokenCount",
      label: "Input Tokens",
      sortable: true,
      render: (row) => (
        <span className="token-count input">
          {formatNumber(row.inputTokenCount)}
        </span>
      ),
    },
    {
      key: "outputTokenCount",
      label: "Output Tokens",
      sortable: true,
      render: (row) => (
        <span className="token-count output">
          {formatNumber(row.outputTokenCount)}
        </span>
      ),
    },
    {
      key: "thoughtsTokenCount",
      label: "Thoughts Tokens",
      sortable: true,
      render: (row) => (
        <span className="token-count output" style={{ color: "#8b5cf6" }}>
          {formatNumber(row.thoughtsTokenCount)}
        </span>
      ),
    },
    {
      key: "totalTokenCount",
      label: "Total Tokens",
      sortable: true,
      render: (row) => (
        <span className="token-count total">
          {formatNumber(row.totalTokenCount)}
        </span>
      ),
    },
    {
      key: "totalCost",
      label: "Cost (₹)",
      sortable: true,
      render: (row) => (
        <span
          className="cost-value"
          style={{ fontWeight: 600, color: "#ea580c" }}
        >
          ₹{((row.totalCost || 0) * 90).toFixed(2)}
        </span>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────
  // Fetch Libraries
  // ─────────────────────────────────────────────────────────────
  const fetchLibraries = async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/get-libraries`,
        { method: "GET", credentials: "include" },
      );
      if (!response) return;
      const data = await response.json();
      if (response.ok) {
        setLibraries(data);
      } else {
        throw new Error(data.message || "Failed to fetch libraries");
      }
    } catch (err) {
      swalError(err.message || "Failed to fetch libraries");
    } finally {
      if (!isRefresh) setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Fetch Question Type LOVs
  // ─────────────────────────────────────────────────────────────
  const fetchQuestionTypeLOVs = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/QB-questionType`,
        { method: "GET", credentials: "include" },
      );
      if (!response || !response.ok) return;
      const data = await response.json();
      const options = data.map((item) => ({
        value: item.value,
        label: item.value,
      }));
      setQuestionTypeOptions(options);
    } catch (err) {
      console.error("Failed to fetch Question Type LOVs:", err);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Fetch Curriculum LOVs
  // ─────────────────────────────────────────────────────────────
  const fetchCurriculumLOVs = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/curriculum`,
        { method: "GET", credentials: "include" },
      );
      if (!response || !response.ok) return;
      const data = await response.json();
      const options = data.map((item) => ({
        value: item.value,
        label: item.value,
      }));
      setCurriculumOptions(options);
    } catch (err) {
      console.error("Failed to fetch curriculum LOVs:", err);
    }
  };

  // ── Fetch Subject LOVs ──
  const fetchSubjectLOVs = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/k12-Subjects`,
        { method: "GET", credentials: "include" },
      );
      if (!response || !response.ok) return;
      const data = await response.json();
      // Sort by displayOrder ascending
      const sortedData = data.sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0),
      );
      const options = sortedData.map((item) => ({
        value: item.value,
        label: item.value,
      }));
      setSubjectOptions(options);
    } catch (err) {
      console.error("Failed to fetch subject LOVs:", err);
    }
  };

  // ── Fetch Grade LOVs ──
  const fetchGradeLOVs = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/level`,
        { method: "GET", credentials: "include" },
      );
      if (!response || !response.ok) return;
      const data = await response.json();
      // Sort by displayOrder ascending
      const sortedData = data.sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0),
      );
      const options = sortedData.map((item) => ({
        value: item.value,
        label: item.value,
      }));
      setGradeOptions(options);
    } catch (err) {
      console.error("Failed to fetch grade LOVs:", err);
    }
  };

  // ── Fetch Standardized Test LOVs ──
  const fetchStandardizedTestLOVs = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/standardized-test/get-all`,
        { method: "GET", credentials: "include" },
      );
      if (!response || !response.ok) return;
      const data = await response.json();
      const options = (data.data || data).map((item) => ({
        value: item._id,
        label: item.templateName,
        templateId: item.templateId,
        subjects: item.subjects || [],
      }));
      setStandardizedTestOptions(options);
    } catch (err) {
      console.error("Failed to fetch Standardized Test LOVs:", err);
    }
  };

  useEffect(() => {
    fetchLibraries();
    fetchCurriculumLOVs();
    fetchSubjectLOVs();
    fetchGradeLOVs();
    fetchStandardizedTestLOVs();
    fetchQuestionTypeLOVs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handlePaste = (e) => {
      if (!isFileUploadModalOpen) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            setSelectedUploadFile(file);
          }
          break; // only take the first image
        }
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, [isFileUploadModalOpen]);

  // Sync library view state based on URL's 'viewLibrary' search parameter
  useEffect(() => {
    const libId = searchParams.get("viewLibrary");
    if (libId) {
      if (
        libraries.length > 0 &&
        (!viewingLibrary || viewingLibrary._id !== libId)
      ) {
        const libToView = libraries.find((l) => l._id === libId);
        if (libToView) {
          viewLibraryQuestions(libToView);
        } else {
          // Fallback for libraries that might not be in the initial libraries list
          viewLibraryQuestions({
            _id: libId,
            status: "Completed",
          });
        }
      }
    } else {
      if (viewingLibrary) {
        setViewingLibrary(null);
        setLibraryQuestions([]);
        setQNumberSearchQuery("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, libraries, viewingLibrary]);

  // Polling for libraries when one of them is in "Requested" state
  useEffect(() => {
    const hasRequested = libraries.some((lib) => lib.status === "Requested");
    if (!hasRequested) return;

    const interval = setInterval(() => {
      fetchWithAuth(`${serverUrl}/api/library-mcq-bank/get-libraries`, {
        method: "GET",
        credentials: "include",
      })
        .then((res) => {
          if (res && res.ok) {
            return res.json();
          }
        })
        .then((data) => {
          if (data) {
            setLibraries(data);
          }
        })
        .catch((err) => console.error("Error polling libraries:", err));
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [libraries]);

  // ─────────────────────────────────────────────────────────────
  // Fetch Questions for a Library
  // ─────────────────────────────────────────────────────────────
  const viewLibraryQuestions = async (library, event = null) => {
    if (event && (event.ctrlKey || event.metaKey)) {
      const url = new URL(window.location.href);
      url.searchParams.set("viewLibrary", library._id);
      window.open(url.toString(), "_blank");
      return;
    }

    if (library.status === "Requested") {
      swalWarning(
        "This library is currently being extracted. Please wait for it to complete.",
      );
      return;
    }
    if (library.status === "Failed") {
      swalError(
        "This library extraction failed. Please delete it and try again.",
      );
      return;
    }
    try {
      setLoadingQuestions(true);
      setViewingLibrary(library);
      setSelectedQuestionIds(new Set());
      setShowInvalidOnly(false);
      setQNumberSearchQuery("");
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/get-questions/${library._id}`,
        { method: "GET", credentials: "include" },
      );
      if (!response) return;
      const data = await response.json();
      if (response.ok) {
        setLibraryQuestions(data);
        const newParams = new URLSearchParams(searchParams);
        newParams.set("viewLibrary", library._id);
        setSearchParams(newParams);
      } else {
        throw new Error(data.message || "Failed to get questions");
      }
    } catch (err) {
      swalError(err.message || "Failed to load library questions");
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("viewLibrary");
      setSearchParams(newParams);
    } finally {
      setLoadingQuestions(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Helper to open PDF blob with proper tab title and download name
  // ─────────────────────────────────────────────────────────────
  const openPdfBlobWithTitle = (pdfBlob, filename) => {
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
      return;
    }

    const pdfWindow = window.open("", "_blank");
    if (pdfWindow) {
      pdfWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${filename}</title>
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            body { margin: 0; padding: 0; background-color: #525659; display: flex; flex-direction: column; height: 100vh; font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; overflow: hidden; }
            .header { background-color: #323639; color: #f1f1f1; padding: 12px 24px; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 2px 4px rgba(0,0,0,0.2); z-index: 10; height: 32px; flex-shrink: 0; }
            .title { font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 60%; font-weight: 500; }
            .download-btn { background-color: #1a73e8; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 13px; font-weight: 600; transition: background-color 0.2s; display: flex; align-items: center; gap: 8px; }
            .download-btn:hover { background-color: #1557b0; }
            .download-btn svg { width: 16px; height: 16px; fill: currentColor; }
            .content-area { flex: 1; display: flex; flex-direction: column; position: relative; }
            iframe { width: 100%; height: 100%; border: none; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">${filename}</div>
            <button id="custom-download-btn" class="download-btn">
              <svg viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
              Download PDF
            </button>
          </div>
          <div class="content-area">
            <iframe src="${pdfUrl}"></iframe>
          </div>
        </body>
        </html>
      `);
      pdfWindow.document.close();

      const downloadBtn = pdfWindow.document.getElementById("custom-download-btn");
      if (downloadBtn) {
        downloadBtn.addEventListener("click", () => {
          const a = document.createElement("a");
          a.href = pdfUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        });
      }
    } else {
      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
  };

  // ─────────────────────────────────────────────────────────────
  // Generate Library as PDF
  // ─────────────────────────────────────────────────────────────
  const handleGenerateLibraryPDF = async (e, libraryId, libraryName) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setGeneratingPdfId(libraryId);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/generate-question-paper/${libraryId}`,
        { method: "GET", credentials: "include" }
      );
      if (!response) return;
      if (response.ok) {
        const pdfBlob = await response.blob();
        const cleanName = libraryName ? libraryName.replace(/[^a-zA-Z0-9_\- ]/g, "").trim() : "";
        const filename = `${cleanName ? cleanName + "_Question_Paper" : "Question_Paper"}.pdf`;
        openPdfBlobWithTitle(pdfBlob, filename);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to generate question paper");
      }
    } catch (err) {
      swalError(err.message || "Failed to download PDF");
    } finally {
      setGeneratingPdfId(null);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Generate Library AI Solutions as PDF
  // ─────────────────────────────────────────────────────────────
  const handleGenerateAISolutionsPDF = async (e, libraryId, libraryName) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setGeneratingAISolutionsId(libraryId);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/generate-answer-key/${libraryId}?generateSolution=true`,
        { method: "GET", credentials: "include" }
      );
      if (!response) return;
      if (response.ok) {
        const pdfBlob = await response.blob();
        const cleanName = libraryName ? libraryName.replace(/[^a-zA-Z0-9_\- ]/g, "").trim() : "";
        const filename = `${cleanName ? cleanName + "_AI_Solutions" : "AI_Solutions"}.pdf`;
        openPdfBlobWithTitle(pdfBlob, filename);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to generate AI Solutions");
      }
    } catch (err) {
      swalError(err.message || "Failed to download AI Solutions PDF");
    } finally {
      setGeneratingAISolutionsId(null);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Generate Library Answer Key as PDF
  // ─────────────────────────────────────────────────────────────
  const handleGenerateAnswerKeyPDF = async (e, libraryId, libraryName) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setGeneratingAnswerKeyId(libraryId);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/generate-answer-key/${libraryId}?useAnswerKeyFields=true&generateSolution=false`,
        { method: "GET", credentials: "include" }
      );
      if (!response) return;
      if (response.ok) {
        const pdfBlob = await response.blob();
        const cleanName = libraryName ? libraryName.replace(/[^a-zA-Z0-9_\- ]/g, "").trim() : "";
        const filename = `${cleanName ? cleanName + "_Answer_Key" : "Answer_Key"}.pdf`;
        openPdfBlobWithTitle(pdfBlob, filename);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to generate answer key");
      }
    } catch (err) {
      swalError(err.message || "Failed to download Answer Key PDF");
    } finally {
      setGeneratingAnswerKeyId(null);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Generate Library AI Solutions or Answer Key as CSV
  // ─────────────────────────────────────────────────────────────
  const handleDownloadCSV = async (libraryId, libraryName, type) => {
    if (type === "AI Solution") setGeneratingAISolutionsId(libraryId);
    else setGeneratingAnswerKeyId(libraryId);
    
    setDownloadModal({ isOpen: false, libraryId: null, libraryName: null, type: null });

    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/get-questions/${libraryId}`,
        { method: "GET", credentials: "include" }
      );
      if (!response) return;
      const data = await response.json();
      if (response.ok) {
        if (!data || data.length === 0) {
          swalWarning("No questions found in this library.");
          return;
        }

        let csvContent = "Question Number,Answer\n";
        
        data.forEach((q, index) => {
          let ans = "";
          if (type === "AI Solution") {
            ans = q.correctOptions ? q.correctOptions : (q.correctAnswer || "");
          } else {
            ans = q.answerKeyCorrectOptions ? q.answerKeyCorrectOptions : (q.answerKeyCorrectAnswer || "");
          }
          if (ans.includes(",")) {
            ans = `"${ans.replace(/"/g, '""')}"`;
          }
          const qNum = q.questionNumber || (index + 1);
          csvContent += `${qNum},${ans}\n`;
        });
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${libraryName}_${type.replace(" ", "_")}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        throw new Error(data.message || "Failed to fetch questions");
      }
    } catch (err) {
      swalError(err.message || `Failed to download ${type} CSV`);
    } finally {
      if (type === "AI Solution") setGeneratingAISolutionsId(null);
      else setGeneratingAnswerKeyId(null);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Delete Library
  // ─────────────────────────────────────────────────────────────
  const handleDeleteLibrary = async (libraryId, libraryName) => {
    const result = await swalConfirm(
      "Delete Library",
      `Are you sure you want to delete "${libraryName || "this library"}"? This will permanently delete the library and all of its questions.`,
      "Yes, delete it!",
      "No, Cancel",
    );
    if (result.isConfirmed) {
      try {
        setIsDeleting(true);
        setDeletingId(libraryId);
        const response = await fetchWithAuth(
          `${serverUrl}/api/library-mcq-bank/delete-library/${libraryId}`,
          { method: "DELETE", credentials: "include" },
        );
        if (!response) return;
        const data = await response.json();
        if (response.ok) {
          swalSuccess("Library deleted successfully");
          fetchLibraries();
        } else {
          throw new Error(data.message || "Failed to delete library");
        }
      } catch (err) {
        swalError(err.message);
      } finally {
        setIsDeleting(false);
        setDeletingId(null);
      }
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Retry Failed Range Extraction
  // ─────────────────────────────────────────────────────────────
  const handleRetryFailedRange = async (
    libraryId,
    startQuestion,
    endQuestion,
  ) => {
    const result = await swalConfirm(
      "Retry Extraction Range",
      `Are you sure you want to retry extracting questions from ${startQuestion} to ${endQuestion}?`,
      "Yes, Retry",
      "Cancel",
    );
    if (!result.isConfirmed) return;

    // Optimistically update library status to "Requested" in the UI
    setLibraries((prev) =>
      prev.map((lib) =>
        lib._id === libraryId ? { ...lib, status: "Requested" } : lib,
      ),
    );

    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/retry-failed-range`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            libraryId,
            startQuestion,
            endQuestion,
          }),
        },
      );
      if (!response) return;
      const data = await response.json();
      if (response.ok) {
        swalSuccess(data.message || "Retry initiated successfully.");
        // Use the returned library object to update libraries state while preserving signed URL
        if (data.library) {
          setLibraries((prev) =>
            prev.map((lib) =>
              lib._id === libraryId
                ? {
                  ...data.library,
                  questionSourceFileURL: lib.questionSourceFileURL, // Preserve the existing signed URL
                }
                : lib,
            ),
          );
        } else {
          fetchLibraries();
        }
      } else {
        // Fetch to reset the state if request failed
        fetchLibraries();
        throw new Error(data.message || "Failed to retry extraction");
      }
    } catch (err) {
      swalError(err.message || "An error occurred while retrying extraction.");
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Delete Library Question
  // ─────────────────────────────────────────────────────────────
  const handleDeleteQuestion = async (question) => {
    const result = await swalConfirm(
      "Delete Question",
      "Are you sure you want to delete this question? This action cannot be undone.",
      "<i class='fa-solid fa-trash'></i> Delete",
      "Cancel",
    );
    if (!result.isConfirmed) return;
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/delete-question/${question._id}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!response) return;
      const data = await response.json();
      if (response.ok) {
        swalSuccess("Question deleted successfully.");
        setLibraryQuestions((prev) =>
          prev.filter((q) => q._id !== question._id),
        );
        setSelectedQuestionIds((prev) => {
          const next = new Set(prev);
          next.delete(question._id);
          return next;
        });
      } else {
        throw new Error(data.message || "Failed to delete question");
      }
    } catch (err) {
      swalError(err.message);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Delete Library Question Diagram
  // ─────────────────────────────────────────────────────────────
  const handleDeleteDiagram = async (question) => {
    const result = await swalConfirm(
      "Delete Diagram",
      "Are you sure you want to delete this question diagram?",
      "Yes, delete it!",
      "Cancel",
    );
    if (!result.isConfirmed) return;

    try {
      setIsDeletingDiagram(true);
      setDeletingDiagramId(question._id);
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/delete-question-diagram/${question._id}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!response) {
        setIsDeletingDiagram(false);
        setDeletingDiagramId(null);
        return;
      }
      const data = await response.json();
      if (response.ok) {
        swalSuccess("Diagram deleted successfully.");

        const updatedFields = {
          diagramFileURL: null,
          diagramPageFileURL: null,
          diagramBoundingBox: null,
          diagramSuggested: false,
        };

        setLibraryQuestions((prev) =>
          prev.map((q) =>
            q._id === question._id ? { ...q, ...updatedFields } : q
          )
        );

        setDiagramQuestion((prev) =>
          prev && prev._id === question._id ? { ...prev, ...updatedFields } : prev
        );
        setSelectedImageQuestion((prev) =>
          prev && prev._id === question._id ? { ...prev, ...updatedFields } : prev
        );

        setIsDiagramImageModalOpen(false);
      } else {
        throw new Error(data.message || "Failed to delete diagram");
      }
    } catch (err) {
      swalError(err.message || "Failed to delete diagram");
    } finally {
      setIsDeletingDiagram(false);
      setDeletingDiagramId(null);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Show Solution / Question Preview
  // ─────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────
  // Image / Diagram Helpers
  // ─────────────────────────────────────────────────────────────
  const handleDiagramSaveSuccess = useCallback(
    (questionId, newDiagramFileURL, newBoundingBox) => {
      setLibraryQuestions((prev) =>
        prev.map((q) =>
          q._id === questionId
            ? {
              ...q,
              diagramFileURL: newDiagramFileURL,
              diagramBoundingBox: newBoundingBox,
            }
            : q,
        ),
      );
      setExtractedQuestions((prev) =>
        prev.map((q) =>
          q._id === questionId
            ? {
              ...q,
              diagramFileURL: newDiagramFileURL,
              diagramBoundingBox: newBoundingBox,
            }
            : q,
        ),
      );
      setDiagramQuestion((prev) => {
        if (prev && prev._id === questionId) {
          return {
            ...prev,
            diagramFileURL: newDiagramFileURL,
            diagramBoundingBox: newBoundingBox,
          };
        }
        return prev;
      });
    },
    [],
  );

  const handleUploadLibraryDiagramFile = async (directFile, directQuestion) => {
    const isDirectFile = directFile instanceof File;
    const isDirect = isDirectFile || applyAllAutoSave;
    const fileToUpload = isDirectFile ? directFile : selectedUploadFile;
    const questionToProcess = isDirectFile ? directQuestion : selectedImageQuestion;
    if (!fileToUpload || !questionToProcess) return;

    if (!isDirectFile && applyAllAutoSave) {
      setIsFileUploadModalOpen(false);
      setSelectedUploadFile(null);
    }

    const processImageLocally = async (imgFile, shouldRemoveBg, shouldNoCrop, boundingBox) => {
      const localUrl = URL.createObjectURL(imgFile);
      const image = new Image();
      image.src = localUrl;
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error("Failed to load image locally"));
      });
      
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(image, 0, 0);

      if (shouldRemoveBg) {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const threshold = 215;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] >= threshold && data[i + 1] >= threshold && data[i + 2] >= threshold) {
            data[i + 3] = 0;
          }
        }
        
        // Edge smoothing
        const width = canvas.width;
        const height = canvas.height;
        const alphaOriginal = new Uint8Array(data.length / 4);
        for (let i = 0; i < alphaOriginal.length; i++) {
          alphaOriginal[i] = data[i * 4 + 3];
        }
        for (let y = 1; y < height - 1; y++) {
          for (let x = 1; x < width - 1; x++) {
            const idx = y * width + x;
            const a = alphaOriginal[idx];
            if (a === 0) continue;
            const neighbors = [
              alphaOriginal[idx - 1], alphaOriginal[idx + 1],
              alphaOriginal[idx - width], alphaOriginal[idx + width],
            ];
            const transparentNeighbors = neighbors.filter(n => n === 0).length;
            if (transparentNeighbors > 0 && transparentNeighbors < 4) {
              const factor = 1 - (transparentNeighbors / 4) * 0.5;
              data[idx * 4 + 3] = Math.round(a * factor);
            }
          }
        }
        ctx.putImageData(imageData, 0, 0);
      }

      let crop = { x: 0, y: 0, width: 100, height: 100 };
      if (!shouldNoCrop) {
        if (boundingBox && Array.isArray(boundingBox) && boundingBox.length === 4) {
          const [ymin, xmin, ymax, xmax] = boundingBox;
          crop = {
            x: xmin / 10,
            y: ymin / 10,
            width: (xmax - xmin) / 10,
            height: (ymax - ymin) / 10,
          };
        } else {
          crop = { x: 10, y: 10, width: 80, height: 80 };
        }
      }

      const pixelX = (crop.x / 100) * canvas.width;
      const pixelY = (crop.y / 100) * canvas.height;
      const pixelWidth = Math.max(1, Math.round((crop.width / 100) * canvas.width));
      const pixelHeight = Math.max(1, Math.round((crop.height / 100) * canvas.height));

      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = pixelWidth;
      finalCanvas.height = pixelHeight;
      const fCtx = finalCanvas.getContext("2d");
      fCtx.drawImage(canvas, pixelX, pixelY, pixelWidth, pixelHeight, 0, 0, pixelWidth, pixelHeight);

      const blob = await new Promise(resolve => finalCanvas.toBlob(resolve, "image/png"));
      URL.revokeObjectURL(localUrl);

      const ymin = Math.round(crop.y * 10);
      const xmin = Math.round(crop.x * 10);
      const ymax = Math.round((crop.y + crop.height) * 10);
      const xmax = Math.round((crop.x + crop.width) * 10);
      const finalBoundingBox = [ymin, xmin, ymax, xmax];

      return { blob, boundingBox: finalBoundingBox };
    };

    const updateProgressText = (text) => {
      if (isDirect) {
        setUploadingQuestions((prev) => {
          if (!prev[questionToProcess._id]) return prev;
          return {
            ...prev,
            [questionToProcess._id]: {
              ...prev[questionToProcess._id],
              progressText: text,
            },
          };
        });
      } else {
        setUploadProgressText(text);
      }
    };

    try {
      if (isDirect) {
        setUploadingQuestions((prev) => ({
          ...prev,
          [questionToProcess._id]: {
            progressText: "Uploading original image...",
          },
        }));
      } else {
        setIsUploadingFile(true);
      }
      updateProgressText("Uploading original image...");
      const formData = new FormData();
      formData.append("image", fileToUpload);

      const endpoint = `${serverUrl}/api/library-mcq-bank/new-question-diagram/${questionToProcess._id}`;
      const response = await fetchWithAuth(endpoint, {
        method: "POST",
        body: formData,
      });

      if (!response) {
        if (!isDirect) {
          setIsUploadingFile(false);
          setUploadProgressText("");
        } else {
          setUploadingQuestions((prev) => {
            const copy = { ...prev };
            delete copy[questionToProcess._id];
            return copy;
          });
        }
        return;
      }

      const data = await response.json();
      if (response.ok) {
        const useAutoSave = isDirect ? true : applyAllAutoSave;
        const useNoCrop = isDirect ? true : noCropDefault;
        const useRemoveBg = isDirect ? true : removeBgDefault;

        if (useAutoSave) {
          try {
            updateProgressText(useNoCrop ? "Applying no crop..." : "Setting dimensions...");
            await new Promise((resolve) => setTimeout(resolve, 500));

            if (useRemoveBg) {
              updateProgressText("Removing background...");
              await new Promise((resolve) => setTimeout(resolve, 600));
            }

            const { blob, boundingBox: finalBoundingBox } = await processImageLocally(
              fileToUpload,
              useRemoveBg,
              useNoCrop,
              questionToProcess.diagramBoundingBox
            );

            updateProgressText("Saving final diagram...");
            await new Promise((resolve) => setTimeout(resolve, 400));

            const file = new File([blob], `cropped_diagram_${questionToProcess._id}.png`, {
              type: "image/png",
            });

            const updateFormData = new FormData();
            updateFormData.append("image", file);
            updateFormData.append("diagramBoundingBox", JSON.stringify(finalBoundingBox));

            const updateEndpoint = `${serverUrl}/api/library-mcq-bank/update-question-diagram/${questionToProcess._id}`;
            const updateResponse = await fetchWithAuth(updateEndpoint, {
              method: "POST",
              body: updateFormData,
            });

            if (updateResponse) {
              const updateData = await updateResponse.json();
              if (updateResponse.ok) {
                //swalSuccess(updateData.message || "Diagram processed and saved successfully");

                const finalQuestion = {
                  ...questionToProcess,
                  diagramPageFileURL: data.diagramPageFileURL,
                  diagramFileURL: updateData.diagramFileURL,
                  diagramBoundingBox: updateData.diagramBoundingBox || finalBoundingBox,
                };

                const updater = (prev) =>
                  prev.map((q) => (q._id === finalQuestion._id ? finalQuestion : q));
                setLibraryQuestions(updater);
                setExtractedQuestions(updater);

                if (!isDirect) {
                  setIsFileUploadModalOpen(false);
                  setSelectedUploadFile(null);
                  setIsUploadingFile(false);
                  setUploadProgressText("");
                  swalSuccess("Diagram processed and saved successfully");
                } else {
                  setUploadingQuestions((prev) => {
                    const copy = { ...prev };
                    delete copy[questionToProcess._id];
                    return copy;
                  });
                  const Toast = Swal.mixin({
                    toast: true,
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                    showClass: {
                      popup: "swal-toast-in",
                    },
                    hideClass: {
                      popup: "swal-toast-out",
                    },
                  });
                  Toast.fire({
                    icon: "success",
                    title: "Diagram processed and saved successfully",
                  });
                }
                return;
              }
            }
          } catch (processErr) {
            console.error("Auto-process failed, falling back to manual crop modal:", processErr);
            updateProgressText("");
          }
        }

        // Standard manual crop modal flow (or fallback)
        if (isDirect) {
          const Toast = Swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            showClass: {
              popup: "swal-toast-in",
            },
            hideClass: {
              popup: "swal-toast-out",
            },
          });
          Toast.fire({
            icon: "error",
            title: "Failed to auto-process diagram",
            text: data.message || "Failed to auto-process diagram",
          });
          setUploadingQuestions((prev) => {
            const copy = { ...prev };
            delete copy[questionToProcess._id];
            return copy;
          });
          return;
        }
        swalSuccess(data.message || "Diagram updated successfully");

        const updatedQuestion = {
          ...questionToProcess,
          diagramPageFileURL: data.diagramPageFileURL,
          diagramFileURL: null,
          diagramBoundingBox: null,
        };

        setLibraryQuestions((prev) =>
          prev.map((q) =>
            q._id === updatedQuestion._id ? updatedQuestion : q,
          ),
        );
        setExtractedQuestions((prev) =>
          prev.map((q) =>
            q._id === updatedQuestion._id ? updatedQuestion : q,
          ),
        );
        setDiagramQuestion(updatedQuestion);
        setIsFileUploadModalOpen(false);
        setSelectedUploadFile(null);
        setIsCropModalOpen(true);
      } else {
        if (isDirect) {
          const Toast = Swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            showClass: {
              popup: "swal-toast-in",
            },
            hideClass: {
              popup: "swal-toast-out",
            },
          });
          Toast.fire({
            icon: "error",
            title: "Failed to upload diagram",
            text: data.message || "Failed to upload diagram",
          });
        } else {
          swalError(data.message || "Failed to upload diagram");
        }
      }
    } catch (err) {
      console.error("Error uploading library diagram:", err);
      if (isDirect) {
        const Toast = Swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          showClass: {
            popup: "swal-toast-in",
          },
          hideClass: {
            popup: "swal-toast-out",
          },
        });
        Toast.fire({
          icon: "error",
          title: "Upload failed",
          text: err.message || "Failed to upload question diagram.",
        });
      } else {
        swalError("Failed to upload question diagram.");
      }
    } finally {
      if (!isDirect) {
        setIsUploadingFile(false);
        setUploadProgressText("");
      } else {
        setUploadingQuestions((prev) => {
          const copy = { ...prev };
          delete copy[questionToProcess._id];
          return copy;
        });
      }
    }
  };

  const handlePasteImage = async (q) => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.read) {
        swalError("Clipboard API is not supported in this browser or environment (requires HTTPS/localhost).");
        return;
      }

      const clipboardItems = await navigator.clipboard.read();
      let imageBlob = null;
      for (const item of clipboardItems) {
        for (const type of item.types) {
          if (type.startsWith("image/")) {
            imageBlob = await item.getType(type);
            break;
          }
        }
        if (imageBlob) break;
      }

      if (!imageBlob) {
        swalError("No image found in clipboard. Please copy an image first!");
        return;
      }

      const file = new File([imageBlob], "pasted_image.png", { type: imageBlob.type });

      setDiagramQuestion(q);
      setSelectedImageQuestion(q);

      await handleUploadLibraryDiagramFile(file, q);
    } catch (err) {
      console.error("Clipboard paste error:", err);
      if (err.name === "NotAllowedError") {
        swalError("Clipboard access denied. Please allow clipboard permissions to paste images.");
      } else {
        swalError("Failed to read image from clipboard: " + err.message);
      }
    }
  };

  const ImageWithErrorHandler = ({ src, alt, style }) => {
    const [hasError, setHasError] = useState(false);
    if (hasError || !src) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "#f8fafc",
            border: "1.5px solid #e2e8f0",
            borderRadius: "8px",
            padding: "12px 16px",
            fontSize: "0.92rem",
            color: "#6b7280",
            ...style,
          }}
        >
          <i
            className="fa-solid fa-triangle-exclamation"
            style={{ color: "#ef4444" }}
          />
          <span>Image unavailable</span>
        </div>
      );
    }
    return (
      <img
        src={src}
        alt={alt}
        style={style}
        onError={() => setHasError(true)}
      />
    );
  };

  const showSolution = (question) => {
    setActiveQuestion(question);
    setIsSolutionModalOpen(true);
  };

  const showQuestion = (question) => {
    setSelectedQuestion(question);
    setIsQuestionModalOpen(true);
  };

  // ─────────────────────────────────────────────────────────────
  // Open Edit Modal
  // ─────────────────────────────────────────────────────────────
  const handleOpenEditModal = (question) => {
    setEditQuestion(question);
    const formValues = {
      questionText: question.questionText || "",
      topic: question.topic || "",
      subTopic: question.subTopic || "",
      type: question.type || question.questionType || "",
      difficulty: question.difficulty || "",
      solution: question.solution || "",
      correctOptions: Array.isArray(question.correctOptions)
        ? question.correctOptions.join(", ")
        : question.correctOptions || "",
      answerKeyCorrectOptions: Array.isArray(question.answerKeyCorrectOptions)
        ? question.answerKeyCorrectOptions.join(", ")
        : question.answerKeyCorrectOptions || "",
      subject: question.subject || "",
      sectionNumber: question.sectionNumber || question.section || "",
      questionNumber: question.questionNumber || "",
      correctAnswer: question.correctAnswer || "",
      answerKeyCorrectAnswer: question.answerKeyCorrectAnswer || "",
      isPastExam: question.isPastExam || false,
      isInvalid: question.isInvalid || false,
      invalidReason: question.invalidReason || "",
      otherInvalidReason: "",
      options:
        question.options && Array.isArray(question.options)
          ? JSON.parse(JSON.stringify(question.options))
          : [],
    };
    setEditForm(formValues);
    setInitialEditForm(formValues);
    setIsEditModalOpen(true);
  };

  // ─────────────────────────────────────────────────────────────
  // Save Edit Question
  // ─────────────────────────────────────────────────────────────
  const handleSaveEditQuestion = async (e) => {
    e.preventDefault();
    if (!editQuestion) return;
    setIsSavingEdit(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/update-question/${editQuestion._id}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            questionText: editForm.questionText,
            topic: editForm.topic,
            subTopic: editForm.subTopic,
            type: editForm.type,
            difficulty: editForm.difficulty,
            solution: editForm.solution,
            correctOptions: editForm.correctOptions
              ? editForm.correctOptions
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
              : [],
            answerKeyCorrectOptions: editForm.answerKeyCorrectOptions
              ? editForm.answerKeyCorrectOptions
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
              : [],
            subject: editForm.subject,
            sectionNumber: editForm.sectionNumber,
            section: editForm.sectionNumber,
            questionNumber: editForm.questionNumber,
            correctAnswer: editForm.correctAnswer,
            answerKeyCorrectAnswer: editForm.answerKeyCorrectAnswer,
            isPastExam: editForm.isPastExam,
            options: Array.isArray(editForm.options)
              ? editForm.options.filter(Boolean)
              : [],
            isInvalid: editForm.isInvalid,
            invalidReason: editForm.invalidReason === "Others" ? editForm.otherInvalidReason : editForm.invalidReason,
          }),
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to update question");
      swalSuccess("Question updated successfully.");
      const updatedFields = {
        questionText: editForm.questionText,
        topic: editForm.topic,
        subTopic: editForm.subTopic,
        type: editForm.type,
        questionType: editForm.type,
        difficulty: editForm.difficulty,
        solution: editForm.solution,
        correctOptions: editForm.correctOptions
          ? editForm.correctOptions
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
          : [],
        answerKeyCorrectOptions: editForm.answerKeyCorrectOptions
          ? editForm.answerKeyCorrectOptions
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
          : [],
        subject: editForm.subject,
        sectionNumber: editForm.sectionNumber,
        section: editForm.sectionNumber,
        questionNumber: editForm.questionNumber,
        correctAnswer: editForm.correctAnswer,
        answerKeyCorrectAnswer: editForm.answerKeyCorrectAnswer,
        isPastExam: editForm.isPastExam,
        options: Array.isArray(editForm.options)
          ? editForm.options.filter(Boolean)
          : [],
        isInvalid: editForm.isInvalid,
        invalidReason: editForm.invalidReason === "Others" ? editForm.otherInvalidReason : editForm.invalidReason,
      };

      setLibraryQuestions((prev) =>
        prev.map((q) =>
          q._id === editQuestion._id
            ? { ...q, ...updatedFields }
            : q,
        ),
      );

      setDiagramQuestion((prev) =>
        prev && prev._id === editQuestion._id ? { ...prev, ...updatedFields } : prev
      );
      setSelectedQuestion((prev) =>
        prev && prev._id === editQuestion._id ? { ...prev, ...updatedFields } : prev
      );
      setActiveQuestion((prev) =>
        prev && prev._id === editQuestion._id ? { ...prev, ...updatedFields } : prev
      );
      setIsEditModalOpen(false);
      setEditQuestion(null);
    } catch (err) {
      swalError(err.message);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const hasBulkChanges = useMemo(() => {
    return Object.values(bulkEditData).some((data) => {
      const textChanged = data.questionText !== data.original.questionText;
      const optionsChanged = JSON.stringify(data.options) !== JSON.stringify(data.original.options);
      return textChanged || optionsChanged;
    });
  }, [bulkEditData]);

  const handleStartBulkEdit = (displayedQs) => {
    const initData = {};
    displayedQs.forEach((q) => {
      initData[q._id] = {
        questionText: q.questionText || "",
        options: Array.isArray(q.options)
          ? JSON.parse(JSON.stringify(q.options))
          : [],
        original: {
          questionText: q.questionText || "",
          options: Array.isArray(q.options)
            ? JSON.parse(JSON.stringify(q.options))
            : [],
        },
      };
    });
    setBulkEditData(initData);
    setIsBulkEditMode(true);
  };

  const handleSaveBulkQuestions = async () => {
    const changedQuestions = Object.entries(bulkEditData).filter(([qId, data]) => {
      const textChanged = data.questionText !== data.original.questionText;
      const optionsChanged = JSON.stringify(data.options) !== JSON.stringify(data.original.options);
      return textChanged || optionsChanged;
    });

    if (changedQuestions.length === 0) {
      setIsBulkEditMode(false);
      return;
    }

    const savingStatus = {};
    changedQuestions.forEach(([qId]) => {
      savingStatus[qId] = { progressText: "Saving..." };
    });
    setUploadingQuestions((prev) => ({ ...prev, ...savingStatus }));

    const promises = changedQuestions.map(async ([qId, data]) => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/library-mcq-bank/update-question/${qId}`,
          {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              questionText: data.questionText,
              options: data.options.filter(Boolean),
            }),
          }
        );
        if (!response) throw new Error("No response from server");
        const resData = await response.json();
        if (!response.ok) {
          throw new Error(resData.message || "Failed to update question");
        }

        const updatedFields = {
          questionText: data.questionText,
          options: data.options.filter(Boolean),
        };

        setLibraryQuestions((prev) =>
          prev.map((q) => (q._id === qId ? { ...q, ...updatedFields } : q))
        );

        setBulkEditData((prev) => {
          if (!prev[qId]) return prev;
          return {
            ...prev,
            [qId]: {
              ...prev[qId],
              original: {
                questionText: data.questionText,
                options: JSON.parse(JSON.stringify(data.options)),
              },
            },
          };
        });
      } catch (err) {
        console.error(`Failed to save question ${qId}:`, err);
        swalError(`Error saving question: ${err.message}`);
      } finally {
        setUploadingQuestions((prev) => {
          const next = { ...prev };
          delete next[qId];
          return next;
        });
      }
    });

    await Promise.all(promises);
    setIsBulkEditMode(false);
    
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      showClass: {
        popup: "swal-toast-in",
      },
      hideClass: {
        popup: "swal-toast-out",
      },
    });
    Toast.fire({
      icon: "success",
      title: "All question changes saved successfully.",
    });
  };

  const handleOpenInvalidToggleModal = (question) => {
    setInvalidToggleQuestion(question);
    setInvalidToggleValue(question.isInvalid || false);
    setInvalidReasonValue(question.invalidReason || "");
    setOtherInvalidReasonValue("");
    setIsInvalidToggleModalOpen(true);
  };

  const handleBatchMarkValid = async () => {
    if (valSelectedQuestions.size === 0) return;
    setIsSavingInvalidToggle(true);
    try {
      const payload = {
        isInvalid: false,
        invalidReason: "",
      };
      
      const promises = Array.from(valSelectedQuestions).map(async (qId) => {
        const res = await fetchWithAuth(
          `${serverUrl}/api/library-mcq-bank/update-question/${qId}`,
          {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );
        if (!res || !res.ok) throw new Error("Failed to update one or more questions");
        return res.json();
      });
      await Promise.all(promises);
      
      swalSuccess("Selected questions marked as valid.");

      setLibraryQuestions((prev) =>
        prev.map((q) => {
          return valSelectedQuestions.has(q._id) ? { ...q, ...payload } : q;
        })
      );
      
      setValResults((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          details: prev.details.map((d) => {
            if (d.qId && valSelectedQuestions.has(d.qId)) {
              return { ...d, isInvalid: payload.isInvalid, invalidReason: payload.invalidReason };
            }
            return d;
          })
        };
      });
      
      setValSelectedQuestions(new Set());
    } catch (err) {
      console.error(err);
      swalError(err.message || "Failed to update questions");
    } finally {
      setIsSavingInvalidToggle(false);
    }
  };

  const handleSaveInvalidToggle = async (e) => {
    e.preventDefault();
    if (!invalidToggleQuestion) return;
    setIsSavingInvalidToggle(true);
    try {
      const payload = {
        isInvalid: invalidToggleValue,
        invalidReason: invalidToggleValue ? (invalidReasonValue === "Others" ? otherInvalidReasonValue : invalidReasonValue) : "",
      };

      if (invalidToggleQuestion.isBatch) {
        const promises = Array.from(valSelectedQuestions).map(async (qId) => {
          const res = await fetchWithAuth(
            `${serverUrl}/api/library-mcq-bank/update-question/${qId}`,
            {
              method: "PATCH",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            }
          );
          if (!res || !res.ok) throw new Error("Failed to update one or more questions");
          return res.json();
        });
        await Promise.all(promises);
        
        swalSuccess("Selected questions marked as invalid.");

        setLibraryQuestions((prev) =>
          prev.map((q) => {
            return valSelectedQuestions.has(q._id) ? { ...q, ...payload } : q;
          })
        );
        
        setValResults((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            details: prev.details.map((d) => {
              if (d.qId && valSelectedQuestions.has(d.qId)) {
                return { ...d, isInvalid: payload.isInvalid, invalidReason: payload.invalidReason };
              }
              return d;
            })
          };
        });
        
        setValSelectedQuestions(new Set());
      } else {
        const response = await fetchWithAuth(
          `${serverUrl}/api/library-mcq-bank/update-question/${invalidToggleQuestion._id}`,
          {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
        if (!response) return;
        const data = await response.json();
        if (!response.ok)
          throw new Error(data.message || "Failed to update invalid status");
        
        swalSuccess("Invalid status updated successfully.");
        
        setLibraryQuestions((prev) =>
          prev.map((q) =>
            q._id === invalidToggleQuestion._id
              ? { ...q, ...payload }
              : q,
          ),
        );

        setDiagramQuestion((prev) =>
          prev && prev._id === invalidToggleQuestion._id ? { ...prev, ...payload } : prev
        );
        setSelectedQuestion((prev) =>
          prev && prev._id === invalidToggleQuestion._id ? { ...prev, ...payload } : prev
        );
        setActiveQuestion((prev) =>
          prev && prev._id === invalidToggleQuestion._id ? { ...prev, ...payload } : prev
        );
        
        setValResults((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            details: prev.details.map((d) => {
              if (d.qId === invalidToggleQuestion._id) {
                return { ...d, isInvalid: payload.isInvalid, invalidReason: payload.invalidReason };
              }
              return d;
            })
          };
        });
      }

      setIsInvalidToggleModalOpen(false);
      setInvalidToggleQuestion(null);
    } catch (err) {
      console.error(err);
      swalError(err.message || "Failed to update invalid status");
    } finally {
      setIsSavingInvalidToggle(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Open Edit Library Modal
  // ─────────────────────────────────────────────────────────────
  const handleOpenEditLibraryModal = (library) => {
    setEditLibrary(library);
    const formValues = {
      libraryName: library.libraryName || "",
      subject: library.subject || "",
      topic: library.topic || "",
      grade: library.grade || "",
      curriculum: library.curriculum || "",
      year: library.year || "",
    };
    setEditLibraryForm(formValues);
    setInitialEditLibraryForm(formValues);
    setIsEditLibraryModalOpen(true);
  };

  // ─────────────────────────────────────────────────────────────
  // Save Edit Library
  // ─────────────────────────────────────────────────────────────
  const handleSaveEditLibrary = async (e) => {
    e.preventDefault();
    if (!editLibrary) return;
    setIsSavingLibraryEdit(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/update-library/${editLibrary._id}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            libraryName: editLibraryForm.libraryName || "",
            subject: editLibraryForm.subject || "",
            topic: editLibraryForm.topic || "",
            grade: editLibraryForm.grade || "",
            curriculum: editLibraryForm.curriculum || "",
            year: editLibraryForm.year || "",
          }),
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to update library");

      swalSuccess("Library updated successfully.");
      const updatedLib = data.library;
      setLibraries((prev) =>
        prev.map((l) => (l._id === updatedLib._id ? updatedLib : l)),
      );
      if (viewingLibrary && viewingLibrary._id === updatedLib._id) {
        setViewingLibrary(updatedLib);
      }
      setIsEditLibraryModalOpen(false);
      setEditLibrary(null);
    } catch (err) {
      swalError(err.message);
    } finally {
      setIsSavingLibraryEdit(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Open Progress Audit Modal
  // ─────────────────────────────────────────────────────────────
  const handleOpenProgressModal = (library) => {
    setProgressLibrary(library);
    setNewIssueText("");
    setIsProgressModalOpen(true);
  };

  // ─────────────────────────────────────────────────────────────
  // Toggle Progress Audit Stage
  // ─────────────────────────────────────────────────────────────
  const handleToggleProgressStage = async (stageKey, currentValue) => {
    if (!progressLibrary) return;
    setLoadingToggles((prev) => ({ ...prev, [stageKey]: true }));
    try {
      const updatedProgress = {
        isInvalidQuestionResolved:
          progressLibrary.progress?.isInvalidQuestionResolved ?? false,
        isQuestionDiagramsReviewed:
          progressLibrary.progress?.isQuestionDiagramsReviewed ?? false,
        isLibraryReady: progressLibrary.progress?.isLibraryReady ?? false,
        ...progressLibrary.progress,
        [stageKey]: !currentValue,
      };

      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/update-library/${progressLibrary._id}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            progress: updatedProgress,
          }),
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to update library progress");

      const updatedLib = data.library;
      setLibraries((prev) =>
        prev.map((l) => (l._id === updatedLib._id ? updatedLib : l)),
      );
      setProgressLibrary(updatedLib);
      if (viewingLibrary && viewingLibrary._id === updatedLib._id) {
        setViewingLibrary(updatedLib);
      }
    } catch (err) {
      swalError(err.message);
    } finally {
      setLoadingToggles((prev) => ({ ...prev, [stageKey]: false }));
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Add Issue to Progress Audit
  // ─────────────────────────────────────────────────────────────
  const handleCreateIssue = async (e) => {
    if (e) e.preventDefault();
    if (!progressLibrary || !newIssueText.trim()) return;
    setIsSavingProgress(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/update-library/${progressLibrary._id}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            newIssue: newIssueText.trim(),
          }),
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to add issue");

      const updatedLib = data.library;
      setLibraries((prev) =>
        prev.map((l) => (l._id === updatedLib._id ? updatedLib : l)),
      );
      setProgressLibrary(updatedLib);
      setNewIssueText("");
      if (viewingLibrary && viewingLibrary._id === updatedLib._id) {
        setViewingLibrary(updatedLib);
      }
      swalSuccess("Issue added successfully.");
    } catch (err) {
      swalError(err.message);
    } finally {
      setIsSavingProgress(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Toggle Issue Solved Status
  // ─────────────────────────────────────────────────────────────
  const handleToggleIssueSolved = async (issueId, currentSolvedStatus) => {
    if (!progressLibrary) return;
    const updatedIssues = (progressLibrary.progress?.issues || []).map(
      (issue, idx) => {
        if (issue._id === issueId || idx === issueId) {
          return { ...issue, solved: !currentSolvedStatus };
        }
        return issue;
      },
    );

    setIsSavingProgress(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/update-library/${progressLibrary._id}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            progress: {
              ...progressLibrary.progress,
              issues: updatedIssues,
            },
          }),
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to update issue status");

      const updatedLib = data.library;
      setLibraries((prev) =>
        prev.map((l) => (l._id === updatedLib._id ? updatedLib : l)),
      );
      setProgressLibrary(updatedLib);
      if (viewingLibrary && viewingLibrary._id === updatedLib._id) {
        setViewingLibrary(updatedLib);
      }
    } catch (err) {
      swalError(err.message);
    } finally {
      setIsSavingProgress(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Generate Similar Form Submission
  // ─────────────────────────────────────────────────────────────
  const handleGenerateSimilar = async () => {
    if (!generateSimilarForm.curriculum?.trim()) {
      swalError("Curriculum is required.");
      return;
    }
    if (!generateSimilarForm.grade?.trim()) {
      swalError("Grade is required.");
      return;
    }
    if (!generateSimilarForm.libraryName?.trim()) {
      swalError("Library Name is required.");
      return;
    }
    if (!generateSimilarForm.sourceTestPaper) {
      swalError("Please upload a reference test paper.");
      return;
    }
    if (!generateSimilarForm.numberOfQs) {
      swalError("Please enter the number of questions to generate.");
      return;
    }
    const numQs = parseInt(generateSimilarForm.numberOfQs);
    if (isNaN(numQs) || numQs <= 0) {
      swalError("Please enter a valid number of questions to generate.");
      return;
    }

    setIsGeneratingSimilar(true);
    setGenerateSimilarProgress("Uploading test paper...");

    try {
      const formData = new FormData();
      formData.append(
        "subject",
        generateSimilarForm.subject?.trim() || "Mixed",
      );
      formData.append("sourceTestPaper", generateSimilarForm.sourceTestPaper);
      if (generateSimilarForm.libraryName?.trim())
        formData.append("libraryName", generateSimilarForm.libraryName.trim());
      if (generateSimilarForm.topic?.trim())
        formData.append("topic", generateSimilarForm.topic.trim());
      if (generateSimilarForm.grade?.trim())
        formData.append("grade", generateSimilarForm.grade.trim());
      if (generateSimilarForm.curriculum?.trim())
        formData.append("curriculum", generateSimilarForm.curriculum.trim());
      if (generateSimilarForm.year)
        formData.append("year", generateSimilarForm.year);
      formData.append("numberOfQs", generateSimilarForm.numberOfQs);
      if (generateSimilarForm.customInstructions?.trim())
        formData.append(
          "customInstructions",
          generateSimilarForm.customInstructions.trim(),
        );

      setGenerateSimilarProgress(
        "Generating similar questions from PDF... This may take a minute.",
      );

      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/generate-similar`,
        { method: "POST", credentials: "include", body: formData },
      );

      if (!response) {
        setIsGeneratingSimilar(false);
        return;
      }
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.message || "Failed to generate similar questions from PDF",
        );
      }

      setIsGenerateSimilarOpen(false);
      setGenerateSimilarForm({
        libraryName: "",
        subject: "",
        topic: "",
        grade: "",
        curriculum: "",
        numberOfQs: "",
        customInstructions: "",
        sourceTestPaper: null,
        year: "",
      });

      await fetchLibraries();
      swalSuccess(
        "Library generation request submitted successfully! The questions are being generated in the background.",
      );
    } catch (error) {
      console.error("Error generating similar questions:", error);
      swalError(error.message || "Failed to generate similar questions.");
    } finally {
      setIsGeneratingSimilar(false);
      setGenerateSimilarProgress("");
    }
  };

  // ─────────────────────────────────────────────────────────────
  // PDF Extraction
  // ─────────────────────────────────────────────────────────────
  const handleExtractFromPdf = async () => {
    if (libraryCreationMode === "template") {
      if (!extractPdfForm.standardizedTestId) {
        swalError("Standardized Test is required.");
        return;
      }
    } else {
      if (!extractPdfForm.curriculum?.trim()) {
        swalError("Curriculum is required.");
        return;
      }
      if (!extractPdfForm.grade?.trim()) {
        swalError("Grade is required.");
        return;
      }
    }
    if (!extractPdfForm.libraryName?.trim()) {
      swalError("Library Name is required.");
      return;
    }
    if (!extractPdfForm.sourceTestPaper) {
      swalError("Please upload the test paper.");
      return;
    }
    const finalNumberOfQs =
      extractPdfForm.numberOfQs || ""
    if (!finalNumberOfQs) {
      swalError("Please enter the number of questions to extract.");
      return;
    }
    const numQs = parseInt(finalNumberOfQs);
    if (isNaN(numQs) || numQs <= 0) {
      swalError("Please enter a valid number of questions to extract.");
      return;
    }

    setIsExtractingPdf(true);
    setExtractPdfProgress("Uploading files...");

    try {
      const formData = new FormData();
      if (extractPdfForm.standardizedTestId) {
        formData.append(
          "standardizedTestId",
          extractPdfForm.standardizedTestId,
        );
      }
      formData.append("subject", extractPdfForm.subject?.trim() || "Mixed");
      formData.append("sourceTestPaper", extractPdfForm.sourceTestPaper);
      if (extractPdfForm.libraryName?.trim())
        formData.append("libraryName", extractPdfForm.libraryName.trim());
      if (extractPdfForm.topic?.trim())
        formData.append("topic", extractPdfForm.topic.trim());
      if (extractPdfForm.grade?.trim())
        formData.append("grade", extractPdfForm.grade.trim());
      if (extractPdfForm.curriculum?.trim())
        formData.append("curriculum", extractPdfForm.curriculum.trim());
      if (extractPdfForm.year) formData.append("year", extractPdfForm.year);
      formData.append("isPastExam", extractPdfForm.isPastExam);
      formData.append("numberOfQs", finalNumberOfQs);
      if (extractPdfForm.customInstructions?.trim())
        formData.append(
          "customInstructions",
          extractPdfForm.customInstructions.trim(),
        );
      if (extractPdfForm.answerKeysFile)
        formData.append("answerKeysFile", extractPdfForm.answerKeysFile);

      setExtractPdfProgress(
        "Extracting questions from PDF... This may take a minute.",
      );

      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/extract-questions`,
        { method: "POST", credentials: "include", body: formData },
      );

      if (!response) {
        setIsExtractingPdf(false);
        return;
      }
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to extract questions from PDF");
      }

      setIsExtractPdfOpen(false);
      setExtractPdfForm({
        libraryName: "",
        standardizedTestId: "",
        subject: "",
        topic: "",
        grade: "",
        curriculum: "",
        numberOfQs: "",
        customInstructions: "",
        sourceTestPaper: null,
        answerKeysFile: null,
        year: "",
        isPastExam: false,
      });

      await fetchLibraries();
      swalSuccess(
        "Library creation request submitted successfully! The questions are being extracted in the background.",
      );
    } catch (err) {
      console.error("Extraction error:", err);
      swalError(err.message || "Failed to extract questions.");
    } finally {
      setIsExtractingPdf(false);
      setExtractPdfProgress("");
    }
  };

  const handleReviewQuestionChange = async (index, field, value) => {
    setExtractedQuestions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    if (field === "correctOptions" || field === "correctAnswer") {
      const question = extractedQuestions[index];
      if (question && question._id) {
        try {
          const response = await fetchWithAuth(
            `${serverUrl}/api/library-mcq-bank/update-question/${question._id}`,
            {
              method: "PATCH",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                [field]: value,
              }),
            },
          );
          if (!response || !response.ok) {
            console.error(`Failed to sync ${field} with backend`);
          }
        } catch (err) {
          console.error(`Error updating question ${field}:`, err);
        }
      }
    }
  };

  const handleReviewQuestionRemove = async (index) => {
    const question = extractedQuestions[index];
    if (!question) return;

    const confirmed = await swalConfirm(
      "Delete Question",
      "Are you sure you want to delete this question? This will permanently delete it from the library.",
      "<i class='fa-solid fa-trash'></i> Delete",
      "Cancel",
    );
    if (!confirmed.isConfirmed) return;

    try {
      if (question._id) {
        const response = await fetchWithAuth(
          `${serverUrl}/api/library-mcq-bank/delete-question/${question._id}`,
          {
            method: "DELETE",
            credentials: "include",
          },
        );
        if (response && response.ok) {
          setExtractedQuestions((prev) => prev.filter((_, i) => i !== index));
          swalSuccess("Question deleted successfully.");
        } else {
          swalError("Failed to delete question from backend.");
        }
      }
    } catch (err) {
      console.error("Error deleting question:", err);
      swalError("Error deleting question: " + err.message);
    }
  };

  const handleFinishReview = async () => {
    setIsExtractPdfOpen(false);
    setExtractedQuestions([]);
    const savedLibrary = extractedLibrary;
    setExtractedLibrary(null);
    setExtractPdfForm({
      libraryName: "",
      standardizedTestId: "",
      subject: "",
      topic: "",
      grade: "",
      curriculum: "",
      numberOfQs: "",
      customInstructions: "",
      sourceTestPaper: null,
      answerKeysFile: null,
      year: "",
      isPastExam: false,
    });

    if (savedLibrary) {
      await viewLibraryQuestions(savedLibrary);
    }
    fetchLibraries();

    swalSuccess("Library questions successfully reviewed and saved!");
  };

  // ─────────────────────────────────────────────────────────────
  // Checkbox selection helpers
  // ─────────────────────────────────────────────────────────────
  const handleCheckboxSelect = useCallback((e, questionId) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setSelectedQuestionIds((prev) => {
      const next = new Set(prev);
      next.has(questionId) ? next.delete(questionId) : next.add(questionId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((visibleItemIds, isCurrentlyChecked) => {
    setSelectedQuestionIds((prev) => {
      const next = new Set(prev);
      if (isCurrentlyChecked) {
        visibleItemIds.forEach((id) => next.delete(id));
      } else {
        visibleItemIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Template Library list columns
  // ─────────────────────────────────────────────────────────────
  const renderLibraryProgress = (row) => {
    const isInvalidResolved = row.progress?.isInvalidQuestionResolved ?? false;
    const isDiagramsReviewed =
      row.progress?.isQuestionDiagramsReviewed ?? false;
    const isLibReady = row.progress?.isLibraryReady ?? false;

    const issues = row.progress?.issues || [];
    const pendingIssuesCount = issues.filter((issue) => !issue.solved).length;
    const hasIssues = issues.length > 0;

    if (isLibReady) {
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <div
            onClick={() => handleOpenProgressModal(row)}
            style={{
              background: "#ecfdf5",
              border: "1.5px solid rgba(16, 185, 129, 0.35)",
              borderRadius: "12px",
              padding: "10px 14px",
              boxShadow:
                "0 4px 6px -1px rgba(16, 185, 129, 0.05), 0 2px 4px -1px rgba(16, 185, 129, 0.03)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              cursor: "pointer",
              width: "100%",
              maxWidth: "160px",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              boxSizing: "border-box",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 8px 12px rgba(16,185,129,0.12)";
              e.currentTarget.style.borderColor = "#10b981";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow =
                "0 4px 6px -1px rgba(16, 185, 129, 0.05), 0 2px 4px -1px rgba(16, 185, 129, 0.03)";
              e.currentTarget.style.borderColor = "rgba(16, 185, 129, 0.35)";
            }}
            title="Click to view and audit progress"
          >
            <i
              className="fa-solid fa-circle-check"
              style={{ color: "#10b981", fontSize: "1.05rem" }}
            />
            <span
              style={{ fontSize: "0.82rem", fontWeight: 700, color: "#10b981" }}
            >
              Ready
            </span>
          </div>
        </div>
      );
    }

    let completedCount = 0;
    if (isInvalidResolved) completedCount++;
    if (isDiagramsReviewed) completedCount++;

    return (
      <div onClick={(e) => e.stopPropagation()}>
        <div
          onClick={() => handleOpenProgressModal(row)}
          style={{
            background: "#ffffff",
            border: `1.5px solid ${completedCount > 0 ? "rgba(245, 158, 11, 0.3)" : "rgba(239, 68, 68, 0.2)"}`,
            borderRadius: "12px",
            padding: "12px 14px",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            cursor: "pointer",
            width: "100%",
            maxWidth: "260px",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            boxSizing: "border-box",
            textAlign: "left",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-3px)";
            e.currentTarget.style.boxShadow =
              "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)";
            e.currentTarget.style.borderColor =
              completedCount > 0 ? "#f59e0b" : "#ef4444";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow =
              "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)";
            e.currentTarget.style.borderColor =
              completedCount > 0
                ? "rgba(245, 158, 11, 0.3)"
                : "rgba(239, 68, 68, 0.2)";
          }}
          title="Click to view and audit progress"
        >
          {/* Status Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: "0.78rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: completedCount > 0 ? "#d97706" : "#ef4444",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: completedCount > 0 ? "#f59e0b" : "#ef4444",
                  display: "inline-block",
                }}
              />
              Audit In Progress
            </span>
            <span
              style={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "#64748b",
                background: "#f1f5f9",
                padding: "2px 6px",
                borderRadius: "4px",
              }}
            >
              {completedCount}/2 Ready
            </span>
          </div>

          {/* Stage Indicator Row */}
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {/* Stage 1: Invalid */}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "0.7rem",
                fontWeight: 600,
                padding: "3px 7px",
                borderRadius: "6px",
                backgroundColor: isInvalidResolved ? "#ecfdf5" : "#f8fafc",
                color: isInvalidResolved ? "#10b981" : "#94a3b8",
                border: `1px solid ${isInvalidResolved ? "rgba(16, 185, 129, 0.15)" : "#e2e8f0"}`,
              }}
              title="Invalid Questions Resolved"
            >
              <i
                className={
                  isInvalidResolved
                    ? "fa-solid fa-circle-check"
                    : "fa-regular fa-circle"
                }
              />
              Invalid
            </span>

            {/* Stage 2: Diagrams */}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "0.7rem",
                fontWeight: 600,
                padding: "3px 7px",
                borderRadius: "6px",
                backgroundColor: isDiagramsReviewed ? "#ecfdf5" : "#f8fafc",
                color: isDiagramsReviewed ? "#10b981" : "#94a3b8",
                border: `1px solid ${isDiagramsReviewed ? "rgba(16, 185, 129, 0.15)" : "#e2e8f0"}`,
              }}
              title="Question Diagrams Reviewed"
            >
              <i
                className={
                  isDiagramsReviewed
                    ? "fa-solid fa-circle-check"
                    : "fa-regular fa-circle"
                }
              />
              Diagrams
            </span>
          </div>

          {/* Issues Indicator Row */}
          {hasIssues ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderTop: "1px dashed #e2e8f0",
                paddingTop: "8px",
                marginTop: "2px",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: pendingIssuesCount > 0 ? "#ef4444" : "#10b981",
                }}
              >
                <i
                  className={
                    pendingIssuesCount > 0
                      ? "fa-solid fa-circle-exclamation"
                      : "fa-solid fa-circle-check"
                  }
                />
                {pendingIssuesCount > 0
                  ? `${pendingIssuesCount} Pending`
                  : "All Solved"}
              </span>
              <span
                style={{
                  fontSize: "0.72rem",
                  color: "#64748b",
                  fontWeight: 600,
                  background: "#f8fafc",
                  padding: "1px 5px",
                  borderRadius: "3px",
                  border: "1px solid #e2e8f0",
                }}
              >
                {issues.length} Issue{issues.length > 1 ? "s" : ""}
              </span>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderTop: "1px dashed #e2e8f0",
                paddingTop: "8px",
                marginTop: "2px",
                color: "#94a3b8",
                fontSize: "0.72rem",
                fontWeight: 500,
              }}
            >
              <span>No Issues</span>
              <i
                className="fa-solid fa-check-double"
                style={{ color: "#cbd5e1", fontSize: "0.85rem" }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const templateLibraryColumns = [
    {
      key: "libraryName",
      label: "Library Name",
      sortable: true,
      render: (row) => (
        <span>{row.libraryName || row.subject || "Untitled Library"}</span>
      ),
    },
    {
      key: "subject",
      label: "Subject",
      sortable: true,
      filter: true,
      render: (row) => {
        return <span>{row.subject || "—"}</span>;
      },
    },
    {
      key: "templateName",
      label: "Template Name",
      sortable: true,
      render: (row) => <span>{getTemplateName(row)}</span>,
    },
    {
      key: "year",
      label: "Year",
      sortable: true,
      render: (row) => <span>{row.year || "—"}</span>,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => {
        const isRequested = row.status === "Requested";
        const hasFailedRanges =
          row.failedRanges && row.failedRanges.length > 0 && !isRequested;
        const status = isRequested
          ? "Requested"
          : hasFailedRanges
            ? "Partial Complete"
            : row.status || "Completed";
        let color = "#10b981"; // green
        let bg = "#ecfdf5";
        let icon = "fa-circle-check";
        if (status === "Requested") {
          color = "#f59e0b"; // amber
          bg = "#fffbeb";
          icon = "fa-spinner fa-spin";
        } else if (status === "Failed") {
          color = "#ef4444"; // red
          bg = "#fef2f2";
          icon = "fa-circle-xmark";
        } else if (status === "Partial Complete") {
          color = "#f97316"; // orange
          bg = "#fff7ed";
          icon = "fa-circle-exclamation";
        }
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              alignItems: "flex-start",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                color: color,
                backgroundColor: bg,
                padding: "4px 10px",
                borderRadius: "100px",
                fontSize: "0.82rem",
                fontWeight: 600,
                border: `1px solid ${color}33`,
              }}
            >
              <i className={`fa-solid ${icon}`} />
              {status}
            </span>
            {hasFailedRanges && (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
                onClick={(e) => e.stopPropagation()}
              >
                {row.failedRanges.map((range, idx) => (
                  <button
                    key={idx}
                    className="retry-range-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRetryFailedRange(row._id, range.start, range.end);
                    }}
                    title={`Retry range ${range.start}-${range.end}`}
                  >
                    <i className="fa-solid fa-rotate-right" /> Retry{" "}
                    {range.start}-{range.end}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "createdAt",
      label: "Created At",
      sortable: true,
      render: (row) => (
        <span>
          {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "—"}
        </span>
      ),
    },
    {
      key: "questionSourceFileURL",
      label: "Source File",
      sortable: false,
      render: (row) => {
        const url = row.questionSourceFileURL;
        if (!url) return <span>—</span>;
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={url}
            className="view-btn"
            style={{ textDecoration: "none" }}
          >
            <i className="fa-solid fa-eye" /> View
          </a>
        );
      },
    },
    {
      key: "progress",
      label: "Progress",
      sortable: true,
      sortBy: (row) => {
        const isInvalidResolved = row.progress?.isInvalidQuestionResolved ?? false;
        const isDiagramsReviewed = row.progress?.isQuestionDiagramsReviewed ?? false;
        const isLibReady = row.progress?.isLibraryReady ?? false;

        let completedCount = 0;
        if (isInvalidResolved) completedCount++;
        if (isDiagramsReviewed) completedCount++;
        if (isLibReady) completedCount++;

        const issues = row.progress?.issues || [];
        const pendingIssuesCount = issues.filter((issue) => !issue.solved).length;
        const totalIssuesCount = issues.length;

        return completedCount * 10000 - pendingIssuesCount * 10 - totalIssuesCount;
      },
      render: renderLibraryProgress
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      render: (row) => (
        <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
          <button
            className="edit-btn"
            onClick={() => handleOpenEditLibraryModal(row)}
            title="Edit Library"
          >
            <i className="fa-solid fa-pen-to-square" />
          </button>
          {user?.isMasterUser !== true && (
            <button
              className="delete-btn"
              onClick={() => handleDeleteLibrary(row._id, row.libraryName)}
              disabled={isDeleting && deletingId === row._id}
              title="Delete Library"
            >
              {isDeleting && deletingId === row._id ? (
                <i className="fa-solid fa-spinner fa-spin" />
              ) : (
                <i className="fa-solid fa-trash" />
              )}
            </button>
          )}
        </div>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────
  // Library list columns
  // ─────────────────────────────────────────────────────────────
  const libraryColumns = [
    {
      key: "curriculum",
      label: "Curriculum",
      sortable: true,
      render: (row) => <span>{row.curriculum || "—"}</span>,
    },
    {
      key: "grade",
      label: "Grade",
      sortable: true,
      render: (row) => <span>{row.grade || "—"}</span>,
    },
    {
      key: "libraryName",
      label: "Library Name",
      sortable: true,
      render: (row) => (
        <span>{row.libraryName || row.subject || "Untitled Library"}</span>
      ),
    },
    {
      key: "topic",
      label: "Topic",
      sortable: true,
      render: (row) => <span>{row.topic || "—"}</span>,
    },
    {
      key: "year",
      label: "Year",
      sortable: true,
      render: (row) => <span>{row.year || "—"}</span>,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => {
        const isRequested = row.status === "Requested";
        const hasFailedRanges =
          row.failedRanges && row.failedRanges.length > 0 && !isRequested;
        const status = isRequested
          ? "Requested"
          : hasFailedRanges
            ? "Partial Complete"
            : row.status || "Completed";

        let color = "#10b981"; // green
        let bg = "#ecfdf5";
        let icon = "fa-circle-check";

        if (status === "Requested") {
          color = "#f59e0b"; // amber
          bg = "#fffbeb";
          icon = "fa-spinner fa-spin";
        } else if (status === "Failed") {
          color = "#ef4444"; // red
          bg = "#fef2f2";
          icon = "fa-circle-xmark";
        } else if (status === "Partial Complete") {
          color = "#f97316"; // orange
          bg = "#fff7ed";
          icon = "fa-circle-exclamation";
        }

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              alignItems: "flex-start",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                color: color,
                backgroundColor: bg,
                padding: "4px 10px",
                borderRadius: "100px",
                fontSize: "0.82rem",
                fontWeight: 600,
                border: `1px solid ${color}33`,
              }}
            >
              <i className={`fa-solid ${icon}`} />
              {status}
            </span>
            {hasFailedRanges && (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
                onClick={(e) => e.stopPropagation()}
              >
                {row.failedRanges.map((range, idx) => (
                  <button
                    key={idx}
                    className="retry-range-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRetryFailedRange(row._id, range.start, range.end);
                    }}
                    title={`Retry range ${range.start}-${range.end}`}
                  >
                    <i className="fa-solid fa-rotate-right" /> Retry{" "}
                    {range.start}-{range.end}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "createdAt",
      label: "Created At",
      sortable: true,
      render: (row) => (
        <span>
          {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "—"}
        </span>
      ),
    },
    {
      key: "questionSourceFileURL",
      label: "Source File",
      sortable: false,
      render: (row) => {
        const url = row.questionSourceFileURL;
        if (!url) return <span>—</span>;
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={url}
            className="view-btn"
            style={{ textDecoration: "none" }}
          >
            <i className="fa-solid fa-eye" /> View
          </a>
        );
      },
    },
    {
      key: "progress",
      label: "Progress",
      sortable: true,
      sortBy: (row) => {
        const isInvalidResolved = row.progress?.isInvalidQuestionResolved ?? false;
        const isDiagramsReviewed = row.progress?.isQuestionDiagramsReviewed ?? false;
        const isLibReady = row.progress?.isLibraryReady ?? false;

        let completedCount = 0;
        if (isInvalidResolved) completedCount++;
        if (isDiagramsReviewed) completedCount++;
        if (isLibReady) completedCount++;

        const issues = row.progress?.issues || [];
        const pendingIssuesCount = issues.filter((issue) => !issue.solved).length;
        const totalIssuesCount = issues.length;

        return completedCount * 10000 - pendingIssuesCount * 10 - totalIssuesCount;
      },
      render: renderLibraryProgress,
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      render: (row) => (
        <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
          <button
            className="edit-btn"
            onClick={() => handleOpenEditLibraryModal(row)}
            title="Edit Library"
          >
            <i className="fa-solid fa-pen-to-square" />
          </button>
          {user?.isMasterUser !== true && (
            <button
              className="delete-btn"
              onClick={() => handleDeleteLibrary(row._id, row.libraryName)}
              disabled={isDeleting && deletingId === row._id}
              title="Delete Library"
            >
              {isDeleting && deletingId === row._id ? (
                <i className="fa-solid fa-spinner fa-spin" />
              ) : (
                <i className="fa-solid fa-trash" />
              )}
            </button>
          )}
        </div>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────
  // Question table columns (for the library questions detail view)
  // ─────────────────────────────────────────────────────────────
  const questionColumns = useMemo(
    () => {
      const isShowingInvalid = showInvalidOnly || subjectFilter?.type === "invalid";
      return [
      {
        key: "questionNumber",
        label: "Q. No.",
        sortable: true,
      },
      {
        key: "subject",
        label: "Subject",
        filter: true,
      },
      {
        key: "topic",
        label: "Topic (Subtopic)",
        filter: true,
        sortable: false,
        render: (question) => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              fontSize: "0.88rem",
            }}
          >
            <span>
              <span style={{ fontWeight: 600, color: "var(--color-1)" }}>
                {question.topic || "—"}
              </span>
            </span>
            {question.subTopic && (
              <span style={{ color: "#6b7280" }}>
                <span style={{ fontWeight: 600 }}>({question.subTopic})</span>
              </span>
            )}
          </div>
        ),
      },
      {
        key: "difficulty",
        label: "Difficulty",
        filter: true,
      },
      {
        key: "type",
        label: "Type",
        filter: true,
      },
      {
        key: "questionText",
        label: "Question",
        sortable: false,
        filter: false,
        render: (question) => (
          <div className="question-text-container">
            <div
              className="question-text"
              style={{ fontSize: "0.97rem", lineHeight: 1.7 }}
            >
              <div
                className="question-preview"
                onClick={(e) => {
                  e.stopPropagation();
                  showQuestion(question);
                }}
                style={{
                  cursor: "pointer",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title="Click to view full question"
              >
                <LazyMarkdown>{question.questionText || ""}</LazyMarkdown>
              </div>
            </div>
          </div>
        ),
      },
      ...(isShowingInvalid
        ? [
            {
              key: "invalidReason",
              label: "Invalid Reason",
              sortable: true,
              filter: true,
              render: (question) => (
                <div style={{ color: "#ef4444", fontSize: "0.85rem", maxWidth: "200px", wordWrap: "break-word", whiteSpace: "normal" }}>
                  {question.invalidReason || "—"}
                </div>
              ),
            },
          ]
        : []),
      // Image column — upload & crop diagram
      {
        key: "imageFilterStatus",
        label: "Image",
        filter: true,
        sortable: true,
        sortBy: (question) => {
          const hasImage = !!(
            question.diagramFileURL ||
            question.imageURL ||
            question.svgCode
          );
          const isRecommended = question.diagramSuggested;
          if (hasImage) return 0;
          if (isRecommended) return 1;
          return 2;
        },
        render: (question) => {
          const isRecommended = question.diagramSuggested;
          const hasImage = !!(
            question.diagramFileURL ||
            question.imageURL ||
            question.svgCode
          );
          return (
            <div className="image-btn-wrapper">
              {isRecommended && !hasImage && (
                <span
                  className="ai-recommended-badge"
                  title="AI suggests this question would benefit from a diagram"
                >
                  Recommended
                </span>
              )}
              <button
                className="view-btn"
                style={{
                  backgroundColor: hasImage ? "var(--color-1)" : "#6bcb77",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  color: "white",
                  border: "none",
                  fontWeight: "600",
                  fontSize: "0.85rem",
                }}
                onClick={() => {
                  setSelectedImageQuestion(question);
                  setDiagramQuestion(question);
                  setIsDiagramImageModalOpen(true);
                }}
              >
                {hasImage ? (
                  <>
                    <i className="fa-regular fa-image" /> View
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-plus" /> Add
                  </>
                )}
              </button>
            </div>
          );
        },
      },
      {
        key: "solution",
        label: "Solution",
        sortable: false,
        filter: false,
        render: (question) => {
          const hasSolution = question.solution;
          return hasSolution ? (
            <button
              onClick={() => showSolution(question)}
              className="view-btn"
              style={{ backgroundColor: "var(--color-1)", cursor: "pointer" }}
            >
              <i className="fa-regular fa-eye" />
            </button>
          ) : (
            "-"
          );
        },
      },
      {
        key: "actions",
        label: "Actions",
        sortable: false,
        filter: false,
        render: (question) => (
          <div
            style={{ display: "flex", gap: "6px", justifyContent: "center" }}
          >
            {isQuestionInvalid(question) && (
              <button
                className="view-btn"
                title="Invalidity Info"
                onClick={() => {
                  setInvalidInfoQuestion(question);
                  setIsInvalidInfoModalOpen(true);
                }}
                style={{
                  backgroundColor: "#ef4444",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "30px",
                  height: "30px",
                  borderRadius: "6px",
                  border: "none",
                  color: "white",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                <i className="fa-solid fa-circle-info" />
              </button>
            )}
            <button
              className="edit-btn"
              title="Edit question"
              onClick={() => handleOpenEditModal(question)}
            >
              <i className="fa-solid fa-pen-to-square" />
            </button>
            {user?.isMasterUser !== true && (
              <button
                className="delete-btn"
                title="Delete question"
                onClick={() => handleDeleteQuestion(question)}
              >
                <i className="fa-solid fa-trash" />
              </button>
            )}
          </div>
        ),
      },
    ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questionIndexMap, showInvalidOnly, subjectFilter, user],
  );

  // ─────────────────────────────────────────────────────────────
  // Form changed guard for Edit modal
  // ─────────────────────────────────────────────────────────────
  const isFormChanged =
    editForm.questionText !== initialEditForm.questionText ||
    editForm.topic !== initialEditForm.topic ||
    editForm.subTopic !== initialEditForm.subTopic ||
    editForm.type !== initialEditForm.type ||
    editForm.difficulty !== initialEditForm.difficulty ||
    editForm.solution !== initialEditForm.solution ||
    editForm.correctOptions !== initialEditForm.correctOptions ||
    editForm.answerKeyCorrectOptions !==
    initialEditForm.answerKeyCorrectOptions ||
    editForm.subject !== initialEditForm.subject ||
    editForm.sectionNumber !== initialEditForm.sectionNumber ||
    editForm.questionNumber !== initialEditForm.questionNumber ||
    editForm.correctAnswer !== initialEditForm.correctAnswer ||
    editForm.answerKeyCorrectAnswer !==
    initialEditForm.answerKeyCorrectAnswer ||
    editForm.isPastExam !== initialEditForm.isPastExam ||
    editForm.isInvalid !== initialEditForm.isInvalid ||
    editForm.invalidReason !== initialEditForm.invalidReason ||
    editForm.otherInvalidReason !== initialEditForm.otherInvalidReason ||
    JSON.stringify(editForm.options) !==
    JSON.stringify(initialEditForm.options);

  const isLibraryFormChanged =
    editLibraryForm.libraryName !== initialEditLibraryForm.libraryName ||
    editLibraryForm.subject !== initialEditLibraryForm.subject ||
    editLibraryForm.topic !== initialEditLibraryForm.topic ||
    editLibraryForm.grade !== initialEditLibraryForm.grade ||
    editLibraryForm.curriculum !== initialEditLibraryForm.curriculum ||
    editLibraryForm.year !== initialEditLibraryForm.year;

  // ─────────────────────────────────────────────────────────────
  // Auth Guard
  // ─────────────────────────────────────────────────────────────
  if (!isAuthenticated || primaryRole !== "superadmin") {
    return <Navigate to="/" />;
  }

  const createLibraryButton = (
    <button
      className="create-btn"
      onClick={() => {
        setLibraryCreationMode(activeTab);
        if (activeTab === "general") {
          setIsQBTypeModalOpen(true);
        } else {
          // For Template Library, directly open the PDF Extractor modal
          setExtractPdfForm({
            libraryName: "",
            omrTemplateId: "",
            subject: "",
            topic: "",
            grade: "",
            curriculum: "",
            numberOfQs: "",
            customInstructions: "",
            sourceTestPaper: null,
            answerKeysFile: null,
            year: "",
            isPastExam: false,
          });
          setIsExtractPdfOpen(true);
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "0.75rem 1.5rem",
        borderRadius: "8px",
        fontSize: "0.95rem",
        fontWeight: "600",
        margin: 0,
        height: "46px",
      }}
    >
      <span>
        {activeTab === "general"
          ? "Create General Library"
          : "Create Template Library"}
      </span>
      <i className="fas fa-plus"></i>
    </button>
  );

  const libraryActionsContainer = (
    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
      {createLibraryButton}
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────
  return (
    <>
      <Navbar breadcrumbs={breadcrumbs} createButtons={createButtons} />

      {/* ── Libraries List View ── */}
      {!viewingLibrary && (
        <div
          className="container"
          style={{ marginTop: "20px", marginBottom: "40px" }}
        >
          {/* Tab buttons */}
          <div className="tabs" style={{ marginBottom: "20px" }}>
            <button
              className={`tab-btn ${activeTab === "general" ? "active" : ""}`}
              onClick={() => setActiveTab("general")}
            >
              <i className="fa-solid fa-book" style={{ marginRight: "8px" }} />
              General Library
            </button>
            <button
              className={`tab-btn ${activeTab === "template" ? "active" : ""}`}
              onClick={() => setActiveTab("template")}
            >
              <i
                className="fa-solid fa-file-invoice"
                style={{ marginRight: "8px" }}
              />
              Template Library
            </button>
            {user?.isMasterUser !== true && (
              <button
                className={`tab-btn ${activeTab === "usages" ? "active" : ""}`}
                onClick={() => setActiveTab("usages")}
              >
                <i
                  className="fa-solid fa-chart-line"
                  style={{ marginRight: "8px" }}
                />
                Usages
              </button>
            )}
            <button
              className={`tab-btn ${activeTab === "stats" ? "active" : ""}`}
              onClick={() => setActiveTab("stats")}
            >
              <i
                className="fa-solid fa-chart-pie"
                style={{ marginRight: "8px" }}
              />
              Stats
            </button>
          </div>

          {/* Library Page Stats Cards */}
          {activeTab !== "usages" && activeTab !== "stats" && (
            <div
              className="manage-token"
              style={{ padding: 0, minHeight: "auto", marginBottom: "20px" }}
            >
              <div
                className="stats-grid"
                style={{
                  marginBottom: "24px",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  width: "100%",
                  gap: "16px",
                }}
              >
                <div className="stat-card" style={{ width: "auto" }}>
                  <div
                    className="stat-icon input"
                    style={{
                      background:
                        "linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%)",
                    }}
                  >
                    📚
                  </div>
                  <div className="stat-content">
                    <h3
                      style={{
                        margin: "0 0 4px 0",
                        color: "#475569",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                      }}
                    >
                      Total Libraries
                    </h3>
                    <p
                      className="stat-number"
                      style={{
                        margin: 0,
                        fontSize: "1.6rem",
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      {libraryStats.total}
                    </p>
                  </div>
                </div>
                <div className="stat-card" style={{ width: "auto" }}>
                  <div
                    className="stat-icon output"
                    style={{
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    }}
                  >
                    ✅
                  </div>
                  <div className="stat-content">
                    <h3
                      style={{
                        margin: "0 0 4px 0",
                        color: "#475569",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                      }}
                    >
                      Ready Libraries
                    </h3>
                    <p
                      className="stat-number"
                      style={{
                        margin: 0,
                        fontSize: "1.6rem",
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      {libraryStats.ready}
                    </p>
                  </div>
                </div>
                <div className="stat-card" style={{ width: "auto" }}>
                  <div
                    className="stat-icon total"
                    style={{
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    }}
                  >
                    ⏳
                  </div>
                  <div className="stat-content">
                    <h3
                      style={{
                        margin: "0 0 4px 0",
                        color: "#475569",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                      }}
                    >
                      Pending Audit
                    </h3>
                    <p
                      className="stat-number"
                      style={{
                        margin: 0,
                        fontSize: "1.6rem",
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      {libraryStats.pendingAudit}
                    </p>
                  </div>
                </div>
                <div className="stat-card" style={{ width: "auto" }}>
                  <div
                    className="stat-icon cost"
                    style={{
                      background:
                        "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      color: "white",
                    }}
                  >
                    ⚠️
                  </div>
                  <div className="stat-content">
                    <h3
                      style={{
                        margin: "0 0 4px 0",
                        color: "#475569",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                      }}
                    >
                      Processing / Failed
                    </h3>
                    <p
                      className="stat-number"
                      style={{
                        margin: 0,
                        fontSize: "1.6rem",
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      {libraryStats.processing + libraryStats.failed}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "template" && (
            <>
              <style>{`
                .tab-btn-pill:hover {
                  background-color: #e2e8f0 !important;
                  color: #0f172a !important;
                }
                .tab-btn-pill.active:hover {
                  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%) !important;
                  color: #ffffff !important;
                }
              `}</style>
              <div
                className="sub-tabs-container"
                style={{
                  display: "flex",
                  gap: "8px",
                  marginBottom: "24px",
                  padding: "6px 8px",
                  background: "#f8fafc",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                }}
              >
                <button
                  type="button"
                  className={`tab-btn-pill ${selectedTestFilter === "all" ? "active" : ""}`}
                  onClick={() => setSelectedTestFilter("all")}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "6px",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    border: "none",
                    transition: "all 0.2s ease-in-out",
                    background:
                      selectedTestFilter === "all"
                        ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                        : "transparent",
                    color: selectedTestFilter === "all" ? "#ffffff" : "#64748b",
                    boxShadow:
                      selectedTestFilter === "all"
                        ? "0 2px 4px rgba(0,0,0,0.1)"
                        : "none",
                  }}
                >
                  All Libraries
                </button>
                {standardizedTestOptions.map((opt) => {
                  const isSelected =
                    selectedTestFilter === opt.value ||
                    selectedTestFilter === opt.templateId;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`tab-btn-pill ${isSelected ? "active" : ""}`}
                      onClick={() => setSelectedTestFilter(opt.value)}
                      style={{
                        padding: "8px 16px",
                        borderRadius: "6px",
                        fontSize: "0.875rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        border: "none",
                        transition: "all 0.2s ease-in-out",
                        background: isSelected
                          ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                          : "transparent",
                        color: isSelected ? "#ffffff" : "#64748b",
                        boxShadow: isSelected
                          ? "0 2px 4px rgba(0,0,0,0.1)"
                          : "none",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {(loading && activeTab !== "usages" && activeTab !== "stats") ||
            (loadingStats && activeTab === "stats") ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "40px 0",
              }}
            >
              <Loader style={{ left: 0, top: 0 }} />
            </div>
          ) : activeTab === "general" ? (
            generalLibraries.length > 0 ? (
              <Table
                columns={libraryColumns}
                data={generalLibraries}
                caption="General Libraries"
                onRowClick={viewLibraryQuestions}
                searchActions={libraryActionsContainer}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  {libraryActionsContainer}
                </div>
                <div
                  className="glass"
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#64748b",
                  }}
                >
                  <p>No general libraries found.</p>
                </div>
              </div>
            )
          ) : activeTab === "template" ? (
            filteredTemplateLibraries.length > 0 ? (
              <Table
                columns={templateLibraryColumns}
                data={filteredTemplateLibraries}
                caption="Template Libraries"
                onRowClick={viewLibraryQuestions}
                searchActions={libraryActionsContainer}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  {libraryActionsContainer}
                </div>
                <div
                  className="glass"
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#64748b",
                  }}
                >
                  <p>No template libraries found.</p>
                </div>
              </div>
            )
          ) : (activeTab === "usages" && user?.isMasterUser !== true) ? (
            <div
              className="manage-token"
              style={{
                padding: 0,
                margin: 0,
                maxWidth: "100%",
                minHeight: "auto",
              }}
            >
              {/* Usage Filters Control Card */}
              <div
                className="controls-section"
                style={{
                  marginBottom: "20px",
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                {/* Usage Type */}
                <div className="type-selector">
                  <label
                    style={{
                      fontSize: "1.05rem",
                      marginBottom: "8px",
                      fontWeight: 600,
                      color: "#475569",
                    }}
                  >
                    Usage Type:
                  </label>
                  <div className="type-buttons">
                    <button
                      className="type-btn active"
                      style={{ cursor: "default" }}
                    >
                      <span className="type-icon">📚</span>
                      Library Extractor
                    </button>
                  </div>
                </div>

                {/* Library Type */}
                <div
                  className="type-selector"
                  style={{ borderTop: "1px solid #f1f5f9", paddingTop: "16px" }}
                >
                  <label
                    style={{
                      fontSize: "1.05rem",
                      marginBottom: "8px",
                      fontWeight: 600,
                      color: "#475569",
                    }}
                  >
                    Library Type:
                  </label>
                  <div className="type-buttons">
                    {[
                      { value: "all", label: "All Libraries", icon: "📚" },
                      {
                        value: "general",
                        label: "General Library",
                        icon: "📖",
                      },
                      {
                        value: "template",
                        label: "Template Library",
                        icon: "📑",
                      },
                    ].map((option) => (
                      <button
                        key={option.value}
                        className={`type-btn ${selectedLibraryType === option.value ? "active" : ""}`}
                        onClick={() => {
                          setSelectedLibraryType(option.value);
                          if (option.value === "general") {
                            setSelectedTemplateId("all");
                          }
                        }}
                      >
                        <span className="type-icon">{option.icon}</span>
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Template Name */}
                {selectedLibraryType !== "general" && (
                  <div
                    className="type-selector"
                    style={{
                      borderTop: "1px solid #f1f5f9",
                      paddingTop: "16px",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "1.05rem",
                        marginBottom: "8px",
                        fontWeight: 600,
                        color: "#475569",
                      }}
                    >
                      Template Name:
                    </label>
                    <div className="type-buttons">
                      <button
                        className={`type-btn ${selectedTemplateId === "all" ? "active" : ""}`}
                        onClick={() => setSelectedTemplateId("all")}
                      >
                        <span className="type-icon">📑</span>
                        All Templates
                      </button>
                      {standardizedTestOptions.map((opt) => (
                        <button
                          key={opt.value}
                          className={`type-btn ${selectedTemplateId === opt.value ? "active" : ""}`}
                          onClick={() => setSelectedTemplateId(opt.value)}
                        >
                          <span className="type-icon">📄</span>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Date Filter Row */}
              <div
                className="time-range-wrapper"
                style={{ marginBottom: "20px", marginLeft: "10px" }}
              >
                <button
                  type="button"
                  onClick={() => setShowDatePopup((prev) => !prev)}
                  className="time-range-btn"
                  aria-expanded={showDatePopup}
                  aria-haspopup="true"
                >
                  <span className="btn-icon">🕒</span>
                  {getTimeRangeText()}
                  <span className={`chevron ${showDatePopup ? "open" : ""}`}>
                    ▼
                  </span>
                </button>

                {showDatePopup && (
                  <>
                    <div
                      className="popup-overlay"
                      onClick={() => setShowDatePopup(false)}
                    />
                    <div className="time-range-popup">
                      <div className="popup-header">
                        <h3>Select Time Range</h3>
                        <button
                          type="button"
                          className="close-btn"
                          onClick={() => setShowDatePopup(false)}
                          aria-label="Close"
                        >
                          ✕
                        </button>
                      </div>

                      <div className="preset-section">
                        <div className="preset-grid">
                          {[
                            "Today",
                            "Last 24 Hours",
                            "This Month",
                            "Custom",
                          ].map((option) => (
                            <button
                              key={option}
                              type="button"
                              className={`preset-option ${rangeOption === option ? "selected" : ""
                                }`}
                              onClick={() => handlePresetSelect(option)}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="custom-range-section">
                        <div className="date-inputs">
                          <div className="input-group">
                            <label htmlFor="start-date">Start Date</label>
                            <input
                              id="start-date"
                              type="date"
                              value={usageFromDate}
                              onChange={(e) => {
                                setUsageFromDate(e.target.value);
                                setRangeOption("Custom");
                              }}
                              className="date-input"
                            />
                          </div>
                          <div className="input-group">
                            <label htmlFor="end-date">End Date</label>
                            <input
                              id="end-date"
                              type="date"
                              value={usageToDate}
                              onChange={(e) => {
                                setUsageToDate(e.target.value);
                                setRangeOption("Custom");
                              }}
                              className="date-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Token Usage Summary Cards */}
              <div
                className="stats-grid"
                style={{
                  marginBottom: "24px",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  width: "100%",
                  gap: "16px",
                }}
              >
                <div className="stat-card" style={{ width: "auto" }}>
                  <div className="stat-icon input">📥</div>
                  <div className="stat-content">
                    <h3>Input Tokens</h3>
                    <p className="stat-number">
                      {formatNumber(usageStats.input)}
                    </p>
                  </div>
                </div>
                <div className="stat-card" style={{ width: "auto" }}>
                  <div className="stat-icon output">📤</div>
                  <div className="stat-content">
                    <h3>Output Tokens</h3>
                    <p className="stat-number">
                      {formatNumber(usageStats.output)}
                    </p>
                  </div>
                </div>
                <div className="stat-card" style={{ width: "auto" }}>
                  <div className="stat-icon total">📊</div>
                  <div className="stat-content">
                    <h3>Total Tokens</h3>
                    <p className="stat-number">
                      {formatNumber(usageStats.total)}
                    </p>
                  </div>
                </div>
                <div className="stat-card" style={{ width: "auto" }}>
                  <div className="stat-icon cost">💰</div>
                  <div className="stat-content">
                    <h3>Total Cost</h3>
                    <p className="stat-number">₹{usageStats.cost.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Token Usage Table */}
              {loadingUsage ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px 0",
                  }}
                >
                  <Loader style={{ left: 0, top: 0 }} />
                </div>
              ) : (
                <div className="table-section">
                  <Table
                    columns={usageTableColumns}
                    data={filteredUsageData}
                    caption={(() => {
                      const sel =
                        selectedUsageRowIds.size > 0
                          ? ` (${selectedUsageRowIds.size} Selected)`
                          : "";
                      return `Library Extractor Token Usage Records${sel}`;
                    })()}
                    isCheckBoxAvailable={true}
                    selectedRowIds={selectedUsageRowIds}
                    onRowSelect={handleUsageRowSelect}
                    onSelectAll={handleUsageSelectAll}
                  />
                </div>
              )}
            </div>
          ) : (
            <div
              className="manage-token"
              style={{
                padding: 0,
                margin: 0,
                maxWidth: "100%",
                minHeight: "auto",
              }}
            >
              {/* Stats Summary Cards */}
              <div
                className="stats-grid"
                style={{
                  marginBottom: "24px",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  width: "100%",
                  gap: "16px",
                }}
              >
                <div className="stat-card" style={{ width: "auto" }}>
                  <div
                    className="stat-icon input"
                    style={{
                      background:
                        "linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%)",
                    }}
                  >
                    📑
                  </div>
                  <div className="stat-content">
                    <h3
                      style={{
                        margin: "0 0 4px 0",
                        color: "#475569",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                      }}
                    >
                      Templates
                    </h3>
                    <p
                      className="stat-number"
                      style={{
                        margin: 0,
                        fontSize: "1.6rem",
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      {overallStats.templatesCount}
                    </p>
                  </div>
                </div>
                <div className="stat-card" style={{ width: "auto" }}>
                  <div
                    className="stat-icon output"
                    style={{
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    }}
                  >
                    📚
                  </div>
                  <div className="stat-content">
                    <h3
                      style={{
                        margin: "0 0 4px 0",
                        color: "#475569",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                      }}
                    >
                      Total Libraries
                    </h3>
                    <p
                      className="stat-number"
                      style={{
                        margin: 0,
                        fontSize: "1.6rem",
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      {overallStats.librariesCount}
                    </p>
                  </div>
                </div>
                <div className="stat-card" style={{ width: "auto" }}>
                  <div
                    className="stat-icon total"
                    style={{
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    }}
                  >
                    ❓
                  </div>
                  <div className="stat-content">
                    <h3
                      style={{
                        margin: "0 0 4px 0",
                        color: "#475569",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                      }}
                    >
                      Total Questions
                    </h3>
                    <p
                      className="stat-number"
                      style={{
                        margin: 0,
                        fontSize: "1.6rem",
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      {overallStats.totalQs}
                    </p>
                  </div>
                </div>
                <div className="stat-card" style={{ width: "auto" }}>
                  <div
                    className="stat-icon cost"
                    style={{
                      background:
                        "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      color: "white",
                    }}
                  >
                    ⚠️
                  </div>
                  <div className="stat-content">
                    <h3
                      style={{
                        margin: "0 0 4px 0",
                        color: "#475569",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                      }}
                    >
                      Invalid Questions
                    </h3>
                    <p
                      className="stat-number"
                      style={{
                        margin: 0,
                        fontSize: "1.6rem",
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      {overallStats.invalidQs - (overallStats.userInvalidQs || 0)}{" "}
                      <span style={{ color: "#2563eb", fontSize: "1.2rem" }}>({overallStats.userInvalidQs || 0})</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Nested Table Section */}
              <div
                className="table-section glass"
                style={{
                  padding: "20px",
                  background: "white",
                  borderRadius: "12px",
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                    flexWrap: "wrap",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      flexWrap: "wrap",
                    }}
                  >
                    <h3
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: 700,
                        color: "#1e293b",
                        margin: 0,
                      }}
                    >
                      Library Quality Metrics
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "3px 9px",
                          borderRadius: "12px",
                          backgroundColor: "#ffedd5",
                          color: "#9a3412",
                          border: "1px solid #fed7aa",
                        }}
                      >
                        <span
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: "#f97316",
                          }}
                        />
                        Invalid Questions Reviewed
                      </span>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "3px 9px",
                          borderRadius: "12px",
                          backgroundColor: "#e0f2fe",
                          color: "#075985",
                          border: "1px solid #bae6fd",
                        }}
                      >
                        <span
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: "#0284c7",
                          }}
                        />
                        Diagrams Reviewed (Invalid + Diagram)
                      </span>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "3px 9px",
                          borderRadius: "12px",
                          backgroundColor: "#dcfce7",
                          color: "#166534",
                          border: "1px solid #bbf7d0",
                        }}
                      >
                        <span
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: "#16a34a",
                          }}
                        />
                        Library Ready
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      setIsRefreshingStats(true);
                      await Promise.all([
                        fetchLibraryStats(true),
                        fetchLibraries(true),
                      ]);
                      setIsRefreshingStats(false);
                    }}
                    disabled={isRefreshingStats || loadingStats}
                    className="view-btn"
                    style={{
                      backgroundColor: "var(--color-1)",
                      color: "white",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontWeight: "600",
                      fontSize: "0.85rem",
                    }}
                  >
                    <i
                      className={`fa-solid fa-rotate-right ${isRefreshingStats ? "fa-spin" : ""}`}
                    />
                    Refresh
                  </button>
                </div>
                <style>{`
                  .stats-row-library.none-reviewed:hover {
                    background-color: #f1f5f9 !important;
                  }
                  .stats-row-library.invalid-reviewed:hover {
                    background-color: #fed7aa !important;
                  }
                  .stats-row-library.both-reviewed:hover {
                    background-color: #bae6fd !important;
                  }
                  .stats-row-library.library-ready:hover {
                    background-color: #bbf7d0 !important;
                  }
                `}</style>
                {statsData.length === 0 ? (
                  <p
                    style={{
                      textAlign: "center",
                      color: "#64748b",
                      margin: "20px 0",
                    }}
                  >
                    No library stats found.
                  </p>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "0.95rem",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            background: "#f8fafc",
                            borderBottom: "2px solid #e2e8f0",
                            color: "#475569",
                            fontWeight: 600,
                          }}
                        >
                          <th
                            style={{ padding: "12px 16px", textAlign: "left" }}
                          >
                            Category / Subject / Library{" "}
                          </th>
                          <th
                            style={{
                              padding: "12px 16px",
                              textAlign: "center",
                              color: "#64748b",
                            }}
                          >
                            Source File
                          </th>
                          <th
                            style={{ padding: "12px 16px", textAlign: "right" }}
                          >
                            Library Count
                          </th>
                          <th
                            style={{ padding: "12px 16px", textAlign: "right" }}
                          >
                            Total Questions{" "}
                            <span style={{ color: "#10b981", fontSize: "0.82rem", fontWeight: "600" }}>
                              (Valid Questions)
                            </span>
                          </th>
                          <th
                            style={{
                              padding: "12px 16px",
                              textAlign: "right",
                              color: "#ef4444",
                            }}
                          >
                            Invalid Questions
                          </th>
                          <th
                            style={{
                              padding: "12px 16px",
                              textAlign: "right",
                              color: "#3b82f6",
                            }}
                          >
                            Diagram Questions
                          </th>
                          <th style={{ padding: "12px 16px", textAlign: "center", color: "#e11d48" }}>Questions PDF</th>
                          <th style={{ padding: "12px 16px", textAlign: "center", color: "#8b5cf6" }}>AI Solutions</th>
                          <th style={{ padding: "12px 16px", textAlign: "center", color: "#10b981" }}>Answer Key</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statsData.map((template) => {
                          const isTemplateExpanded = expandedTemplates.has(
                            template.templateId,
                          );
                          const templateTotalQuestions =
                            template.subjects.reduce(
                              (sum, s) => sum + s.totalQuestions,
                              0,
                            );
                          const templateTotalValid = template.subjects.reduce(
                            (sum, s) => sum + s.validQuestions,
                            0,
                          );
                          const templateTotalInvalid = template.subjects.reduce(
                            (sum, s) => sum + s.invalidQuestions,
                            0,
                          );
                          const templateTotalUserInvalid = template.subjects.reduce(
                            (sum, s) => sum + (s.userInvalidQuestions || 0),
                            0,
                          );
                          const templateTotalDiagram = template.subjects.reduce(
                            (sum, s) => sum + s.diagramQuestions,
                            0,
                          );
                          const templateReviewedDiagram = template.subjects.reduce((sumSubj, subj) => {
                            return sumSubj + subj.libraries.reduce((sumLib, lib) => {
                              const foundLib = libraries.find((l) => l._id === lib.libraryId);
                              const isDiagramsReviewed = foundLib?.progress?.isQuestionDiagramsReviewed ?? false;
                              return sumLib + (isDiagramsReviewed ? lib.diagramQuestions : 0);
                            }, 0);
                          }, 0);
                          const templateReadyLibraries = template.subjects.reduce((sumSubj, subj) => {
                            return sumSubj + subj.libraries.reduce((sumLib, lib) => {
                              const foundLib = libraries.find((l) => l._id === lib.libraryId);
                              const isReady = foundLib?.progress?.isLibraryReady ?? false;
                              return sumLib + (isReady ? 1 : 0);
                            }, 0);
                          }, 0);

                          return (
                            <React.Fragment key={template.templateId}>
                              {/* Template Row */}
                              <tr
                                onClick={() =>
                                  toggleTemplate(template.templateId)
                                }
                                style={{
                                  cursor: "pointer",
                                  background: "#f1f5f9",
                                  fontWeight: 700,
                                  borderBottom: "1.5px solid #cbd5e1",
                                  transition: "background 0.2s",
                                }}
                                className="stats-row-template"
                              >
                                <td
                                  style={{
                                    padding: "12px 16px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    color: "#1e293b",
                                  }}
                                >
                                  <i
                                    className={`fa-solid ${isTemplateExpanded ? "fa-chevron-down" : "fa-chevron-right"}`}
                                    style={{ color: "#64748b", width: "12px" }}
                                  />
                                  <i
                                    className="fa-solid fa-folder-open"
                                    style={{ color: "var(--color-1)" }}
                                  />
                                  <span>{template.templateName}</span>
                                </td>
                                <td
                                  style={{
                                    padding: "12px 16px",
                                    textAlign: "center",
                                    color: "#cbd5e1",
                                  }}
                                >
                                  —
                                </td>
                                <td
                                  style={{
                                    padding: "12px 16px",
                                    textAlign: "right",
                                    color: "#1e293b",
                                  }}
                                >
                                  {template.libraryCount}{" "}
                                  <span style={{ color: "#10b981", fontSize: "0.82rem", fontWeight: "600" }}>
                                    ({templateReadyLibraries} ready)
                                  </span>
                                </td>
                                <td
                                  style={{
                                    padding: "12px 16px",
                                    textAlign: "right",
                                    color: "#1e293b",
                                  }}
                                >
                                  {templateTotalQuestions}{" "}
                                  <span style={{ color: "#10b981", fontWeight: "600", fontSize: "0.85rem" }}>
                                    ({templateTotalValid})
                                  </span>
                                </td>
                                <td
                                  style={{
                                    padding: "12px 16px",
                                    textAlign: "right",
                                    color: "#ef4444",
                                  }}
                                >
                                  {templateTotalInvalid - (templateTotalUserInvalid || 0)}{" "}
                                  <span style={{ color: "#2563eb" }}>({templateTotalUserInvalid || 0})</span>
                                </td>
                                <td
                                  style={{
                                    padding: "12px 16px",
                                    textAlign: "right",
                                    color: "#3b82f6",
                                  }}
                                >
                                  <div>{templateTotalDiagram}</div>
                                  <div style={{ color: "#10b981", fontSize: "0.8rem", fontWeight: "600", marginTop: "2px" }}>
                                    ({templateReviewedDiagram} reviewed)
                                  </div>
                                </td>
                                <td style={{ padding: "12px 16px", textAlign: "center" }}>—</td>
                                <td style={{ padding: "12px 16px", textAlign: "center" }}>—</td>
                              </tr>

                              {/* Subjects inside template */}
                              {isTemplateExpanded &&
                                template.subjects.map((subj) => {
                                  const subjectKey = `${template.templateId}-${subj.subjectName}`;
                                  const isSubjectExpanded =
                                    expandedSubjects.has(subjectKey);
                                  const subjReviewedDiagram = subj.libraries.reduce((sum, lib) => {
                                    const foundLib = libraries.find((l) => l._id === lib.libraryId);
                                    const isDiagramsReviewed = foundLib?.progress?.isQuestionDiagramsReviewed ?? false;
                                    return sum + (isDiagramsReviewed ? lib.diagramQuestions : 0);
                                  }, 0);
                                  const subjReadyLibraries = subj.libraries.reduce((sum, lib) => {
                                    const foundLib = libraries.find((l) => l._id === lib.libraryId);
                                    const isReady = foundLib?.progress?.isLibraryReady ?? false;
                                    return sum + (isReady ? 1 : 0);
                                  }, 0);

                                  return (
                                    <React.Fragment key={subj.subjectName}>
                                      <tr
                                        onClick={() =>
                                          toggleSubject(
                                            template.templateId,
                                            subj.subjectName,
                                          )
                                        }
                                        style={{
                                          cursor: "pointer",
                                          background: "#f8fafc",
                                          borderBottom: "1px solid #e2e8f0",
                                          transition: "background 0.2s",
                                        }}
                                        className="stats-row-subject"
                                      >
                                        <td
                                          style={{
                                            padding: "10px 16px 10px 40px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                          }}
                                        >
                                          <i
                                            className={`fa-solid ${isSubjectExpanded ? "fa-chevron-down" : "fa-chevron-right"}`}
                                            style={{
                                              color: "#94a3b8",
                                              fontSize: "0.85rem",
                                              width: "10px",
                                            }}
                                          />
                                          <i
                                            className="fa-solid fa-tags"
                                            style={{
                                              color: "#10b981",
                                              fontSize: "0.9rem",
                                            }}
                                          />
                                          <span
                                            style={{
                                              color: "#334155",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {subj.subjectName}
                                          </span>
                                        </td>
                                        <td
                                          style={{
                                            padding: "10px 16px",
                                            textAlign: "center",
                                            color: "#cbd5e1",
                                          }}
                                        >
                                          —
                                        </td>
                                        <td
                                          style={{
                                            padding: "10px 16px",
                                            textAlign: "right",
                                            color: "#475569",
                                          }}
                                        >
                                          {subj.libraries.length}{" "}
                                          <span style={{ color: "#10b981", fontSize: "0.8rem", fontWeight: "600" }}>
                                            ({subjReadyLibraries} ready)
                                          </span>
                                        </td>
                                        <td
                                          style={{
                                            padding: "10px 16px",
                                            textAlign: "right",
                                            color: "#334155",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {subj.totalQuestions}{" "}
                                          <span style={{ color: "#10b981", fontWeight: "600", fontSize: "0.82rem" }}>
                                            ({subj.validQuestions})
                                          </span>
                                        </td>
                                        <td
                                          style={{
                                            padding: "10px 16px",
                                            textAlign: "right",
                                            color: "#ef4444",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {subj.invalidQuestions - (subj.userInvalidQuestions || 0)}{" "}
                                          <span style={{ color: "#2563eb" }}>({subj.userInvalidQuestions || 0})</span>
                                        </td>
                                        <td
                                          style={{
                                            padding: "10px 16px",
                                            textAlign: "right",
                                            color: "#3b82f6",
                                            fontWeight: 500,
                                          }}
                                        >
                                          <div>{subj.diagramQuestions}</div>
                                          <div style={{ color: "#10b981", fontSize: "0.78rem", fontWeight: "600", marginTop: "2px" }}>
                                            ({subjReviewedDiagram} reviewed)
                                          </div>
                                        </td>
                                        <td style={{ padding: "10px 16px", textAlign: "center" }}>—</td>
                                        <td style={{ padding: "10px 16px", textAlign: "center" }}>—</td>
                                    </tr>

                                      {/* Libraries inside subject */}
                                      {isSubjectExpanded &&
                                        subj.libraries.map((lib) => {
                                          const foundLib = libraries.find(
                                            (l) => l._id === lib.libraryId,
                                          );
                                          const isInvalidResolved =
                                            foundLib?.progress
                                              ?.isInvalidQuestionResolved ??
                                            false;
                                          const isDiagramsReviewed =
                                            foundLib?.progress
                                              ?.isQuestionDiagramsReviewed ??
                                            false;
                                          const isLibraryReady =
                                            foundLib?.progress
                                              ?.isLibraryReady ??
                                            false;

                                          const isReady = isLibraryReady;
                                          const isBoth =
                                            isInvalidResolved &&
                                            isDiagramsReviewed;
                                          const isInvalid = isInvalidResolved;

                                          let rowBg = "#ffffff";
                                          let rowClass = "none-reviewed";

                                          if (isReady) {
                                            rowBg = "#dcfce7";
                                            rowClass = "library-ready";
                                          } else if (isBoth) {
                                            rowBg = "#e0f2fe";
                                            rowClass = "both-reviewed";
                                          } else if (isInvalid) {
                                            rowBg = "#ffedd5";
                                            rowClass = "invalid-reviewed";
                                          }

                                          return (
                                            <tr
                                              key={lib.libraryId}
                                              onClick={(e) => {
                                                if (foundLib) {
                                                  viewLibraryQuestions(
                                                    foundLib,
                                                    e,
                                                  );
                                                } else {
                                                  viewLibraryQuestions(
                                                    {
                                                      _id: lib.libraryId,
                                                      libraryName:
                                                        lib.libraryName,
                                                      status: "Completed",
                                                    },
                                                    e,
                                                  );
                                                }
                                              }}
                                              style={{
                                                background: rowBg,
                                                borderBottom:
                                                  "1px solid #f1f5f9",
                                                transition: "background 0.2s",
                                                cursor: "pointer",
                                              }}
                                              className={`stats-row-library ${rowClass}`}
                                            >
                                              <td
                                                style={{
                                                  padding: "8px 16px 8px 70px",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "8px",
                                                  color: "#475569",
                                                }}
                                              >
                                                <i
                                                  className="fa-solid fa-book"
                                                  style={{
                                                    color: "#3b82f6",
                                                    fontSize: "0.8rem",
                                                  }}
                                                />
                                                <span>{lib.libraryName}</span>
                                              </td>
                                              <td
                                                style={{
                                                  padding: "8px 16px",
                                                  textAlign: "center",
                                                }}
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                {foundLib?.questionSourceFileURL ? (
                                                  <a
                                                    href={
                                                      foundLib.questionSourceFileURL
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    style={{ color: "#3b82f6" }}
                                                    title="View Source"
                                                  >
                                                    <i className="fa-solid fa-eye" />
                                                  </a>
                                                ) : (
                                                  <span
                                                    style={{ color: "#cbd5e1" }}
                                                  >
                                                    —
                                                  </span>
                                                )}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "8px 16px",
                                                  textAlign: "right",
                                                  color: "#cbd5e1",
                                                }}
                                              >
                                                —
                                              </td>
                                              <td
                                                style={{
                                                  padding: "8px 16px",
                                                  textAlign: "right",
                                                  color: "#475569",
                                                  fontWeight: 500,
                                                }}
                                              >
                                                {lib.totalQuestions}{" "}
                                                <span style={{ color: "#10b981", fontWeight: "600", fontSize: "0.82rem" }}>
                                                  ({lib.validQuestions})
                                                </span>
                                              </td>
                                              <td
                                                style={{ padding: "8px 16px" }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "flex-end",
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      color: "#ef4444",
                                                      fontWeight: 500,
                                                    }}
                                                  >
                                                    {lib.invalidQuestions - (lib.userInvalidQuestions || 0)}{" "}
                                                    <span style={{ color: "#2563eb" }}>({lib.userInvalidQuestions || 0})</span>
                                                  </span>
                                                  <span
                                                    style={{
                                                      color: isInvalidResolved
                                                        ? "#10b981"
                                                        : "#ef4444",
                                                      fontWeight: 600,
                                                      fontSize: "0.75rem",
                                                      marginTop: "2px",
                                                    }}
                                                  >
                                                    (
                                                    {isInvalidResolved
                                                      ? "Resolved"
                                                      : "Not Resolved"}
                                                    )
                                                  </span>
                                                </div>
                                              </td>
                                              <td
                                                style={{ padding: "8px 16px" }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "flex-end",
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      color: "#3b82f6",
                                                      fontWeight: 500,
                                                    }}
                                                  >
                                                    {lib.diagramQuestions}
                                                  </span>
                                                  <span
                                                    style={{
                                                      color: isDiagramsReviewed
                                                        ? "#10b981"
                                                        : "#ef4444",
                                                      fontWeight: 600,
                                                      fontSize: "0.75rem",
                                                      marginTop: "2px",
                                                    }}
                                                  >
                                                    (
                                                    {isDiagramsReviewed
                                                      ? "Reviewed"
                                                      : "Not Reviewed"}
                                                    )
                                                  </span>
                                                </div>
                                              </td>
                                              <td style={{ padding: "8px 16px", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                            <button
                                              onClick={(e) => handleGenerateLibraryPDF(e, lib.libraryId, lib.libraryName)}
                                              disabled={generatingPdfId === lib.libraryId}
                                              style={{
                                                background: "none",
                                                border: "none",
                                                cursor: generatingPdfId === lib.libraryId ? "not-allowed" : "pointer",
                                                color: generatingPdfId === lib.libraryId ? "#94a3b8" : "#e11d48",
                                                fontSize: "1.1rem"
                                              }}
                                              title={generatingPdfId === lib.libraryId ? "Generating PDF..." : "Download PDF"}
                                            >
                                              {generatingPdfId === lib.libraryId ? (
                                                <i className="fa-solid fa-spinner fa-spin" />
                                              ) : (
                                                <i className="fa-solid fa-file-pdf" />
                                              )}
                                            </button>
                                          </td>
                                          <td style={{ padding: "8px 16px", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setDownloadModal({
                                                  isOpen: true,
                                                  libraryId: lib.libraryId,
                                                  libraryName: lib.libraryName,
                                                  type: "AI Solution"
                                                });
                                              }}
                                              disabled={generatingAISolutionsId === lib.libraryId}
                                              style={{
                                                background: "none",
                                                border: "none",
                                                cursor: generatingAISolutionsId === lib.libraryId ? "not-allowed" : "pointer",
                                                color: generatingAISolutionsId === lib.libraryId ? "#94a3b8" : "#8b5cf6",
                                                fontSize: "1.1rem"
                                              }}
                                              title={generatingAISolutionsId === lib.libraryId ? "Generating AI Solutions..." : "Download AI Solutions"}
                                            >
                                              {generatingAISolutionsId === lib.libraryId ? (
                                                <i className="fa-solid fa-spinner fa-spin" />
                                              ) : (
                                                <i className="fa-solid fa-lightbulb" />
                                              )}
                                            </button>
                                          </td>
                                          <td style={{ padding: "8px 16px", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setDownloadModal({
                                                  isOpen: true,
                                                  libraryId: lib.libraryId,
                                                  libraryName: lib.libraryName,
                                                  type: "Answer Key"
                                                });
                                              }}
                                              disabled={generatingAnswerKeyId === lib.libraryId}
                                              style={{
                                                background: "none",
                                                border: "none",
                                                cursor: generatingAnswerKeyId === lib.libraryId ? "not-allowed" : "pointer",
                                                color: generatingAnswerKeyId === lib.libraryId ? "#94a3b8" : "#10b981",
                                                fontSize: "1.1rem"
                                              }}
                                              title={generatingAnswerKeyId === lib.libraryId ? "Generating Answer Key..." : "Download Answer Key"}
                                            >
                                              {generatingAnswerKeyId === lib.libraryId ? (
                                                <i className="fa-solid fa-spinner fa-spin" />
                                              ) : (
                                                <i className="fa-solid fa-key" />
                                              )}
                                            </button>
                                          </td>
                                        </tr>
                                          );
                                        })}
                                    </React.Fragment>
                                  );
                                })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Library Questions Detail View ── */}
      {viewingLibrary && (
        <div
          className="container"
          style={{ marginTop: "20px", marginBottom: "40px" }}
        >
          {/* Library Header Card */}
          <div
            style={{
              background: "linear-gradient(135deg, #fff 0%, #fffbf2 100%)",
              border: "1.5px solid rgba(240, 154, 33, 0.2)",
              borderRadius: "16px",
              marginBottom: "24px",
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            }}
          >
            <div style={{ padding: "24px 28px" }}>
              {/* Top row: Title + stats */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: "16px",
                  marginBottom: "20px",
                }}
              >
                {/* Title + subject */}
                <div
                  style={{ display: "flex", alignItems: "center", gap: "14px" }}
                >
                  <div
                    style={{
                      width: "52px",
                      height: "52px",
                      borderRadius: "14px",
                      background:
                        "linear-gradient(135deg, var(--color-1), #e08800)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: "0 4px 14px rgba(240,154,33,0.35)",
                    }}
                  >
                    <i
                      className="fa-solid fa-book-open"
                      style={{ color: "#fff", fontSize: "1.3rem" }}
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <h2
                        style={{
                          margin: 0,
                          fontWeight: 800,
                          fontSize: "1.45rem",
                          color: "#0f172a",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {viewingLibrary.libraryName ||
                          viewingLibrary.subject ||
                          "Untitled Library"}
                      </h2>
                      <button
                        className="edit-btn"
                        onClick={() =>
                          handleOpenEditLibraryModal(viewingLibrary)
                        }
                        title="Edit Library Details"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "var(--color-1)",
                          fontSize: "1.1rem",
                          padding: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <i className="fa-solid fa-pen-to-square" />
                      </button>
                    </div>
                    <span
                      style={{
                        display: "inline-block",
                        marginTop: "4px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: "var(--color-1)",
                        background: "rgba(240,154,33,0.1)",
                        padding: "2px 10px",
                        borderRadius: "100px",
                        letterSpacing: "0.03em",
                      }}
                    >
                      Library
                    </span>
                  </div>
                </div>

                {/* Stats chip */}
                {!loadingQuestions && (
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    {/* Progress Audit Card */}
                    <div style={{ flexShrink: 0 }}>
                      {renderLibraryProgress(viewingLibrary)}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "rgba(141,198,63,0.1)",
                        border: "1.5px solid rgba(141,198,63,0.3)",
                        borderRadius: "12px",
                        padding: "8px 16px",
                      }}
                    >
                      <i
                        className="fa-solid fa-layer-group"
                        style={{ color: "var(--color-3)", fontSize: "1rem" }}
                      />
                      <div>
                        <div
                          style={{
                            fontWeight: 800,
                            fontSize: "1.2rem",
                            color: "#1e293b",
                            lineHeight: 1,
                          }}
                        >
                          {libraryQuestions.length}
                        </div>
                        <div
                          style={{
                            fontSize: "0.72rem",
                            color: "#64748b",
                            fontWeight: 600,
                            letterSpacing: "0.04em",
                          }}
                        >
                          QUESTIONS
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "rgba(239, 68, 68, 0.1)",
                        border: "1.5px solid rgba(239, 68, 68, 0.3)",
                        borderRadius: "12px",
                        padding: "8px 16px",
                      }}
                    >
                      <i
                        className="fa-solid fa-triangle-exclamation"
                        style={{ color: "#ef4444", fontSize: "1rem" }}
                      />
                      <div>
                        <div
                          style={{
                            fontWeight: 800,
                            fontSize: "1.2rem",
                            color: "#ef4444",
                            lineHeight: 1,
                          }}
                        >
                          {aiInvalidCount}{" "}
                          <span style={{ color: "#f97316" }}>({userInvalidCount})</span>
                        </div>
                        <div
                          style={{
                            fontSize: "0.72rem",
                            color: "#64748b",
                            fontWeight: 600,
                            letterSpacing: "0.04em",
                          }}
                        >
                          INVALID
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "rgba(59, 130, 246, 0.1)",
                        border: "1.5px solid rgba(59, 130, 246, 0.3)",
                        borderRadius: "12px",
                        padding: "8px 16px",
                      }}
                    >
                      <i
                        className="fa-regular fa-image"
                        style={{ color: "#3b82f6", fontSize: "1rem" }}
                      />
                      <div>
                        <div
                          style={{
                            fontWeight: 800,
                            fontSize: "1.2rem",
                            color: "#1e293b",
                            lineHeight: 1,
                          }}
                        >
                          {totalWithImage}
                        </div>
                        <div
                          style={{
                            fontSize: "0.72rem",
                            color: "#64748b",
                            fontWeight: 600,
                            letterSpacing: "0.04em",
                          }}
                        >
                          IMAGE QS
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "rgba(245, 158, 11, 0.1)",
                        border: "1.5px solid rgba(245, 158, 11, 0.3)",
                        borderRadius: "12px",
                        padding: "8px 16px",
                      }}
                    >
                      <i
                        className="fa-solid fa-wand-magic-sparkles"
                        style={{ color: "#f59e0b", fontSize: "1rem" }}
                      />
                      <div>
                        <div
                          style={{
                            fontWeight: 800,
                            fontSize: "1.2rem",
                            color: "#1e293b",
                            lineHeight: 1,
                          }}
                        >
                          {totalRecommended}
                        </div>
                        <div
                          style={{
                            fontSize: "0.72rem",
                            color: "#64748b",
                            fontWeight: 600,
                            letterSpacing: "0.04em",
                          }}
                        >
                          RECOMMENDED
                        </div>
                      </div>
                    </div>
                    {selectedQuestionIds.size > 0 && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          background: "rgba(240,154,33,0.1)",
                          border: "1.5px solid rgba(240,154,33,0.3)",
                          borderRadius: "12px",
                          padding: "8px 16px",
                        }}
                      >
                        <i
                          className="fa-solid fa-check-square"
                          style={{ color: "var(--color-1)", fontSize: "1rem" }}
                        />
                        <div>
                          <div
                            style={{
                              fontWeight: 800,
                              fontSize: "1.2rem",
                              color: "#1e293b",
                              lineHeight: 1,
                            }}
                          >
                            {selectedQuestionIds.size}
                          </div>
                          <div
                            style={{
                              fontSize: "0.72rem",
                              color: "#64748b",
                              fontWeight: 600,
                              letterSpacing: "0.04em",
                            }}
                          >
                            SELECTED
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Divider */}
              <div
                style={{
                  borderTop: "1px solid rgba(240,154,33,0.15)",
                  marginBottom: "18px",
                }}
              />

              {/* Meta badges row */}
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {viewingLibrary.grade && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      background: "#f1f5f9",
                      borderRadius: "8px",
                      padding: "6px 14px",
                      fontSize: "0.85rem",
                    }}
                  >
                    <i
                      className="fa-solid fa-graduation-cap"
                      style={{ color: "var(--color-1)", fontSize: "0.8rem" }}
                    />
                    <span style={{ fontWeight: 600, color: "#334155" }}>
                      Grade:
                    </span>
                    <span style={{ color: "#64748b" }}>
                      {viewingLibrary.grade}
                    </span>
                  </div>
                )}
                {viewingLibrary.curriculum && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      background: "#f1f5f9",
                      borderRadius: "8px",
                      padding: "6px 14px",
                      fontSize: "0.85rem",
                    }}
                  >
                    <i
                      className="fa-solid fa-globe"
                      style={{ color: "var(--color-1)", fontSize: "0.8rem" }}
                    />
                    <span style={{ fontWeight: 600, color: "#334155" }}>
                      Curriculum:
                    </span>
                    <span style={{ color: "#64748b" }}>
                      {viewingLibrary.curriculum}
                    </span>
                  </div>
                )}
                {viewingLibrary.year && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      background: "#f1f5f9",
                      borderRadius: "8px",
                      padding: "6px 14px",
                      fontSize: "0.85rem",
                    }}
                  >
                    <i
                      className="fa-solid fa-calendar"
                      style={{ color: "var(--color-1)", fontSize: "0.8rem" }}
                    />
                    <span style={{ fontWeight: 600, color: "#334155" }}>
                      Year:
                    </span>
                    <span style={{ color: "#64748b" }}>
                      {viewingLibrary.year}
                    </span>
                  </div>
                )}
                {viewingLibrary.createdAt && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      background: "#f1f5f9",
                      borderRadius: "8px",
                      padding: "6px 14px",
                      fontSize: "0.85rem",
                    }}
                  >
                    <i
                      className="fa-solid fa-calendar-days"
                      style={{ color: "var(--color-1)", fontSize: "0.8rem" }}
                    />
                    <span style={{ fontWeight: 600, color: "#334155" }}>
                      Created:
                    </span>
                    <span style={{ color: "#64748b" }}>
                      {new Date(viewingLibrary.createdAt).toLocaleDateString(
                        "en-US",
                        {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        },
                      )}
                    </span>
                  </div>
                )}
                {viewingLibrary.questionSourceFileURL && (
                  <a
                    href={viewingLibrary.questionSourceFileURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      background: "#eff6ff",
                      border: "1px solid rgba(59, 130, 246, 0.2)",
                      borderRadius: "8px",
                      padding: "6px 14px",
                      fontSize: "0.85rem",
                      textDecoration: "none",
                      color: "#2563eb",
                      fontWeight: 600,
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#dbeafe";
                      e.currentTarget.style.borderColor =
                        "rgba(59, 130, 246, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#eff6ff";
                      e.currentTarget.style.borderColor =
                        "rgba(59, 130, 246, 0.2)";
                    }}
                    title="View Source File"
                  >
                    <i
                      className="fa-solid fa-file-pdf"
                      style={{ color: "#ef4444", fontSize: "0.85rem" }}
                    />
                    <span>Source File</span>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* ── Subject Stats Cards ── */}
          {!loadingQuestions &&
            libraryQuestions.length > 0 &&
            subjectStats.length > 0 && (
              <div className="lib-subject-stats-section">
                <div className="lib-subject-stats-header">
                  <span className="lib-subject-stats-title">
                    <i className="fa-solid fa-chart-bar" />
                    Question Validity by Subject
                  </span>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      {totalInvalid > 0 && (
                        <span className="lib-invalid-total-badge">
                          <i className="fa-solid fa-triangle-exclamation" />
                          {aiInvalidCount}{" "}
                          <span style={{ color: "#f97316" }}>({userInvalidCount})</span> invalid question
                          {totalInvalid !== 1 ? "s" : ""} found
                        </span>
                      )}
                      {libraryQuestions.length > 0 && (
                        <>
                          <button
                            className={`lib-filter-btn${!showInvalidOnly && !subjectFilter ? " active" : ""}`}
                            onClick={() => {
                              setShowInvalidOnly(false);
                              setSubjectFilter(null);
                            }}
                          >
                            <i className="fa-solid fa-list" />
                            All Questions
                            <span className="lib-filter-count">
                              {libraryQuestions.length}
                            </span>
                          </button>
                          <button
                            className={`lib-filter-btn lib-filter-btn-invalid${showInvalidOnly && !subjectFilter ? " active" : ""}`}
                            onClick={() => {
                              setShowInvalidOnly(true);
                              setSubjectFilter(null);
                              setInvalidSubFilter("all");
                            }}
                            disabled={totalInvalid === 0}
                          >
                            <i className="fa-solid fa-triangle-exclamation" />
                            Invalid Questions
                            <span
                              className={`lib-filter-count${totalInvalid > 0 ? " has-invalid" : ""}`}
                            >
                              {aiInvalidCount}{" "}
                              <span className="lib-user-invalid-count">({userInvalidCount})</span>
                            </span>
                          </button>
                        </>
                      )}
                    </div>
                    {showInvalidOnly && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "0.85rem",
                          marginTop: "2px",
                        }}
                      >
                        <button
                          className={`lib-filter-btn${invalidSubFilter === "all" ? " active" : ""}`}
                          onClick={() => setInvalidSubFilter("all")}
                          style={{
                            padding: "4px 12px",
                            fontSize: "0.78rem",
                            borderRadius: "100px",
                            ...(invalidSubFilter === "all" ? {
                              background: "#ef4444",
                              borderColor: "#ef4444",
                              color: "white",
                              boxShadow: "0 2px 8px rgba(239, 68, 68, 0.2)",
                            } : {
                              background: "#f8fafc",
                              borderColor: "#e2e8f0",
                              color: "#64748b",
                            })
                          }}
                        >
                          All ({totalInvalid})
                        </button>
                        <button
                          className={`lib-filter-btn${invalidSubFilter === "ai" ? " active" : ""}`}
                          onClick={() => setInvalidSubFilter("ai")}
                          style={{
                            padding: "4px 12px",
                            fontSize: "0.78rem",
                            borderRadius: "100px",
                            ...(invalidSubFilter === "ai" ? {
                              background: "#ef4444",
                              borderColor: "#ef4444",
                              color: "white",
                              boxShadow: "0 2px 8px rgba(239, 68, 68, 0.2)",
                            } : {
                              background: "rgba(254, 242, 242, 0.6)",
                              borderColor: "rgba(239, 68, 68, 0.3)",
                              color: "#dc2626",
                            })
                          }}
                        >
                          AI Marked ({aiInvalidCount})
                        </button>
                        <button
                          className={`lib-filter-btn${invalidSubFilter === "user" ? " active" : ""}`}
                          onClick={() => setInvalidSubFilter("user")}
                          style={{
                            padding: "4px 12px",
                            fontSize: "0.78rem",
                            borderRadius: "100px",
                            ...(invalidSubFilter === "user" ? {
                              background: "#f97316",
                              borderColor: "#f97316",
                              color: "white",
                              boxShadow: "0 2px 8px rgba(249, 115, 22, 0.2)",
                            } : {
                              background: "rgba(255, 237, 213, 0.6)",
                              borderColor: "rgba(249, 115, 22, 0.35)",
                              color: "#ea580c",
                            })
                          }}
                        >
                          User Marked ({userInvalidCount})
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="lib-subject-stats-grid">
                  {subjectStats.map((stat) => (
                    <div
                      key={stat.subject}
                      className={`lib-subject-stat-card${stat.invalid > 0 ? " has-invalid" : ""}`}
                    >
                      {/* Header */}
                      <div className="lib-stat-card-header">
                        <div className="lib-stat-card-icon">
                          <i className="fa-solid fa-book" />
                        </div>
                        <div className="lib-stat-card-subject">
                          {stat.subject}
                        </div>
                        {stat.invalid > 0 && (
                          <div
                            className="lib-stat-card-invalid-dot"
                            title="Has invalid questions"
                          />
                        )}
                      </div>
                      {/* Stats row — each item is a clickable filter button */}
                      <div className="lib-stat-card-stats">
                        <button
                          className="lib-stat-item lib-stat-total"
                          onClick={() =>
                            setSubjectFilter(
                              subjectFilter?.subject === stat.subject &&
                                subjectFilter?.type === "all"
                                ? null
                                : { subject: stat.subject, type: "all" },
                            )
                          }
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            outline:
                              subjectFilter?.subject === stat.subject &&
                                subjectFilter?.type === "all"
                                ? "2px solid #6366f1"
                                : "none",
                            borderRadius: "8px",
                            padding: "4px 8px",
                          }}
                          title={`Show all ${stat.subject} questions`}
                        >
                          <span className="lib-stat-value">{stat.total}</span>
                          <span className="lib-stat-label">TOTAL</span>
                        </button>
                        <div className="lib-stat-divider" />
                        <button
                          className="lib-stat-item lib-stat-valid"
                          onClick={() =>
                            setSubjectFilter(
                              subjectFilter?.subject === stat.subject &&
                                subjectFilter?.type === "valid"
                                ? null
                                : { subject: stat.subject, type: "valid" },
                            )
                          }
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            outline:
                              subjectFilter?.subject === stat.subject &&
                                subjectFilter?.type === "valid"
                                ? "2px solid #22c55e"
                                : "none",
                            borderRadius: "8px",
                            padding: "4px 8px",
                          }}
                          title={`Show valid ${stat.subject} questions`}
                        >
                          <span className="lib-stat-value">{stat.valid}</span>
                          <span className="lib-stat-label">VALID</span>
                        </button>
                        <div className="lib-stat-divider" />
                        <button
                          className="lib-stat-item lib-stat-invalid"
                          onClick={() => {
                            setSubjectFilter(
                              subjectFilter?.subject === stat.subject &&
                                subjectFilter?.type === "invalid"
                                ? null
                                : { subject: stat.subject, type: "invalid" },
                            );
                            setInvalidSubFilter("all");
                          }}
                          disabled={stat.invalid === 0}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: stat.invalid > 0 ? "pointer" : "default",
                            outline:
                              subjectFilter?.subject === stat.subject &&
                                subjectFilter?.type === "invalid"
                                ? "2px solid #ef4444"
                                : "none",
                            borderRadius: "8px",
                            padding: "4px 8px",
                            opacity: stat.invalid === 0 ? 0.45 : 1,
                          }}
                          title={`Show invalid ${stat.subject} questions`}
                        >
                          <span className="lib-stat-value">
                            {stat.aiInvalid}{" "}
                            <span style={{ color: "#f97316" }}>({stat.userInvalid})</span>
                          </span>
                          <span className="lib-stat-label">INVALID</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Questions Table */}
          {loadingQuestions ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "40px 0",
              }}
            >
              <Loader style={{ left: 0, top: 0 }} />
            </div>
          ) : (
            <div className="questions-view">
              <Table
                ref={tableRef}
                leftSearchActions={
                  <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                    <input
                      type="text"
                      placeholder="Search Q. Nos (e.g. 67, 77, 90)"
                      value={qNumberSearchQuery}
                      onChange={(e) => setQNumberSearchQuery(e.target.value)}
                      className="search-input"
                      style={{
                        width: "300px",
                        paddingRight: "2.5rem",
                      }}
                    />
                    {qNumberSearchQuery && (
                      <button
                        type="button"
                        onClick={() => setQNumberSearchQuery("")}
                        style={{
                          position: "absolute",
                          right: "12px",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#94a3b8",
                          fontSize: "1rem",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "0",
                        }}
                        title="Clear search"
                      >
                        <i className="fa-solid fa-circle-xmark" />
                      </button>
                    )}
                  </div>
                }
                searchActions={
                  <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    <button
                      type="button"
                      className="btn-submit"
                      onClick={() => setIsViewQuestionsModalOpen(true)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: "8px 16px",
                        background: "var(--color-1)",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontWeight: "600",
                        fontSize: "0.9rem",
                        boxShadow: "0 2px 4px rgba(240, 154, 33, 0.25)",
                        transition: "all 0.2s ease",
                        width: "auto",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 8px rgba(240, 154, 33, 0.35)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 4px rgba(240, 154, 33, 0.25)";
                      }}
                    >
                      <i className="fa-solid fa-eye"></i> View Questions
                    </button>

                    {viewingLibrary && libraryQuestions && libraryQuestions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setValJsonInput("");
                          setValCsvData(null);
                          setValCsvFileName("");
                          setValResults(null);
                          setValError(null);
                          setValFilter("all");
                          setIsValModalOpen(true);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          padding: "8px 16px",
                          background: "#16a34a",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontWeight: "600",
                          fontSize: "0.9rem",
                          boxShadow: "0 2px 4px rgba(22, 163, 74, 0.25)",
                          transition: "all 0.2s ease",
                          width: "auto",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-1px)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 8px rgba(22, 163, 74, 0.35)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 2px 4px rgba(22, 163, 74, 0.25)";
                        }}
                      >
                        <i className="fa-solid fa-square-check" /> Solution Validation
                      </button>
                    )}
                    {viewingLibrary && libraryQuestions && libraryQuestions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => handleDownloadCSV(viewingLibrary._id, viewingLibrary.libraryName || viewingLibrary.subject || "Library", "AI Solution")}
                        disabled={generatingAISolutionsId === viewingLibrary._id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          padding: "8px 16px",
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          cursor: generatingAISolutionsId === viewingLibrary._id ? "not-allowed" : "pointer",
                          fontWeight: "600",
                          fontSize: "0.9rem",
                          boxShadow: "0 2px 4px rgba(37, 99, 235, 0.25)",
                          transition: "all 0.2s ease",
                          width: "auto",
                          opacity: generatingAISolutionsId === viewingLibrary._id ? 0.7 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (generatingAISolutionsId !== viewingLibrary._id) {
                            e.currentTarget.style.transform = "translateY(-1px)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 8px rgba(37, 99, 235, 0.35)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (generatingAISolutionsId !== viewingLibrary._id) {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow =
                              "0 2px 4px rgba(37, 99, 235, 0.25)";
                          }
                        }}
                      >
                        {generatingAISolutionsId === viewingLibrary._id ? (
                          <i className="fa-solid fa-spinner fa-spin" />
                        ) : (
                          <i className="fa-solid fa-file-csv" />
                        )}
                        AI Solution
                      </button>
                    )}
                  </div>
                }
                data={displayedQuestions}
                columns={questionColumns}
                caption={(() => {
                  const sel =
                    selectedQuestionIds.size > 0
                      ? ` (${selectedQuestionIds.size} Selected)`
                      : "";
                  if (subjectFilter) {
                    const typeLabel =
                      subjectFilter.type === "invalid"
                        ? `Invalid ${displayedQuestions.length} Questions`
                        : subjectFilter.type === "valid"
                          ? `Valid ${displayedQuestions.length} Questions`
                          : `Total ${displayedQuestions.length} Questions`;
                    return `${subjectFilter.subject} — ${typeLabel}${sel}`;
                  }
                  return `Questions${sel}${showInvalidOnly ? " — Invalid Questions" : ""}`;
                })()}
                selectedRowIds={selectedQuestionIds}
                onRowSelect={handleCheckboxSelect}
                onSelectAll={handleSelectAll}
                isCheckBoxAvailable={true}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Invalidity Info Modal ── */}
      {invalidInfoQuestion &&
        (() => {
          const q = invalidInfoQuestion;
          const type = (q.type || "").toLowerCase();
          const isMCQ = type === "mcq";
          const storedCorrect = isMCQ
            ? Array.isArray(q.correctOptions)
              ? q.correctOptions.join(", ")
              : "—"
            : String(q.correctAnswer || "").trim() || "—";
          const akCorrect = isMCQ
            ? Array.isArray(q.answerKeyCorrectOptions)
              ? q.answerKeyCorrectOptions.join(", ")
              : "—"
            : String(q.answerKeyCorrectAnswer || "").trim() || "—";
          const mismatch = storedCorrect !== akCorrect;
          return (
            <Modal
              isOpen={isInvalidInfoModalOpen}
              onClose={() => {
                setIsInvalidInfoModalOpen(false);
                setInvalidInfoQuestion(null);
              }}
              maxWidth="520px"
            >
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "1.2rem",
                }}
              >
                <div
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "10px",
                    background: "linear-gradient(135deg,#f87171,#ef4444)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 3px 10px rgba(239,68,68,0.3)",
                  }}
                >
                  <i
                    className="fa-solid fa-circle-info"
                    style={{ color: "white", fontSize: "1rem" }}
                  />
                </div>
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      color: "#1e293b",
                    }}
                  >
                    Invalidity Details
                  </h3>
                  <p
                    style={{ margin: 0, fontSize: "0.8rem", color: "#64748b" }}
                  >
                    Type:{" "}
                    <strong style={{ textTransform: "uppercase" }}>
                      {q.type || "—"}
                    </strong>
                  </p>
                </div>
              </div>

              {/* Cause banner */}
              <div
                style={{
                  background: q?.invalidReason !== "Generated Solution mismatch with given answer key" ? "rgba(255, 237, 213, 0.85)" : "rgba(254,226,226,0.6)",
                  border: q?.invalidReason !== "Generated Solution mismatch with given answer key" ? "1.5px solid rgba(249, 115, 22, 0.4)" : "1.5px solid rgba(239,68,68,0.3)",
                  borderRadius: "10px",
                  padding: "12px 16px",
                  marginBottom: "1rem",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                }}
              >
                <i
                  className="fa-solid fa-triangle-exclamation"
                  style={{ color: q?.invalidReason !== "Generated Solution mismatch with given answer key" ? "#f97316" : "#ef4444", marginTop: "2px", flexShrink: 0 }}
                />
                <div
                  style={{
                    fontSize: "0.88rem",
                    color: q?.invalidReason !== "Generated Solution mismatch with given answer key" ? "#7c2d12" : "#7f1d1d",
                    lineHeight: 1.5,
                  }}
                >
                  <strong>Cause:</strong> {q.invalidReason || (
                    <>
                      The correct answer does not match the answer key.
                      {isMCQ
                        ? " Correct options mismatch."
                        : " Numerical answer mismatch."}
                    </>
                  )}
                </div>
              </div>

              {/* Question Text & Image */}
              {(q.questionText || q.diagramFileURL || q.imageURL || q.svgCode) && (
                <div
                  style={{
                    background: "#f8fafc",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: "10px",
                    padding: "12px 14px",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      letterSpacing: "0.05em",
                      marginBottom: "10px",
                      textTransform: "uppercase",
                    }}
                  >
                    Question
                  </div>
                  {q.questionText && (
                    <div
                      style={{
                        fontSize: "0.85rem",
                        color: "#334155",
                        lineHeight: 1.6,
                        marginBottom: (q.diagramFileURL || q.imageURL || q.svgCode) ? "12px" : 0,
                      }}
                    >
                      <LazyMarkdown>
                        {formatLatexText(q.questionText)}
                      </LazyMarkdown>
                    </div>
                  )}
                  {(q.diagramFileURL || q.imageURL || q.svgCode) && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#ffffff",
                        padding: "8px",
                        maxWidth: "100%",
                        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        const imgUrl = q.diagramFileURL || q.imageURL;
                        if (imgUrl) window.open(imgUrl, "_blank");
                      }}
                      title="Click to view full size image"
                    >
                      {q.svgCode ? (
                        <div
                          className="modal-diagram-container"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            maxWidth: "100%",
                          }}
                        >
                          <style>{`
                            .modal-diagram-container svg {
                              max-width: 100% !important;
                              max-height: 200px !important;
                              height: auto !important;
                              width: auto !important;
                            }
                          `}</style>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: q.svgCode,
                            }}
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              maxWidth: "100%",
                              maxHeight: "200px",
                            }}
                          />
                        </div>
                      ) : (
                        <img
                          src={q.diagramFileURL || q.imageURL}
                          alt="Question Diagram"
                          style={{
                            maxHeight: "200px",
                            maxWidth: "100%",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Answer comparison */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                  marginBottom: "1rem",
                }}
              >
                {/* Stored answer */}
                <div
                  style={{
                    background: mismatch
                      ? "rgba(254,226,226,0.4)"
                      : "rgba(220,252,231,0.4)",
                    border: `1.5px solid ${mismatch ? "rgba(239,68,68,0.3)" : "rgba(34,197,94,0.3)"}`,
                    borderRadius: "10px",
                    padding: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      letterSpacing: "0.05em",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                    }}
                  >
                    Correct Answer
                  </div>
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: 800,
                      color: mismatch ? "#dc2626" : "#16a34a",
                    }}
                  >
                    {storedCorrect}
                  </div>
                </div>
                {/* Answer key */}
                <div
                  style={{
                    background: "rgba(219,234,254,0.4)",
                    border: "1.5px solid rgba(59,130,246,0.3)",
                    borderRadius: "10px",
                    padding: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      letterSpacing: "0.05em",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                    }}
                  >
                    Answer Key Answer
                  </div>
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: 800,
                      color: "#1d4ed8",
                    }}
                  >
                    {akCorrect}
                  </div>
                </div>
              </div>

              {/* Options (MCQ only) */}
              {isMCQ &&
                Array.isArray(q.options) &&
                q.options.filter(Boolean).length > 0 && (
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1.5px solid #e2e8f0",
                      borderRadius: "10px",
                      padding: "12px 14px",
                      marginBottom: "1rem",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: "#94a3b8",
                        letterSpacing: "0.05em",
                        marginBottom: "10px",
                        textTransform: "uppercase",
                      }}
                    >
                      Options
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      {q.options.filter(Boolean).map((opt) => {
                        const key = opt.key || "";
                        const isStored = (
                          Array.isArray(q.correctOptions)
                            ? q.correctOptions
                            : []
                        ).includes(key);
                        const isAK = (
                          Array.isArray(q.answerKeyCorrectOptions)
                            ? q.answerKeyCorrectOptions
                            : []
                        ).includes(key);
                        return (
                          <div
                            key={key}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "10px",
                              padding: "7px 10px",
                              borderRadius: "7px",
                              background: isAK
                                ? "rgba(219,234,254,0.5)"
                                : isStored
                                  ? "rgba(254,226,226,0.5)"
                                  : "transparent",
                              border: isAK
                                ? "1px solid rgba(59,130,246,0.3)"
                                : isStored
                                  ? "1px solid rgba(239,68,68,0.3)"
                                  : "1px solid transparent",
                            }}
                          >
                            <span
                              style={{
                                minWidth: "22px",
                                height: "22px",
                                borderRadius: "5px",
                                background: isAK
                                  ? "#3b82f6"
                                  : isStored
                                    ? "#ef4444"
                                    : "#e2e8f0",
                                color: isAK || isStored ? "white" : "#64748b",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                flexShrink: 0,
                              }}
                            >
                              {key}
                            </span>
                            <div
                              style={{
                                fontSize: "0.85rem",
                                color: "#334155",
                                lineHeight: 1.4,
                                flex: 1,
                              }}
                            >
                              <LazyMarkdown>
                                {formatLatexText(opt.text || "")}
                              </LazyMarkdown>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "3px",
                                alignItems: "flex-end",
                                flexShrink: 0,
                              }}
                            >
                              {isStored && (
                                <span
                                  style={{
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    color: "#dc2626",
                                    background: "rgba(254,226,226,0.8)",
                                    padding: "1px 6px",
                                    borderRadius: "4px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Solution
                                </span>
                              )}
                              {isAK && (
                                <span
                                  style={{
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    color: "#1d4ed8",
                                    background: "rgba(219,234,254,0.8)",
                                    padding: "1px 6px",
                                    borderRadius: "4px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Answer Key
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {/* Solution */}
              {q.solution && (
                <div
                  style={{
                    background: "#f8fafc",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: "10px",
                    padding: "12px 14px",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      letterSpacing: "0.05em",
                      marginBottom: "10px",
                      textTransform: "uppercase",
                    }}
                  >
                    Solution
                  </div>
                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: "#334155",
                      lineHeight: 1.6,
                    }}
                  >
                    <LazyMarkdown>
                      {q.solution
                        .replace(/\\\(/g, "$")
                        .replace(/\\\)/g, "$")
                        .replace(/\\\[/g, "$$")
                        .replace(/\\\]/g, "$$")}
                    </LazyMarkdown>
                  </div>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "1rem",
                }}
              >
                <button
                  onClick={() => {
                    const details = {
                      subject: q.subject || "",
                      topic: q.topic || "",
                      subTopic: q.subTopic || "",
                      type: q.type || "",
                      questionText: q.questionText || "",
                      options: Array.isArray(q.options)
                        ? q.options
                          .filter(Boolean)
                          .map((opt) => ({ key: opt.key, text: opt.text }))
                        : [],
                      correctOption: Array.isArray(q.correctOptions)
                        ? q.correctOptions.join(", ")
                        : q.correctOptions || "",
                      answerKeyCorrectOption: Array.isArray(
                        q.answerKeyCorrectOptions,
                      )
                        ? q.answerKeyCorrectOptions.join(", ")
                        : q.answerKeyCorrectOptions || "",
                      correctAnswer: q.correctAnswer || "",
                      answerKeyCorrectAnswer: q.answerKeyCorrectAnswer || "",
                      solution: q.solution || "",
                    };
                    const imageLink = q.diagramFileURL || q.imageURL;
                    if (imageLink) {
                      details.imageURL = imageLink;
                    }
                    copyToClipboard(JSON.stringify(details, null, 2));
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                    color: "white",
                    border: "none",
                    borderRadius: "100px",
                    padding: "0.65rem 1.4rem",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(59, 130, 246, 0.2)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 6px rgba(59, 130, 246, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 4px rgba(59, 130, 246, 0.2)";
                  }}
                >
                  <i className="fa-regular fa-copy" /> Copy Details
                </button>
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setIsInvalidInfoModalOpen(false);
                    setInvalidInfoQuestion(null);
                  }}
                >
                  Close
                </button>
              </div>
            </Modal>
          );
        })()}

      {/* ── Diagram Image Viewer Modal ── */}
      <Modal
        isOpen={isDiagramImageModalOpen}
        onClose={() => {
          setIsDiagramImageModalOpen(false);
          setDiagramQuestion(null);
          setSelectedImageQuestion(null);
        }}
        maxWidth="460px"
      >
        <h2 style={{ marginBottom: "0.8rem" }}>
          <i
            className="fa-regular fa-image"
            style={{ marginRight: "8px", color: "var(--color-1)" }}
          />
          Question Diagram
        </h2>

        {/* Image display */}
        {(diagramQuestion?.diagramFileURL ||
          diagramQuestion?.imageURL ||
          diagramQuestion?.svgCode) && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                maxHeight: "300px",
                background: "#f8fafc",
                borderRadius: "10px",
                overflow: "hidden",
                border: "2px solid #e2e8f0",
                marginBottom: "1rem",
              }}
            >
              {diagramQuestion.svgCode ? (
                <div
                  dangerouslySetInnerHTML={{ __html: diagramQuestion.svgCode }}
                  style={{
                    width: "100%",
                    maxHeight: "260px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "16px",
                  }}
                />
              ) : (
                <ImageWithErrorHandler
                  src={diagramQuestion.diagramFileURL || diagramQuestion.imageURL}
                  alt="Question Diagram"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "260px",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              )}
            </div>
          )}

        {/* Action buttons */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "0.6rem",
            marginTop: "1rem",
          }}
        >
          {/* Upload / Replace */}
          <button
            type="button"
            className="btn-submit"
            onClick={() => {
              setSelectedImageQuestion(diagramQuestion);
              setIsFileUploadModalOpen(true);
            }}
            disabled={isDeletingDiagram}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "10px 8px",
              background: isDeletingDiagram ? "#94a3b8" : "linear-gradient(45deg, black, grey)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              cursor: isDeletingDiagram ? "not-allowed" : "pointer",
              fontWeight: "600",
              fontSize: "0.8rem",
              lineHeight: 1.3,
              textAlign: "center",
            }}
            onMouseEnter={(e) => {
              if (!isDeletingDiagram) e.currentTarget.style.opacity = "0.9";
            }}
            onMouseLeave={(e) => {
              if (!isDeletingDiagram) e.currentTarget.style.opacity = "1";
            }}
          >
            <i
              className="fa-solid fa-cloud-arrow-up"
              style={{ fontSize: "1.15rem" }}
            />
            {diagramQuestion?.diagramFileURL ||
              diagramQuestion?.imageURL ||
              diagramQuestion?.svgCode
              ? "Upload Replacement"
              : "Upload Diagram"}
          </button>

          <button
            type="button"
            className="btn-submit"
            onClick={() => {
              setIsDiagramImageModalOpen(false);
              handlePasteImage(diagramQuestion);
            }}
            disabled={isDeletingDiagram}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "10px 8px",
              background: isDeletingDiagram ? "#94a3b8" : "linear-gradient(45deg, #10b981, #059669)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              cursor: isDeletingDiagram ? "not-allowed" : "pointer",
              fontWeight: "600",
              fontSize: "0.8rem",
              lineHeight: 1.3,
              textAlign: "center",
            }}
            onMouseEnter={(e) => {
              if (!isDeletingDiagram) e.currentTarget.style.opacity = "0.9";
            }}
            onMouseLeave={(e) => {
              if (!isDeletingDiagram) e.currentTarget.style.opacity = "1";
            }}
          >
            <i
              className="fa-solid fa-paste"
              style={{ fontSize: "1.15rem" }}
            />
            Paste Image
          </button>

          {/* Resize / Recrop — when diagramPageFileURL or diagramFileURL/imageURL exists */}
          {(diagramQuestion?.diagramPageFileURL ||
            diagramQuestion?.diagramFileURL ||
            diagramQuestion?.imageURL) && (
              <button
                type="button"
                className="btn-submit"
                onClick={() => setIsCropModalOpen(true)}
                disabled={isDeletingDiagram}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  padding: "10px 8px",
                  background: isDeletingDiagram ? "#94a3b8" : "linear-gradient(45deg, black, grey)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: isDeletingDiagram ? "not-allowed" : "pointer",
                  fontWeight: "600",
                  fontSize: "0.8rem",
                  lineHeight: 1.3,
                  textAlign: "center",
                }}
                onMouseEnter={(e) => {
                  if (!isDeletingDiagram) e.currentTarget.style.opacity = "0.9";
                }}
                onMouseLeave={(e) => {
                  if (!isDeletingDiagram) e.currentTarget.style.opacity = "1";
                }}
              >
                <i
                  className="fa-solid fa-crop-simple"
                  style={{ fontSize: "1.15rem" }}
                />
                Resize Diagram
              </button>
            )}

          {/* Delete Diagram */}
          {(diagramQuestion?.diagramFileURL ||
            diagramQuestion?.imageURL ||
            diagramQuestion?.svgCode) && (
              <button
                type="button"
                className="btn-submit"
                onClick={() => handleDeleteDiagram(diagramQuestion)}
                disabled={isDeletingDiagram}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  padding: "10px 8px",
                  background: isDeletingDiagram ? "#94a3b8" : "linear-gradient(45deg, black, grey)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: isDeletingDiagram ? "not-allowed" : "pointer",
                  fontWeight: "600",
                  fontSize: "0.8rem",
                  lineHeight: 1.3,
                  textAlign: "center",
                }}
                onMouseEnter={(e) => {
                  if (!isDeletingDiagram) e.currentTarget.style.opacity = "0.9";
                }}
                onMouseLeave={(e) => {
                  if (!isDeletingDiagram) e.currentTarget.style.opacity = "1";
                }}
              >
                <i
                  className={isDeletingDiagram ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-trash"}
                  style={{ fontSize: "1.15rem" }}
                />
                {isDeletingDiagram ? "Deleting..." : "Delete Diagram"}
              </button>
            )}
        </div>
      </Modal>

      {/* ── File Upload Modal ── */}
      <Modal
        isOpen={isFileUploadModalOpen}
        onClose={() => {
          if (!isUploadingFile) {
            setIsFileUploadModalOpen(false);
            setSelectedUploadFile(null);
          }
        }}
        maxWidth="500px"
        zIndex={10000}
      >
        <h2
          style={{
            marginBottom: "1.2rem",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <i
            className="fa-solid fa-cloud-arrow-up"
            style={{ color: "var(--color-1)" }}
          />
          Upload Diagram Image
        </h2>

        <div style={{ margin: "1.5rem 0" }}>
          <FileUploadSection
            currentFileUrl={null}
            currentFileName={
              selectedUploadFile ? selectedUploadFile.name : null
            }
            isSubmitting={isUploadingFile}
            accept="image/*"
            allowPasteImage={true}
            browseDescription="Select image file from device (or Paste)"
            captureDescription="Take photo of diagram"
            selectedFile={selectedUploadFile}
            onFileChange={(file) => setSelectedUploadFile(file)}
            onRemoveSelectedFile={() => setSelectedUploadFile(null)}
          />
        </div>

        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            padding: "14px",
            marginBottom: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {/* Switch 1: Apply All & Auto Save */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "#1e293b" }}>Auto Save</div>
              <div style={{ fontSize: "0.75rem", color: "#64748b" }}>Process and save diagram directly without editing</div>
            </div>
            <label className="switch" style={{ position: "relative", display: "inline-block", width: "42px", height: "24px" }}>
              <input
                type="checkbox"
                checked={applyAllAutoSave}
                onChange={(e) => setApplyAllAutoSave(e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span
                style={{
                  position: "absolute",
                  cursor: "pointer",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: applyAllAutoSave ? "var(--color-1)" : "#cbd5e1",
                  transition: "0.2s",
                  borderRadius: "24px",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    content: '""',
                    height: "18px",
                    width: "18px",
                    left: "3px",
                    bottom: "3px",
                    backgroundColor: "white",
                    transition: "0.2s",
                    borderRadius: "50%",
                    transform: applyAllAutoSave ? "translateX(18px)" : "translateX(0)",
                  }}
                />
              </span>
            </label>
          </div>

          {/* Switch 2: No Crop */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "#1e293b" }}>No Crop</div>
              <div style={{ fontSize: "0.75rem", color: "#64748b" }}>Keep the full original image dimensions</div>
            </div>
            <label className="switch" style={{ position: "relative", display: "inline-block", width: "42px", height: "24px" }}>
              <input
                type="checkbox"
                checked={noCropDefault}
                onChange={(e) => setNoCropDefault(e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span
                style={{
                  position: "absolute",
                  cursor: "pointer",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: noCropDefault ? "var(--color-1)" : "#cbd5e1",
                  transition: "0.2s",
                  borderRadius: "24px",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    content: '""',
                    height: "18px",
                    width: "18px",
                    left: "3px",
                    bottom: "3px",
                    backgroundColor: "white",
                    transition: "0.2s",
                    borderRadius: "50%",
                    transform: noCropDefault ? "translateX(18px)" : "translateX(0)",
                  }}
                />
              </span>
            </label>
          </div>

          {/* Switch 3: Remove Background */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "#1e293b" }}>Remove Background</div>
              <div style={{ fontSize: "0.75rem", color: "#64748b" }}>Remove white background automatically</div>
            </div>
            <label className="switch" style={{ position: "relative", display: "inline-block", width: "42px", height: "24px" }}>
              <input
                type="checkbox"
                checked={removeBgDefault}
                onChange={(e) => setRemoveBgDefault(e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span
                style={{
                  position: "absolute",
                  cursor: "pointer",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: removeBgDefault ? "var(--color-1)" : "#cbd5e1",
                  transition: "0.2s",
                  borderRadius: "24px",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    content: '""',
                    height: "18px",
                    width: "18px",
                    left: "3px",
                    bottom: "3px",
                    backgroundColor: "white",
                    transition: "0.2s",
                    borderRadius: "50%",
                    transform: removeBgDefault ? "translateX(18px)" : "translateX(0)",
                  }}
                />
              </span>
            </label>
          </div>
        </div>

        {isUploadingFile ? (
          <div style={{ margin: "1rem 0 0.5rem 0" }}>
            <div className="extract-pdf-progress-visual" style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <div
                className="extract-pdf-progress-icon"
                style={{
                  background:
                    "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
                  width: "45px",
                  height: "45px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i
                  className="fa-solid fa-gears fa-spin"
                  style={{ color: "white", fontSize: "1.2rem" }}
                />
              </div>
              <div className="extract-pdf-progress-bar-container" style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
                <div
                  className="oeb-generate-progress-bar"
                  style={{
                    background: "#e2e8f0",
                    borderRadius: "100px",
                    height: "8px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    className="oeb-generate-progress-fill"
                    style={{
                      background: "linear-gradient(90deg, #14b8a6, #0f766e)",
                      width: getProgressPercentage(),
                      height: "100%",
                      transition: "width 0.4s ease-in-out",
                    }}
                  />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontWeight: "600", color: "#0f766e", fontSize: "0.88rem" }}>
                    {uploadProgressText}
                  </span>
                  <span style={{ fontWeight: "700", color: "#0f766e", fontSize: "0.85rem" }}>
                    {getProgressPercentage()}
                  </span>
                </div>
              </div>
            </div>
            <p style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "1rem", display: "flex", alignItems: "center", gap: "6px", marginBottom: 0 }}>
              <i className="fa-solid fa-info-circle" />
              Please do not close this modal. We are uploading and processing the diagram.
            </p>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.75rem",
              marginTop: "1rem",
            }}
          >
            <button
              className="btn-submit"
              style={{
                background: "var(--color-1)",
                color: "white",
              }}
              onClick={handleUploadLibraryDiagramFile}
              disabled={!selectedUploadFile}
            >
              {applyAllAutoSave ? "Upload & Save" : "Upload & Crop"}
            </button>
          </div>
        )}
      </Modal>

      {/* ── Diagram Crop / Resize Modal ── */}
      <QuestionDiagramResizeModal
        isOpen={isCropModalOpen}
        onClose={() => setIsCropModalOpen(false)}
        question={diagramQuestion}
        serverUrl={serverUrl}
        onSaveSuccess={handleDiagramSaveSuccess}
        apiEndpoint="/api/library-mcq-bank/update-question-diagram"
        showPreview={true}
        isLibraryQuestion={true}
        onEditQuestion={handleOpenEditModal}
        onInvalidQuestion={handleOpenInvalidToggleModal}
        questionIndexMap={questionIndexMap}
        defaultNoCrop={noCropDefault}
        defaultRemoveBg={removeBgDefault}
      />

      {/* ── QB Type Selection Modal ── */}

      <Modal
        isOpen={isQBTypeModalOpen}
        onClose={() => setIsQBTypeModalOpen(false)}
        maxWidth="520px"
      >
        <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
          <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📚</div>
          <h2 style={{ fontSize: "1.4rem", fontWeight: 700, margin: 0 }}>
            Create General Library
          </h2>
          <p
            style={{
              color: "#6b7280",
              marginTop: "0.4rem",
              fontSize: "0.95rem",
            }}
          >
            Select how you want to build this general library.
          </p>
        </div>
        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}
        >
          {/* Option 1: AI Library Generator (Coming Soon) */}
          {/* <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              padding: "18px 20px",
              background: "#f8fafc",
              border: "1.5px solid #e2e8f0",
              borderRadius: "14px",
              opacity: 0.6,
              cursor: "not-allowed",
              textAlign: "left",
              width: "100%",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i className="fa-solid fa-robot" style={{ color: "white", fontSize: "1.4rem" }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontWeight: 700, fontSize: "1.05rem", color: "#64748b" }}>
                  AI Library Generator
                </span>
                <span
                  style={{
                    fontSize: "0.7rem",
                    background: "#cbd5e1",
                    padding: "2px 6px",
                    borderRadius: "100px",
                    color: "#475569",
                    fontWeight: 600,
                  }}
                >
                  COMING SOON
                </span>
              </div>
              <div style={{ fontSize: "0.86rem", color: "#94a3b8", lineHeight: 1.5 }}>
                Generate custom questions via AI from topics and curriculum settings.
              </div>
            </div>
          </div> */}

          {/* Option 1: Generate Similar (Multimodal inspiration) */}
          <button
            onClick={() => {
              setIsQBTypeModalOpen(false);
              setIsGenerateSimilarOpen(true);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              padding: "18px 20px",
              background: "white",
              border: "1.5px solid #e5e7eb",
              borderRadius: "14px",
              cursor: "pointer",
              textAlign: "left",
              transition: "border-color 0.2s, box-shadow 0.2s",
              width: "100%",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#0f766e";
              e.currentTarget.style.boxShadow =
                "0 0 0 3px rgba(15,118,110,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e5e7eb";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i
                className="fa-solid fa-wand-magic-sparkles"
                style={{ color: "white", fontSize: "1.4rem" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: "#111827",
                  marginBottom: "3px",
                }}
              >
                Generate Similar
              </div>
              <div
                style={{
                  fontSize: "0.86rem",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                Create brand-new questions inspired by a reference PDF test
                paper.
              </div>
            </div>
            <i
              className="fa-solid fa-chevron-right"
              style={{ color: "#9ca3af", fontSize: "0.9rem" }}
            />
          </button>

          {/* Option 2: PDF Extractor (Active) */}
          <button
            onClick={() => {
              setIsQBTypeModalOpen(false);
              setExtractPdfForm({
                libraryName: "",
                standardizedTestId: "",
                subject: "",
                topic: "",
                grade: "",
                curriculum: "",
                numberOfQs: "",
                customInstructions: "",
                sourceTestPaper: null,
                answerKeysFile: null,
                year: "",
                isPastExam: false,
              });
              setIsExtractPdfOpen(true);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              padding: "18px 20px",
              background: "white",
              border: "1.5px solid #e5e7eb",
              borderRadius: "14px",
              cursor: "pointer",
              textAlign: "left",
              transition: "border-color 0.2s, box-shadow 0.2s",
              width: "100%",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#0f766e";
              e.currentTarget.style.boxShadow =
                "0 0 0 3px rgba(15,118,110,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e5e7eb";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i
                className="fa-solid fa-file-pdf"
                style={{ color: "white", fontSize: "1.4rem" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: "#111827",
                  marginBottom: "3px",
                }}
              >
                PDF Extractor
              </div>
              <div
                style={{
                  fontSize: "0.86rem",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                Convert a test paper PDF into a structured library
                automatically.
              </div>
            </div>
            <i
              className="fa-solid fa-chevron-right"
              style={{ color: "#9ca3af", fontSize: "0.9rem" }}
            />
          </button>
        </div>
      </Modal>

      {/* ── Solution Modal ── */}
      <Modal
        isOpen={isSolutionModalOpen}
        onClose={() => setIsSolutionModalOpen(false)}
      >
        {!(
          activeQuestion?.solution || activeQuestion?.correctOptions?.length > 0
        ) ? (
          <p>No Solution Available. Retry Generating.</p>
        ) : (
          <>
            <h2>Solution Details</h2>
            <div
              className="solution-content"
              style={{
                background: "#f8fafc",
                border: "1.5px solid #e2e8f0",
                borderRadius: "8px",
                padding: "12px 16px",
                fontSize: "0.92rem",
                marginTop: "1rem",
                marginBottom: "1rem",
                lineHeight: 1.7,
                whiteSpace: "pre-wrap",
              }}
            >
              {activeQuestion?.type?.toUpperCase() === "MCQ" &&
                Array.isArray(activeQuestion.correctOptions) &&
                activeQuestion.correctOptions.length > 0 && (
                  <div
                    style={{
                      marginBottom: activeQuestion.solution ? "16px" : "0",
                    }}
                  >
                    <strong>Correct Option: </strong>
                    {activeQuestion.correctOptions.join(", ")}
                  </div>
                )}
              {activeQuestion?.solution && (
                <div>
                  <strong>Solution: </strong>
                  <div style={{ marginTop: "8px" }}>
                    <ReactMarkdown
                      children={activeQuestion.solution
                        .replace(/\\\(/g, "$")
                        .replace(/\\\)/g, "$")
                        .replace(/\\\[/g, "$$")
                        .replace(/\\\]/g, "$$")}
                      remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                      rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                className="btn-submit"
                onClick={() => copyToClipboard(activeQuestion?.solution)}
                title="Copy solution to clipboard"
              >
                <i className="fa-solid fa-copy" /> Copy Solution
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* ── Question Preview Modal ── */}
      <Modal
        isOpen={isQuestionModalOpen}
        onClose={() => setIsQuestionModalOpen(false)}
        maxWidth="660px"
      >
        {/* Question Details Badges */}
        {selectedQuestion && (
          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              marginBottom: "1.2rem",
            }}
          >
            {selectedQuestion.questionNumber && (
              <span
                style={{
                  background: "#f1f5f9",
                  padding: "4px 10px",
                  borderRadius: "6px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#475569",
                }}
              >
                Q. No: {selectedQuestion.questionNumber}
              </span>
            )}
            {selectedQuestion.subject && (
              <span
                style={{
                  background: "#eff6ff",
                  padding: "4px 10px",
                  borderRadius: "6px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#1d4ed8",
                }}
              >
                Subject: {selectedQuestion.subject}
              </span>
            )}
            {(selectedQuestion.sectionNumber || selectedQuestion.section) && (
              <span
                style={{
                  background: "#f5f3ff",
                  padding: "4px 10px",
                  borderRadius: "6px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#6d28d9",
                }}
              >
                Section:{" "}
                {selectedQuestion.sectionNumber || selectedQuestion.section}
              </span>
            )}
            {selectedQuestion.difficulty && (
              <span
                style={{
                  background: "#fef3c7",
                  padding: "4px 10px",
                  borderRadius: "6px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#b45309",
                }}
              >
                Difficulty: {selectedQuestion.difficulty}
              </span>
            )}
            {selectedQuestion.type && (
              <span
                style={{
                  background: "#ecfdf5",
                  padding: "4px 10px",
                  borderRadius: "6px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#047857",
                }}
              >
                Type: {selectedQuestion.type.toUpperCase()}
              </span>
            )}
          </div>
        )}

        {/* Question */}
        <div style={{ marginBottom: "1.2rem" }}>
          <p
            style={{
              fontWeight: 700,
              fontSize: "0.8rem",
              textTransform: "uppercase",
              color: "var(--color-1)",
              letterSpacing: "0.08em",
              marginBottom: "6px",
            }}
          >
            Question
          </p>
          <div style={{ fontSize: "0.97rem", lineHeight: 1.7 }}>
            <ReactMarkdown
              children={selectedQuestion?.questionText || ""}
              remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
              rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
            />
          </div>
        </div>
        <hr
          style={{
            margin: "1rem 0",
            border: "none",
            borderTop: "1.5px solid #e2e8f0",
          }}
        />

        {/* Options (MCQ) */}
        {selectedQuestion &&
          Array.isArray(selectedQuestion.options) &&
          selectedQuestion.options.filter(Boolean).length > 0 && (
            <>
              <div style={{ marginBottom: "1.2rem" }}>
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    textTransform: "uppercase",
                    color: "var(--color-1)",
                    letterSpacing: "0.08em",
                    marginBottom: "8px",
                  }}
                >
                  Options
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px 24px",
                    background: "#f8fafc",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "12px 16px",
                  }}
                >
                  {selectedQuestion.options.filter(Boolean).map((opt) => (
                    <div
                      key={opt.key}
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "flex-start",
                        fontSize: "0.92rem",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          minWidth: "20px",
                          color: "var(--color-1)",
                        }}
                      >
                        {opt.key})
                      </span>
                      <div style={{ flex: 1 }}>
                        <ReactMarkdown
                          children={opt.text}
                          remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                          rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <hr
                style={{
                  margin: "1rem 0",
                  border: "none",
                  borderTop: "1.5px solid #e2e8f0",
                }}
              />
            </>
          )}

        {/* Correct Option */}
        {selectedQuestion &&
          Array.isArray(selectedQuestion.correctOptions) &&
          selectedQuestion.correctOptions.length > 0 && (
            <>
              <div style={{ marginBottom: "1.2rem" }}>
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    textTransform: "uppercase",
                    color: "var(--color-1)",
                    letterSpacing: "0.08em",
                    marginBottom: "8px",
                  }}
                >
                  Correct Option
                </p>
                <div
                  style={{
                    background: "#f0fdf4",
                    border: "1.5px solid #86efac",
                    borderRadius: "8px",
                    padding: "10px 16px",
                    fontSize: "0.92rem",
                    fontWeight: 600,
                    color: "#166534",
                  }}
                >
                  {selectedQuestion.correctOptions.join(", ")}
                </div>
              </div>
              <hr
                style={{
                  margin: "1rem 0",
                  border: "none",
                  borderTop: "1.5px solid #e2e8f0",
                }}
              />
            </>
          )}

        {/* Correct Answer (for numericals) */}
        {selectedQuestion &&
          (selectedQuestion.type?.toUpperCase() === "NUMERICAL" ||
            !selectedQuestion.options ||
            selectedQuestion.options.length === 0) &&
          selectedQuestion.correctAnswer !== undefined &&
          selectedQuestion.correctAnswer !== null &&
          String(selectedQuestion.correctAnswer).trim() !== "" && (
            <>
              <div style={{ marginBottom: "1.2rem" }}>
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    textTransform: "uppercase",
                    color: "var(--color-1)",
                    letterSpacing: "0.08em",
                    marginBottom: "8px",
                  }}
                >
                  Correct Answer
                </p>
                <div
                  style={{
                    background: "#f0fdf4",
                    border: "1.5px solid #86efac",
                    borderRadius: "8px",
                    padding: "10px 16px",
                    fontSize: "0.92rem",
                    fontWeight: 600,
                    color: "#166534",
                  }}
                >
                  {selectedQuestion.correctAnswer}
                </div>
              </div>
              <hr
                style={{
                  margin: "1rem 0",
                  border: "none",
                  borderTop: "1.5px solid #e2e8f0",
                }}
              />
            </>
          )}

        {/* Solution */}
        {selectedQuestion && selectedQuestion.solution && (
          <>
            <div style={{ marginBottom: "1.2rem" }}>
              <p
                style={{
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  textTransform: "uppercase",
                  color: "var(--color-1)",
                  letterSpacing: "0.08em",
                  marginBottom: "8px",
                }}
              >
                Solution
              </p>
              <div
                style={{
                  background: "#f8fafc",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  fontSize: "0.92rem",
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                }}
              >
                <ReactMarkdown
                  children={selectedQuestion.solution
                    .replace(/\\\(/g, "$")
                    .replace(/\\\)/g, "$")
                    .replace(/\\\[/g, "$$")
                    .replace(/\\\]/g, "$$")}
                  remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                  rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                />
              </div>
            </div>
            <hr
              style={{
                margin: "1rem 0",
                border: "none",
                borderTop: "1.5px solid #e2e8f0",
              }}
            />
          </>
        )}

        <div className="modal-actions">
          <button
            className="btn-submit"
            onClick={() => {
              const optionsText =
                Array.isArray(selectedQuestion?.options) &&
                  selectedQuestion.options.filter(Boolean).length > 0
                  ? "\n" +
                  selectedQuestion.options
                    .filter(Boolean)
                    .map((opt) => `${opt.key}. ${opt.text}`)
                    .join("\n")
                  : "";
              copyToClipboard(selectedQuestion?.questionText + optionsText);
            }}
            title="Copy question to clipboard"
          >
            <i className="fa-solid fa-copy" /> Copy Question
          </button>
        </div>
      </Modal>

      {/* ── Edit Question Modal ── */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          if (!isSavingEdit) {
            setIsEditModalOpen(false);
            setEditQuestion(null);
          }
        }}
        maxWidth="860px"
        zIndex={1200}
      >
        <h2 style={{ marginBottom: "1.2rem" }}>Edit Question</h2>
        <button
              type="submit"
              className="btn-submit"
              style={{margin: "-2rem", width: "fit-content", float: "right"}}
              disabled={isSavingEdit || !isFormChanged}
              onClick={handleSaveEditQuestion}
        >
          {isSavingEdit ? (
            <>
              <Loader style={{ left: 0, top: 0, fontSize: "18px" }} />{" "}
              Saving...
            </>
          ) : (
            <>
              <i
                className="fa-solid fa-floppy-disk"
                style={{ marginRight: "6px" }}
              />
              Save Changes
            </>
          )}
        </button>

        {/* Invalid Reason Warning Banner */}
        {editQuestion && isQuestionInvalid(editQuestion) && (
          <div
            style={{
              background: editQuestion.invalidReason !== "Generated Solution mismatch with given answer key"
                ? "rgba(255, 237, 213, 0.9)"
                : "rgba(254, 226, 226, 0.9)",
              border: editQuestion.invalidReason !== "Generated Solution mismatch with given answer key"
                ? "1.5px solid rgba(249, 115, 22, 0.4)"
                : "1.5px solid rgba(239, 68, 68, 0.35)",
              borderRadius: "10px",
              padding: "10px 14px",
              marginBottom: "1.2rem",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "0.88rem",
              color: editQuestion.invalidReason !== "Generated Solution mismatch with given answer key"
                ? "#7c2d12"
                : "#7f1d1d",
            }}
          >
            <i
              className="fa-solid fa-triangle-exclamation"
              style={{
                color: editQuestion.invalidReason !== "Generated Solution mismatch with given answer key"
                  ? "#f97316"
                  : "#ef4444",
                fontSize: "1rem",
                flexShrink: 0,
              }}
            />
            <div>
              <strong>Invalid Question — </strong>
              {editQuestion.invalidReason || "This question has been marked as invalid."}
            </div>
          </div>
        )}

        <form onSubmit={handleSaveEditQuestion} className="rubric-form">
          {/* Question Text */}
          <div className="form-group">
            <label style={{ fontWeight: 600 }}>
              <i
                className="fa-solid fa-question"
                style={{ marginRight: "6px", color: "var(--color-1)" }}
              />
              Question Text
            </label>
            <textarea
              ref={(el) => {
                if (el) {
                  el.style.height = "auto";
                  el.style.height = el.scrollHeight + 5 + "px";
                }
              }}
              value={editForm.questionText}
              onChange={(e) => {
                setEditForm((p) => ({ ...p, questionText: e.target.value }));
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + 5 + "px";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  handleSaveEditQuestion(e);
                }
              }}
              placeholder="Enter question text..."
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "2px solid var(--color-8, #e2e8f0)",
                fontSize: "0.95rem",
                resize: "vertical",
                fontFamily: "inherit",
                lineHeight: 1.6,
                minHeight: "120px",
                overflow: "hidden",
              }}
            />
            {editForm.questionText && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                }}
              >
                <div
                  style={{
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: "#64748b",
                    marginBottom: "6px",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Question Preview
                </div>
                <div className="math-text">
                  <LazyMarkdown>{formatLatexText(editForm.questionText)}</LazyMarkdown>
                </div>
              </div>
            )}
          </div>

          {/* Options – Editable view */}
          {editForm &&
            Array.isArray(editForm.options) &&
            editForm.options.length > 0 && (
              <div className="form-group">
                <label style={{ fontWeight: 600 }}>
                  <i
                    className="fa-solid fa-list-ul"
                    style={{ marginRight: "6px", color: "var(--color-1)" }}
                  />
                  Options
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    marginTop: "0.5rem",
                  }}
                >
                  {editForm.options.map((opt, index) => {
                    if (!opt) return null;
                    return (
                      <div
                        key={opt.key || index}
                        style={{
                          display: "flex",
                          gap: "10px",
                          alignItems: "flex-start",
                          fontSize: "0.9rem",
                          color: "#374151",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 700,
                            minWidth: "20px",
                            marginTop: "10px",
                          }}
                        >
                          {opt.key}.
                        </span>
                        <input
                          type="text"
                          value={opt.text || ""}
                          onChange={(e) => {
                            const newOptions = [...editForm.options];
                            newOptions[index] = {
                              ...newOptions[index],
                              text: e.target.value,
                            };
                            setEditForm((prev) => ({
                              ...prev,
                              options: newOptions,
                            }));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              handleSaveEditQuestion(e);
                            }
                          }}
                          placeholder={`Option ${opt.key} text...`}
                          style={{
                            width: "100%",
                            padding: "9px 12px",
                            borderRadius: "8px",
                            border: "2px solid var(--color-8, #e2e8f0)",
                            fontSize: "0.9rem",
                            fontFamily: "inherit",
                            background: "white",
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}



          {/* Row 1: Question Number | Question Type | Section */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontWeight: 600 }}>
                <i
                  className="fa-solid fa-hashtag"
                  style={{ marginRight: "6px", color: "var(--color-1)" }}
                />
                Question Number
              </label>
              <input
                type="text"
                value={editForm.questionNumber}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, questionNumber: e.target.value }))
                }
                placeholder="e.g. 1"
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: "8px",
                  border: "2px solid var(--color-8, #e2e8f0)",
                  fontSize: "0.9rem",
                }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontWeight: 600 }}>
                <i
                  className="fa-solid fa-tag"
                  style={{ marginRight: "6px", color: "var(--color-1)" }}
                />
                Question Type
              </label>
              <Select
                options={questionTypeOptions}
                value={
                  questionTypeOptions.find(
                    (opt) => opt.value === editForm.type
                  ) || null
                }
                onChange={(option) =>
                  setEditForm((p) => ({ ...p, type: option ? option.value : "" }))
                }
                placeholder="Select Question Type"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "8px",
                    border: "2px solid var(--color-8, #e2e8f0)",
                    fontSize: "0.9rem",
                    padding: "1px 0",
                  }),
                }}
                isClearable
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontWeight: 600 }}>
                <i
                  className="fa-solid fa-layer-group"
                  style={{ marginRight: "6px", color: "var(--color-1)" }}
                />
                Section
              </label>
              <input
                type="text"
                value={editForm.sectionNumber}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, sectionNumber: e.target.value }))
                }
                placeholder="e.g. SECTION A"
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: "8px",
                  border: "2px solid var(--color-8, #e2e8f0)",
                  fontSize: "0.9rem",
                }}
              />
            </div>
          </div>

          {/* Row 2: Subject | Difficulty */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontWeight: 600 }}>
                <i
                  className="fa-solid fa-atom"
                  style={{ marginRight: "6px", color: "var(--color-1)" }}
                />
                Subject
              </label>
              <input
                type="text"
                value={editForm.subject}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, subject: e.target.value }))
                }
                placeholder="e.g. Physics"
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: "8px",
                  border: "2px solid var(--color-8, #e2e8f0)",
                  fontSize: "0.9rem",
                }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontWeight: 600 }}>
                <i
                  className="fa-solid fa-gauge"
                  style={{ marginRight: "6px", color: "var(--color-1)" }}
                />
                Difficulty
              </label>
              <select
                value={editForm.difficulty}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, difficulty: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: "8px",
                  border: "2px solid var(--color-8, #e2e8f0)",
                  fontSize: "0.9rem",
                  background: "white",
                }}
              >
                <option value="">-- Select Difficulty --</option>
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
            </div>
          </div>

          {/* Row 3: Topic | Subtopic */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontWeight: 600 }}>
                <i
                  className="fa-solid fa-tag"
                  style={{ marginRight: "6px", color: "var(--color-1)" }}
                />
                Topic
              </label>
              <input
                type="text"
                value={editForm.topic}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, topic: e.target.value }))
                }
                placeholder="e.g. Linear Motion"
                required
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: "8px",
                  border: "2px solid var(--color-8, #e2e8f0)",
                  fontSize: "0.9rem",
                }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontWeight: 600 }}>
                <i
                  className="fa-solid fa-bookmark"
                  style={{ marginRight: "6px", color: "var(--color-1)" }}
                />
                Subtopic
              </label>
              <input
                type="text"
                value={editForm.subTopic}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, subTopic: e.target.value }))
                }
                placeholder="e.g. Dimensions"
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: "8px",
                  border: "2px solid var(--color-8, #e2e8f0)",
                  fontSize: "0.9rem",
                }}
              />
            </div>
          </div>

          {/* Row 4: Correct Option | Answer Key Correct Option — MCQ only */}
          {editQuestion &&
            Array.isArray(editQuestion.options) &&
            editQuestion.options.filter(Boolean).length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "1rem",
                  marginTop: "1rem",
                }}
              >
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 600 }}>
                    <i
                      className="fa-solid fa-check-circle"
                      style={{ marginRight: "6px", color: "var(--color-1)" }}
                    />
                    Correct Option
                  </label>
                  <select
                    value={editForm.correctOptions || ""}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        correctOptions: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: "8px",
                      border: "2px solid var(--color-8, #e2e8f0)",
                      fontSize: "0.9rem",
                      background: "white",
                    }}
                  >
                    <option value="">-- Select Correct Option --</option>
                    {editQuestion.options.filter(Boolean).map((opt) => (
                      <option key={opt.key} value={opt.key}>
                        {opt.key} — {opt.text}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 600 }}>
                    <i
                      className="fa-solid fa-key"
                      style={{ marginRight: "6px", color: "#3b82f6" }}
                    />
                    Answer Key Correct Option
                  </label>
                  <select
                    value={editForm.answerKeyCorrectOptions || ""}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        answerKeyCorrectOptions: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: "8px",
                      border: "2px solid #bfdbfe",
                      fontSize: "0.9rem",
                      background: "white",
                    }}
                  >
                    <option value="">-- Select Answer Key Option --</option>
                    {editQuestion.options.filter(Boolean).map((opt) => (
                      <option key={opt.key} value={opt.key}>
                        {opt.key} — {opt.text}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

          {/* Row 5: Correct Answer | Answer Key Correct Answer — Numerical only */}
          {editQuestion &&
            (editQuestion.type || "").toLowerCase() === "numerical" && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "1rem",
                  marginTop: "1rem",
                }}
              >
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 600 }}>
                    <i
                      className="fa-solid fa-circle-check"
                      style={{ marginRight: "6px", color: "var(--color-1)" }}
                    />
                    Correct Answer
                  </label>
                  <input
                    type="text"
                    value={editForm.correctAnswer}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        correctAnswer: e.target.value,
                      }))
                    }
                    placeholder="e.g. 12 or 5.5"
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: "8px",
                      border: "2px solid var(--color-8, #e2e8f0)",
                      fontSize: "0.9rem",
                    }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 600 }}>
                    <i
                      className="fa-solid fa-key"
                      style={{ marginRight: "6px", color: "#3b82f6" }}
                    />
                    Answer Key Correct Answer
                  </label>
                  <input
                    type="text"
                    value={editForm.answerKeyCorrectAnswer}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        answerKeyCorrectAnswer: e.target.value,
                      }))
                    }
                    placeholder="e.g. 12 or 5.5"
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: "8px",
                      border: "2px solid #bfdbfe",
                      fontSize: "0.9rem",
                    }}
                  />
                </div>
              </div>
            )}

          {/* Row 6: Is Past Exam Question */}
          <div className="form-group" style={{ marginTop: "1rem" }}>
            <label
              style={{
                fontWeight: 600,
                display: "block",
                marginBottom: "0.5rem",
              }}
            >
              <i
                className="fa-solid fa-clock-rotate-left"
                style={{ marginRight: "6px", color: "var(--color-1)" }}
              />
              Is Past Exam Question?
            </label>
            <label className="solution-toggle">
              <input
                type="checkbox"
                name="editIsPastExam"
                checked={!!editForm.isPastExam}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, isPastExam: e.target.checked }))
                }
                disabled={isSavingEdit}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editForm.isPastExam ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* Row 7: Is Invalid Toggle & Reason */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: editForm.isInvalid ? "1fr 1fr" : "1fr",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label
                style={{
                  fontWeight: 600,
                  display: "block",
                  marginBottom: "0.5rem",
                }}
              >
                <i
                  className="fa-solid fa-triangle-exclamation"
                  style={{ marginRight: "6px", color: "#ef4444" }}
                />
                Is Invalid Question?
              </label>
              <label className="solution-toggle">
                <input
                  type="checkbox"
                  name="editIsInvalid"
                  checked={!!editForm.isInvalid}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setEditForm((p) => ({
                      ...p,
                      isInvalid: checked,
                      invalidReason: checked ? p.invalidReason : "",
                    }));
                  }}
                  disabled={isSavingEdit}
                />
                <span className="toggle-track" style={{ background: editForm.isInvalid ? "#ef4444" : "#e2e8f0" }}>
                  <span className="toggle-indicator"></span>
                </span>
                <span className="toggle-label-text">
                  {editForm.isInvalid ? "Yes" : "No"}
                </span>
              </label>
            </div>
            {editForm.isInvalid && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontWeight: 600, display: "block", marginBottom: "6px" }}>
                  <i
                    className="fa-solid fa-pen-fancy"
                    style={{ marginRight: "6px", color: "var(--color-1)" }}
                  />
                  Invalid Reason
                </label>
                <CreatableSelect
                  classNamePrefix="react-select"
                  options={INVALID_REASON_OPTIONS}
                  value={
                    editForm.invalidReason
                      ? { value: editForm.invalidReason, label: editForm.invalidReason }
                      : null
                  }
                  onChange={(sel) =>
                    setEditForm((prev) => ({ ...prev, invalidReason: sel ? sel.value : "" }))
                  }
                  onCreateOption={(inputValue) => {
                    setEditForm((prev) => ({ ...prev, invalidReason: inputValue.trim() }));
                  }}
                  placeholder="Select or type a new reason..."
                  isClearable
                  formatCreateLabel={(inputValue) => `Create new issue "${inputValue}"`}
                />
                <span
                  style={{
                    display: "block",
                    fontSize: "0.78rem",
                    color: "#64748b",
                    marginTop: "6px",
                  }}
                >
                  Select issue from dropdown or type your issue and click on "create new" in dropdown
                </span>
                {editForm.invalidReason === "Others" && (
                  <div style={{ marginTop: "10px" }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter other reason..."
                      value={editForm.otherInvalidReason || ""}
                      onChange={(e) => setEditForm(prev => ({ ...prev, otherInvalidReason: e.target.value }))}
                    />
                  </div>
                )}
              </div>
            )}
          </div>


          {/* Solution */}
          <div className="form-group" style={{ marginTop: "1rem" }}>
            <label style={{ fontWeight: 600 }}>
              <i
                className="fa-solid fa-lightbulb"
                style={{ marginRight: "6px", color: "var(--color-1)" }}
              />
              Solution
            </label>
            <textarea
              ref={(el) => {
                if (el) {
                  el.style.height = "auto";
                  el.style.height = el.scrollHeight + 5 + "px";
                }
              }}
              value={editForm.solution}
              onChange={(e) => {
                setEditForm((p) => ({ ...p, solution: e.target.value }));
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + 5 + "px";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  handleSaveEditQuestion(e);
                }
              }}
              placeholder="Enter solution (optional)..."
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "2px solid var(--color-8, #e2e8f0)",
                fontSize: "0.95rem",
                resize: "vertical",
                fontFamily: "inherit",
                lineHeight: 1.6,
                minHeight: "120px",
                overflow: "hidden",
              }}
            />
            {editForm.solution && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                }}
              >
                <div
                  style={{
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: "#64748b",
                    marginBottom: "6px",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Solution Preview
                </div>
                <div className="math-text">
                  <LazyMarkdown>{formatLatexText(editForm.solution)}</LazyMarkdown>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="modal-actions" style={{ marginTop: "1.8rem" }}>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => {
                if (!isSavingEdit) {
                  setIsEditModalOpen(false);
                  setEditQuestion(null);
                }
              }}
              disabled={isSavingEdit}
              style={{ marginRight: "10px" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSavingEdit || !isFormChanged}
            >
              {isSavingEdit ? (
                <>
                  <Loader style={{ left: 0, top: 0, fontSize: "18px" }} />{" "}
                  Saving...
                </>
              ) : (
                <>
                  <i
                    className="fa-solid fa-floppy-disk"
                    style={{ marginRight: "6px" }}
                  />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Invalidity Toggle Modal ── */}
      <Modal
        isOpen={isInvalidToggleModalOpen}
        onClose={() => {
          if (!isSavingInvalidToggle) {
            setIsInvalidToggleModalOpen(false);
            setInvalidToggleQuestion(null);
          }
        }}
        maxWidth="500px"
        zIndex={1300}
      >
        <h2 style={{ marginBottom: "1.2rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <i
            className="fa-solid fa-triangle-exclamation"
            style={{ color: "#ef4444" }}
          />
          Question Invalid Status
        </h2>
        <form onSubmit={handleSaveInvalidToggle} className="rubric-form">
          {/* Toggle */}
          <div className="form-group" style={{ marginBottom: "1.2rem" }}>
            <label
              style={{
                fontWeight: 600,
                display: "block",
                marginBottom: "0.5rem",
              }}
            >
              Is Invalid Question?
            </label>
            <label className="solution-toggle">
              <input
                type="checkbox"
                name="isInvalidToggle"
                checked={!!invalidToggleValue}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setInvalidToggleValue(checked);
                  if (!checked) {
                    setInvalidReasonValue("");
                  }
                }}
                disabled={isSavingInvalidToggle}
              />
              <span className="toggle-track" style={{ background: invalidToggleValue ? "#ef4444" : "#e2e8f0" }}>
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {invalidToggleValue ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* Text Area for Reason */}
          {invalidToggleValue && (
            <div className="form-group" style={{ marginBottom: "1.2rem" }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: "6px" }}>
                <i
                  className="fa-solid fa-pen-fancy"
                  style={{ marginRight: "6px", color: "var(--color-1)" }}
                />
                Invalid Reason
              </label>
              <CreatableSelect
                classNamePrefix="react-select"
                options={INVALID_REASON_OPTIONS}
                value={
                  invalidReasonValue
                    ? { value: invalidReasonValue, label: invalidReasonValue }
                    : null
                }
                onChange={(sel) => setInvalidReasonValue(sel ? sel.value : "")}
                onCreateOption={(inputValue) => setInvalidReasonValue(inputValue.trim())}
                placeholder="Select or type a new reason..."
                isClearable
                formatCreateLabel={(inputValue) => `Create new issue "${inputValue}"`}
              />
              <span
                style={{
                  display: "block",
                  fontSize: "0.78rem",
                  color: "#64748b",
                  marginTop: "6px",
                }}
              >
                Select issue from dropdown or type your issue and click on "create new" in dropdown
              </span>
              {invalidReasonValue === "Others" && (
                <div style={{ marginTop: "10px" }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter other reason..."
                    value={otherInvalidReasonValue || ""}
                    onChange={(e) => setOtherInvalidReasonValue(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          {(() => {
            const finalReason = invalidReasonValue === "Others" ? otherInvalidReasonValue : invalidReasonValue;
            const isInvalidFormChanged =
              invalidToggleQuestion &&
              (invalidToggleValue !== (invalidToggleQuestion.isInvalid || false) ||
                finalReason !== (invalidToggleQuestion.invalidReason || ""));
            return (
              <div className="modal-actions" style={{ marginTop: "1.8rem", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    if (!isSavingInvalidToggle) {
                      setIsInvalidToggleModalOpen(false);
                      setInvalidToggleQuestion(null);
                    }
                  }}
                  disabled={isSavingInvalidToggle}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isSavingInvalidToggle || !isInvalidFormChanged}
                >
                  {isSavingInvalidToggle ? (
                    <>Saving...</>
                  ) : (
                    <>
                      <i
                        className="fa-solid fa-floppy-disk"
                        style={{ marginRight: "6px" }}
                      />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            );
          })()}
        </form>
      </Modal>


      {/* ── Edit Library Modal ── */}
      <Modal
        isOpen={isEditLibraryModalOpen}
        onClose={() => {
          if (!isSavingLibraryEdit) {
            setIsEditLibraryModalOpen(false);
            setEditLibrary(null);
          }
        }}
        maxWidth="680px"
      >
        <h2 style={{ marginBottom: "1.2rem" }}>Edit Library</h2>
        <form onSubmit={handleSaveEditLibrary} className="rubric-form">
          {editLibrary && editLibrary.templateId ? (
            <>
              {/* Library Name */}
              <div className="form-group">
                <label style={{ fontWeight: 600 }}>
                  <i
                    className="fa-solid fa-book-open"
                    style={{ marginRight: "6px", color: "var(--color-1)" }}
                  />
                  Library Name
                </label>
                <input
                  type="text"
                  value={editLibraryForm.libraryName}
                  onChange={(e) =>
                    setEditLibraryForm((p) => ({
                      ...p,
                      libraryName: e.target.value,
                    }))
                  }
                  placeholder="e.g. JEE Main PCM"
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    borderRadius: "8px",
                    border: "2px solid var(--color-8, #e2e8f0)",
                    fontSize: "0.95rem",
                  }}
                />
              </div>

              {/* Template Name */}
              <div className="form-group">
                <label style={{ fontWeight: 600 }}>
                  <i
                    className="fa-solid fa-file-invoice"
                    style={{ marginRight: "6px", color: "var(--color-1)" }}
                  />
                  Template Name
                </label>
                <input
                  type="text"
                  value={getTemplateName(editLibrary)}
                  disabled
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    borderRadius: "8px",
                    border: "2px solid var(--color-8, #e2e8f0)",
                    fontSize: "0.95rem",
                    background: "#f1f5f9",
                    cursor: "not-allowed",
                  }}
                />
              </div>

              {/* Year Dropdown (Optional for template library) */}
              <div className="form-group">
                <label style={{ fontWeight: 600 }}>
                  <i
                    className="fa-solid fa-calendar"
                    style={{ marginRight: "6px", color: "var(--color-1)" }}
                  />
                  Year{" "}
                  <span style={{ color: "#888", fontSize: "0.82rem" }}>
                    (Optional)
                  </span>
                </label>
                <Select
                  options={yearOptions}
                  value={
                    yearOptions.find(
                      (option) => option.value === editLibraryForm.year,
                    ) || null
                  }
                  onChange={(selectedOption) =>
                    setEditLibraryForm((p) => ({
                      ...p,
                      year: selectedOption ? selectedOption.value : "",
                    }))
                  }
                  placeholder="Select Year..."
                  isClearable={true}
                />
              </div>
            </>
          ) : (
            <>
              {/* Library Name */}
              <div className="form-group">
                <label style={{ fontWeight: 600 }}>
                  <i
                    className="fa-solid fa-book-open"
                    style={{ marginRight: "6px", color: "var(--color-1)" }}
                  />
                  Library Name
                </label>
                <input
                  type="text"
                  value={editLibraryForm.libraryName}
                  onChange={(e) =>
                    setEditLibraryForm((p) => ({
                      ...p,
                      libraryName: e.target.value,
                    }))
                  }
                  placeholder="e.g. Grade 10 - Biology"
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    borderRadius: "8px",
                    border: "2px solid var(--color-8, #e2e8f0)",
                    fontSize: "0.95rem",
                  }}
                />
              </div>

              {/* Year Dropdown (Optional for general library) */}
              <div className="form-group">
                <label style={{ fontWeight: 600 }}>
                  <i
                    className="fa-solid fa-calendar"
                    style={{ marginRight: "6px", color: "var(--color-1)" }}
                  />
                  Year{" "}
                  <span style={{ color: "#888", fontSize: "0.82rem" }}>
                    (Optional)
                  </span>
                </label>
                <Select
                  options={yearOptions}
                  value={
                    yearOptions.find(
                      (option) => option.value === editLibraryForm.year,
                    ) || null
                  }
                  onChange={(selectedOption) =>
                    setEditLibraryForm((p) => ({
                      ...p,
                      year: selectedOption ? selectedOption.value : "",
                    }))
                  }
                  placeholder="Select Year..."
                  isClearable={true}
                />
              </div>
            </>
          )}
          <div className="modal-actions" style={{ marginTop: "1rem" }}>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => {
                if (!isSavingLibraryEdit) {
                  setIsEditLibraryModalOpen(false);
                  setEditLibrary(null);
                }
              }}
              disabled={isSavingLibraryEdit}
              style={{ marginRight: "10px" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSavingLibraryEdit || !isLibraryFormChanged}
            >
              {isSavingLibraryEdit ? "Saving..." : "Save Library"}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── PDF Extraction Form Modal ── */}
      <Modal
        isOpen={isExtractPdfOpen}
        onClose={() => {
          if (!isExtractingPdf) {
            setIsExtractPdfOpen(false);
            setExtractPdfForm({
              libraryName: "",
              standardizedTestId: "",
              subject: "",
              topic: "",
              grade: "",
              curriculum: "",
              numberOfQs: "",
              customInstructions: "",
              sourceTestPaper: null,
              answerKeysFile: null,
              year: "",
              librarySource: "",
              isPastExam: false,
            });
            setExtractedQuestions([]);
            setExtractedLibrary(null);
          }
        }}
        maxWidth={extractedQuestions.length > 0 ? "100%" : undefined}
      >
        {extractedQuestions.length === 0 ? (
          <div className="oeb-generate-modal">
            <div className="oeb-generate-modal-header">
              <div
                className="oeb-generate-modal-icon"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(220,38,38,0.1), rgba(249,115,22,0.2))",
                  color: "#dc2626",
                }}
              >
                <i className="fa-solid fa-file-pdf" />
              </div>
              <div>
                <h2>
                  {libraryCreationMode === "template"
                    ? "Create Template Library"
                    : "Convert Test Paper to General Library"}
                </h2>
                <p className="modal-description">
                  {libraryCreationMode === "template"
                    ? "Upload a test paper and select a Standardized Test to create a template library."
                    : "Upload a test paper and we'll extract questions automatically to a general library."}
                </p>
              </div>
            </div>

            {/* Standardized Test (only for Template Library creation) */}
            {libraryCreationMode === "template" && (
              <>
                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Standardized Test{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <Select
                    options={standardizedTestOptions}
                    value={
                      standardizedTestOptions.find(
                        (option) =>
                          option.value === extractPdfForm.standardizedTestId,
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      const val = selectedOption ? selectedOption.value : "";
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        standardizedTestId: val,
                        numberOfQs:
                          val && !prev.numberOfQs ? "" : prev.numberOfQs,
                      }));
                    }}
                    placeholder="Select Standardized Test..."
                    isClearable={false}
                    isDisabled={isExtractingPdf}
                  />
                </div>

                {/* Library Name */}
                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Library Name <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., JEE Advanced Template Library"
                    value={extractPdfForm.libraryName}
                    onChange={(e) =>
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        libraryName: e.target.value,
                      }))
                    }
                    disabled={isExtractingPdf}
                  />
                </div>

                {/* Year Dropdown (Optional for template library) */}
                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Year{" "}
                    <span style={{ color: "#888", fontSize: "0.82rem" }}>
                      (Optional)
                    </span>
                  </label>
                  <Select
                    options={yearOptions}
                    value={
                      yearOptions.find(
                        (option) => option.value === extractPdfForm.year,
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        year: selectedOption ? selectedOption.value : "",
                      }))
                    }
                    placeholder="Select Year..."
                    isClearable={true}
                    isDisabled={isExtractingPdf}
                  />
                </div>

                {/* Is Past Exam Toggle */}
                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label
                    className="form-label"
                    style={{ marginBottom: "0.5rem", display: "block" }}
                  >
                    Is Past Exam Paper?
                  </label>
                  <label className="solution-toggle">
                    <input
                      type="checkbox"
                      name="templateIsPastExam"
                      checked={!!extractPdfForm.isPastExam}
                      onChange={(e) =>
                        setExtractPdfForm((prev) => ({
                          ...prev,
                          isPastExam: e.target.checked,
                        }))
                      }
                      disabled={isExtractingPdf}
                    />
                    <span className="toggle-track">
                      <span className="toggle-indicator"></span>
                    </span>
                    <span className="toggle-label-text">
                      {extractPdfForm.isPastExam ? "Yes" : "No"}
                    </span>
                  </label>
                </div>
              </>
            )}

            {libraryCreationMode === "general" && (
              <>
                {/* Curriculum */}
                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Curriculum <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <Select
                    options={curriculumOptions}
                    value={
                      curriculumOptions.find(
                        (option) => option.value === extractPdfForm.curriculum,
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        curriculum: selectedOption ? selectedOption.value : "",
                      }))
                    }
                    placeholder="Select curriculum..."
                    isDisabled={isExtractingPdf}
                  />
                </div>

                {/* Grade */}
                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Grade <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <Select
                    options={gradeOptions}
                    value={
                      gradeOptions.find(
                        (option) => option.value === extractPdfForm.grade,
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      const val = selectedOption ? selectedOption.value : "";
                      setExtractPdfForm((prev) => {
                        const grade = val;
                        const subject = prev.subject;
                        const libraryName =
                          grade && subject
                            ? `${grade} - ${subject}`
                            : grade || subject || "";
                        return {
                          ...prev,
                          grade,
                          libraryName,
                        };
                      });
                    }}
                    placeholder="Select grade..."
                    isDisabled={isExtractingPdf}
                  />
                </div>

                {/* Subject */}
                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Subject{" "}
                    <span style={{ color: "#888", fontSize: "0.82rem" }}>
                      (Optional)
                    </span>
                  </label>
                  <Select
                    options={subjectOptions}
                    value={
                      subjectOptions.find(
                        (option) => option.value === extractPdfForm.subject,
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      const val = selectedOption ? selectedOption.value : "";
                      setExtractPdfForm((prev) => {
                        const subject = val;
                        const grade = prev.grade;
                        const libraryName =
                          grade && subject
                            ? `${grade} - ${subject}`
                            : grade || subject || "";
                        return {
                          ...prev,
                          subject,
                          grade,
                          libraryName,
                        };
                      });
                    }}
                    placeholder="Select subject..."
                    isDisabled={isExtractingPdf}
                  />
                </div>

                {/* Topic */}
                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Topic{" "}
                    <span style={{ color: "#888", fontSize: "0.82rem" }}>
                      (Optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Quadratic Equations"
                    value={extractPdfForm.topic}
                    onChange={(e) =>
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        topic: e.target.value,
                      }))
                    }
                    disabled={isExtractingPdf}
                  />
                </div>

                {/* Library Name */}
                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Library Name <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Grade 10 - Biology"
                    value={extractPdfForm.libraryName}
                    onChange={(e) =>
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        libraryName: e.target.value,
                      }))
                    }
                    disabled={isExtractingPdf}
                  />
                </div>

                {/* Year Dropdown (Optional for general library) */}
                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Year{" "}
                    <span style={{ color: "#888", fontSize: "0.82rem" }}>
                      (Optional)
                    </span>
                  </label>
                  <Select
                    options={yearOptions}
                    value={
                      yearOptions.find(
                        (option) => option.value === extractPdfForm.year,
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        year: selectedOption ? selectedOption.value : "",
                      }))
                    }
                    placeholder="Select Year..."
                    isClearable={true}
                    isDisabled={isExtractingPdf}
                  />
                </div>

                {/* Is Past Exam Toggle */}
                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label
                    className="form-label"
                    style={{ marginBottom: "0.5rem", display: "block" }}
                  >
                    Is Past Exam Paper?
                  </label>
                  <label className="solution-toggle">
                    <input
                      type="checkbox"
                      name="generalIsPastExam"
                      checked={!!extractPdfForm.isPastExam}
                      onChange={(e) =>
                        setExtractPdfForm((prev) => ({
                          ...prev,
                          isPastExam: e.target.checked,
                        }))
                      }
                      disabled={isExtractingPdf}
                    />
                    <span className="toggle-track">
                      <span className="toggle-indicator"></span>
                    </span>
                    <span className="toggle-label-text">
                      {extractPdfForm.isPastExam ? "Yes" : "No"}
                    </span>
                  </label>
                </div>
              </>
            )}

            {/* Number of Questions */}
            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
              <label className="form-label">
                Number of Questions <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="number"
                className="form-input"
                min="1"
                placeholder="Enter number of questions to extract"
                value={extractPdfForm.numberOfQs}
                onChange={(e) =>
                  setExtractPdfForm((prev) => ({
                    ...prev,
                    numberOfQs: e.target.value,
                  }))
                }
                disabled={isExtractingPdf}
              />
            </div>

            {/* Test Paper PDF */}
            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
              <label className="form-label">
                Test Paper PDF <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <FileUploadSection
                currentFileUrl={null}
                currentFileName={
                  extractPdfForm.sourceTestPaper
                    ? extractPdfForm.sourceTestPaper.name
                    : null
                }
                isSubmitting={isExtractingPdf}
                accept=".pdf"
                showCapture={false}
                onFileChange={(file) =>
                  setExtractPdfForm((prev) => {
                    const nameWithoutExt = file
                      ? file.name.substring(0, file.name.lastIndexOf(".")) ||
                      file.name
                      : "";
                    return {
                      ...prev,
                      sourceTestPaper: file,
                      libraryName: nameWithoutExt || prev.libraryName,
                    };
                  })
                }
                onRemoveSelectedFile={() =>
                  setExtractPdfForm((prev) => ({
                    ...prev,
                    sourceTestPaper: null,
                  }))
                }
              />
            </div>

            {/* Answer Key PDF */}
            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
              <label className="form-label">Answer Key PDF (Optional)</label>
              <FileUploadSection
                currentFileUrl={null}
                currentFileName={
                  extractPdfForm.answerKeysFile
                    ? extractPdfForm.answerKeysFile.name
                    : null
                }
                isSubmitting={isExtractingPdf}
                accept=".pdf"
                showCapture={false}
                onFileChange={(file) =>
                  setExtractPdfForm((prev) => ({
                    ...prev,
                    answerKeysFile: file,
                  }))
                }
                onRemoveSelectedFile={() =>
                  setExtractPdfForm((prev) => ({
                    ...prev,
                    answerKeysFile: null,
                  }))
                }
              />
            </div>

            {/* Custom Instructions */}
            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
              <label className="form-label">
                Custom Instructions (Optional)
              </label>
              <textarea
                className="form-input"
                style={{ width: "100%", height: "80px", resize: "vertical" }}
                placeholder="e.g., Focus only on algebra questions"
                value={extractPdfForm.customInstructions}
                onChange={(e) =>
                  setExtractPdfForm((prev) => ({
                    ...prev,
                    customInstructions: e.target.value,
                  }))
                }
                disabled={isExtractingPdf}
              />
            </div>

            {/* Progress */}
            {isExtractingPdf && (
              <div
                className="extract-pdf-progress"
                style={{ margin: "20px 0" }}
              >
                <div className="extract-pdf-progress-visual">
                  <div
                    className="extract-pdf-progress-icon"
                    style={{
                      background:
                        "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
                    }}
                  >
                    <i
                      className="fa-solid fa-gears fa-spin"
                      style={{ color: "white" }}
                    ></i>
                  </div>
                  <div className="extract-pdf-progress-bar-container">
                    <div className="oeb-generate-progress-bar">
                      <div
                        className="oeb-generate-progress-fill"
                        style={{
                          background:
                            "linear-gradient(90deg, #14b8a6, #0f766e)",
                        }}
                      ></div>
                    </div>
                    <span style={{ fontWeight: "600", color: "#0f766e" }}>
                      {extractPdfProgress}
                    </span>
                  </div>
                </div>
                <p className="extract-pdf-progress-hint">
                  <i className="fa-solid fa-info-circle"></i>
                  Please don&apos;t close this window. AI is reading your test
                  paper and converting questions.
                </p>
              </div>
            )}

            {/* Actions */}
            <div
              className="modal-actions"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "20px",
              }}
            >
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setIsExtractPdfOpen(false)}
                disabled={isExtractingPdf}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={handleExtractFromPdf}
                disabled={isSubmitDisabled()}
              >
                {isExtractingPdf ? "Extracting..." : "Extract Questions"}
              </button>
            </div>
          </div>
        ) : (
          <div className="online-exam-builder" style={{ marginTop: "0.5rem" }}>
            <div className="oeb-header" style={{ marginBottom: "1rem" }}>
              <h3
                className="oeb-title"
                style={{ fontSize: "1.3rem", fontWeight: 700 }}
              >
                <i
                  className="fa-solid fa-clipboard-question"
                  style={{ marginRight: "8px", color: "var(--color-1)" }}
                ></i>
                Extracted Questions
                <span
                  className="oeb-count"
                  style={{
                    marginLeft: "8px",
                    fontSize: "0.95rem",
                    color: "#64748b",
                  }}
                >
                  ({extractedQuestions.length} question
                  {extractedQuestions.length !== 1 ? "s" : ""})
                </span>
              </h3>
              <p
                style={{
                  color: "#64748b",
                  fontSize: "0.88rem",
                  marginTop: "4px",
                }}
              >
                Review and modify the extracted questions. Correct answer
                changes are saved automatically.
              </p>
            </div>

            <div style={{ paddingRight: "4px", marginBottom: "1rem" }}>
              {extractedQuestions.map((q, idx) => (
                <div
                  key={idx}
                  className="oe-question-card"
                  style={{
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    padding: "1rem 1.25rem",
                    marginBottom: "0.75rem",
                    transition: "all 0.2s ease",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: "1rem",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <span
                          style={{
                            background:
                              "linear-gradient(135deg, var(--color-1), var(--color-2))",
                            color: "#fff",
                            borderRadius: "50%",
                            width: "28px",
                            height: "28px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {idx + 1}
                        </span>
                        <div
                          style={{
                            fontSize: "0.9rem",
                            color: "#1e293b",
                            lineHeight: 1.5,
                            overflow: "hidden",
                          }}
                        >
                          <ReactMarkdown
                            children={formatLatexText(q.questionText)}
                            remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                            rehypePlugins={[
                              [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
                            ]}
                          />
                        </div>
                      </div>

                      {(q.diagramFileURL || q.svgCode) && (
                        <div
                          className="diagram-viewer-container"
                          style={{
                            marginTop: "0.5rem",
                            marginBottom: "0.75rem",
                            marginLeft: "2.25rem",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "0.25rem",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "1rem",
                              flexWrap: "wrap",
                            }}
                          >
                            <div
                              style={{
                                position: "relative",
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                overflow: "hidden",
                                backgroundColor: "#f8fafc",
                                maxWidth: "100%",
                                display: "flex",
                                justifyCenter: "center",
                                alignItems: "center",
                                boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
                                cursor: "pointer",
                                transition:
                                  "transform 0.2s ease, box-shadow 0.2s ease",
                              }}
                              onClick={() => {
                                if (!q.svgCode && q.diagramFileURL)
                                  window.open(q.diagramFileURL, "_blank");
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.02)";
                                e.currentTarget.style.boxShadow =
                                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                e.currentTarget.style.boxShadow =
                                  "0 1px 3px 0 rgba(0, 0, 0, 0.05)";
                              }}
                              title="Click to view full size image"
                            >
                              {q.svgCode ? (
                                <>
                                  <style>{`
                                .diagram-viewer-container svg {
                                  max-width: 100% !important;
                                  max-height: 220px !important;
                                  height: auto !important;
                                  width: auto !important;
                                }
                              `}</style>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: q.svgCode,
                                    }}
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      maxWidth: "100%",
                                      maxHeight: "220px",
                                    }}
                                  />
                                </>
                              ) : (
                                <img
                                  src={q.diagramFileURL}
                                  alt={`Diagram for Question ${idx + 1}`}
                                  style={{
                                    maxHeight: "220px",
                                    maxWidth: "100%",
                                    objectFit: "contain",
                                    display: "block",
                                  }}
                                />
                              )}
                            </div>
                            {(q.diagramPageFileURL ||
                              q.diagramFileURL ||
                              q.imageURL) && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDiagramQuestion({
                                      ...q,
                                      isEditMode: true,
                                    });
                                    setIsCropModalOpen(true);
                                  }}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    padding: "8px 14px",
                                    background: "var(--color-1)",
                                    color: "#ffffff",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontWeight: "600",
                                    fontSize: "0.85rem",
                                    boxShadow: "0 2px 4px rgba(79, 70, 229, 0.2)",
                                    transition: "all 0.2s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform =
                                      "translateY(-1px)";
                                    e.currentTarget.style.boxShadow =
                                      "0 4px 6px rgba(79, 70, 229, 0.3)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform =
                                      "translateY(0)";
                                    e.currentTarget.style.boxShadow =
                                      "0 2px 4px rgba(79, 70, 229, 0.2)";
                                  }}
                                >
                                  <i className="fa-solid fa-crop-simple"></i> Edit
                                  Diagram
                                </button>
                              )}
                          </div>
                        </div>
                      )}

                      {/* Options & Correct Answer block */}
                      {q.options && q.options.filter(Boolean).length > 0 ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.6rem",
                            marginLeft: "2.25rem",
                            marginBottom: "1rem",
                          }}
                        >
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "0.4rem 1rem",
                            }}
                          >
                            {q.options.filter(Boolean).map((opt, oi) => {
                              const optLabel =
                                opt && typeof opt === "object"
                                  ? opt.key
                                  : opt
                                    ? opt.charAt(0)
                                    : "";
                              const optText =
                                opt && typeof opt === "object"
                                  ? `${opt.key}) ${opt.text}`
                                  : opt || "";
                              const isCorrect =
                                q.correctOptions &&
                                q.correctOptions.includes(optLabel);
                              return (
                                <div
                                  key={oi}
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "0.4rem",
                                    fontSize: "0.85rem",
                                    color: isCorrect ? "#16a34a" : "#475569",
                                    fontWeight: isCorrect ? 600 : 400,
                                    background: isCorrect
                                      ? "#f0fdf4"
                                      : "transparent",
                                    padding: "4px 8px",
                                    borderRadius: "6px",
                                    border: isCorrect
                                      ? "1px solid #bbf7d0"
                                      : "1px solid transparent",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <span
                                    style={{
                                      lineHeight: 1.4,
                                      width: "100%",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <ReactMarkdown
                                      children={formatLatexText(optText)}
                                      remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                      rehypePlugins={[
                                        [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
                                      ]}
                                      components={{ p: "span" }}
                                    />
                                  </span>
                                </div>
                              );
                            })}
                          </div>

                          <div style={{ marginTop: "0.5rem" }}>
                            <label
                              style={{
                                fontSize: "0.78rem",
                                color: "#64748b",
                                fontWeight: 500,
                                marginBottom: "0.4rem",
                                display: "block",
                              }}
                            >
                              Correct Answer:
                              {(!q.correctOptions ||
                                q.correctOptions.length === 0) && (
                                  <span
                                    style={{
                                      color: "#ef4444",
                                      fontStyle: "italic",
                                      marginLeft: "0.5rem",
                                    }}
                                  >
                                    *Required
                                  </span>
                                )}
                            </label>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: "1rem",
                              }}
                            >
                              <div style={{ display: "flex", gap: "0.5rem" }}>
                                {["A", "B", "C", "D"].map((optLabel) => {
                                  const isCorrect =
                                    q.correctOptions &&
                                    q.correctOptions.includes(optLabel);
                                  return (
                                    <button
                                      key={optLabel}
                                      type="button"
                                      onClick={() =>
                                        handleReviewQuestionChange(
                                          idx,
                                          "correctOptions",
                                          [optLabel],
                                        )
                                      }
                                      style={{
                                        padding: "6px 14px",
                                        borderRadius: "20px",
                                        border: isCorrect
                                          ? "1px solid #16a34a"
                                          : "1px solid #cbd5e1",
                                        background: isCorrect
                                          ? "#16a34a"
                                          : "#fff",
                                        color: isCorrect ? "#fff" : "#475569",
                                        fontSize: "0.85rem",
                                        fontWeight: 600,
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.3rem",
                                      }}
                                    >
                                      {optLabel}
                                      {isCorrect && (
                                        <i
                                          className="fa-solid fa-check"
                                          style={{ fontSize: "0.7rem" }}
                                        />
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.6rem",
                            marginLeft: "2.25rem",
                            marginBottom: "1rem",
                          }}
                        >
                          <div style={{ marginTop: "0.5rem" }}>
                            <label
                              style={{
                                fontSize: "0.78rem",
                                color: "#64748b",
                                fontWeight: 500,
                                marginBottom: "0.4rem",
                                display: "block",
                              }}
                            >
                              Correct Answer:
                              {(!q.correctAnswer ||
                                String(q.correctAnswer).trim() === "") && (
                                  <span
                                    style={{
                                      color: "#ef4444",
                                      fontStyle: "italic",
                                      marginLeft: "0.5rem",
                                    }}
                                  >
                                    *Required
                                  </span>
                                )}
                            </label>
                            <input
                              type="text"
                              value={q.correctAnswer || ""}
                              onChange={(e) =>
                                handleReviewQuestionChange(
                                  idx,
                                  "correctAnswer",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter correct numerical answer..."
                              style={{
                                width: "240px",
                                padding: "6px 12px",
                                borderRadius: "8px",
                                border: "1px solid #cbd5e1",
                                fontSize: "0.85rem",
                                outline: "none",
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Question metadata tags: type, subject, topic, subtopic, difficulty, source */}
                      {(q.type ||
                        q.subject ||
                        q.topic ||
                        q.subTopic ||
                        q.difficulty ||
                        q.source) && (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "0.4rem",
                              alignItems: "center",
                              marginLeft: "2.25rem",
                              marginBottom: "1rem",
                            }}
                          >
                            {q.type && (
                              <span
                                style={{
                                  background: "#f1f5f9",
                                  color: "#475569",
                                  border: "1px solid #cbd5e1",
                                  borderRadius: "16px",
                                  padding: "2px 8px",
                                  fontSize: "0.72rem",
                                  fontWeight: "600",
                                }}
                              >
                                {q.type.toUpperCase()}
                              </span>
                            )}
                            {q.subject && (
                              <span
                                style={{
                                  background: "#eff6ff",
                                  color: "#2563eb",
                                  border: "1px solid #bfdbfe",
                                  borderRadius: "16px",
                                  padding: "2px 8px",
                                  fontSize: "0.72rem",
                                  fontWeight: "600",
                                }}
                                title={q.subject}
                              >
                                {q.subject}
                              </span>
                            )}
                            {q.topic && (
                              <span
                                style={{
                                  background: "#faf5ff",
                                  color: "#7c3aed",
                                  border: "1px solid #e9d5ff",
                                  borderRadius: "16px",
                                  padding: "2px 8px",
                                  fontSize: "0.72rem",
                                  fontWeight: "600",
                                }}
                                title={q.topic}
                              >
                                {q.topic}
                              </span>
                            )}
                            {q.subTopic && (
                              <span
                                style={{
                                  background: "#ecfeff",
                                  color: "#0891b2",
                                  border: "1px solid #cffafe",
                                  borderRadius: "16px",
                                  padding: "2px 8px",
                                  fontSize: "0.72rem",
                                  fontWeight: "600",
                                }}
                                title={q.subTopic}
                              >
                                {q.subTopic}
                              </span>
                            )}
                            {q.difficulty &&
                              (() => {
                                const diff = q.difficulty.toLowerCase().trim();
                                const isEasy = diff === "easy";
                                const isHard = diff === "hard";
                                const bg = isEasy
                                  ? "#f0fdf4"
                                  : isHard
                                    ? "#fef2f2"
                                    : "#fff7ed";
                                const color = isEasy
                                  ? "#16a34a"
                                  : isHard
                                    ? "#dc2626"
                                    : "#ea580c";
                                const border = isEasy
                                  ? "#bbf7d0"
                                  : isHard
                                    ? "#fecaca"
                                    : "#ffedd5";
                                return (
                                  <span
                                    style={{
                                      background: bg,
                                      color: color,
                                      border: `1px solid ${border}`,
                                      borderRadius: "16px",
                                      padding: "2px 8px",
                                      fontSize: "0.72rem",
                                      fontWeight: "600",
                                    }}
                                    title={q.difficulty}
                                  >
                                    {q.difficulty.charAt(0).toUpperCase() +
                                      q.difficulty.slice(1)}
                                  </span>
                                );
                              })()}
                            {q.source && (
                              <span
                                style={{
                                  background: "#fdf2f8",
                                  color: "#db2777",
                                  border: "1px solid #fbcfe8",
                                  borderRadius: "16px",
                                  padding: "2px 8px",
                                  fontSize: "0.72rem",
                                  fontWeight: "600",
                                }}
                                title={q.source}
                              >
                                {q.source}
                              </span>
                            )}
                          </div>
                        )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleReviewQuestionRemove(idx)}
                      className="modal-table-remove-btn"
                      style={{ flexShrink: 0, width: "32px", height: "32px" }}
                    >
                      <i
                        className="fa-solid fa-trash"
                        style={{ fontSize: "0.75rem" }}
                      ></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div
              className="modal-actions"
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "flex-end",
                marginTop: "1.5rem",
                borderTop: "1px solid #e2e8f0",
                paddingTop: "1rem",
              }}
            >
              <button
                type="button"
                className="btn-submit"
                onClick={handleFinishReview}
              >
                <i className="fa-solid fa-check"></i> Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Generate Similar Form Modal ── */}
      <Modal
        isOpen={isGenerateSimilarOpen}
        onClose={() => {
          if (!isGeneratingSimilar) {
            setIsGenerateSimilarOpen(false);
          }
        }}
      >
        <div className="oeb-generate-modal">
          <div className="oeb-generate-modal-header">
            <div
              className="oeb-generate-modal-icon"
              style={{
                background:
                  "linear-gradient(135deg, rgba(13,148,136,0.1), rgba(20,184,166,0.2))",
                color: "#0d9488",
              }}
            >
              <i className="fa-solid fa-wand-magic-sparkles" />
            </div>
            <div>
              <h2>Generate Similar Questions (General Library)</h2>
              <p className="modal-description">
                Upload a reference test paper and we&apos;ll generate brand-new
                inspired questions.
              </p>
            </div>
          </div>

          {/* Curriculum */}
          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label className="form-label">
              Curriculum <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <Select
              options={curriculumOptions}
              value={
                curriculumOptions.find(
                  (option) => option.value === generateSimilarForm.curriculum,
                ) || null
              }
              onChange={(selectedOption) =>
                setGenerateSimilarForm((prev) => ({
                  ...prev,
                  curriculum: selectedOption ? selectedOption.value : "",
                }))
              }
              placeholder="Select curriculum..."
              isDisabled={isGeneratingSimilar}
            />
          </div>

          {/* Grade */}
          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label className="form-label">
              Grade <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <Select
              options={gradeOptions}
              value={
                gradeOptions.find(
                  (option) => option.value === generateSimilarForm.grade,
                ) || null
              }
              onChange={(selectedOption) => {
                const val = selectedOption ? selectedOption.value : "";
                setGenerateSimilarForm((prev) => {
                  const grade = val;
                  const subject = prev.subject;
                  const libraryName =
                    grade && subject
                      ? `${grade} - ${subject}`
                      : grade || subject || "";
                  return {
                    ...prev,
                    grade,
                    libraryName,
                  };
                });
              }}
              placeholder="Select grade..."
              isDisabled={isGeneratingSimilar}
            />
          </div>

          {/* Subject */}
          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label className="form-label">
              Subject{" "}
              <span style={{ color: "#888", fontSize: "0.82rem" }}>
                (Optional)
              </span>
            </label>
            <Select
              options={subjectOptions}
              value={
                subjectOptions.find(
                  (option) => option.value === generateSimilarForm.subject,
                ) || null
              }
              onChange={(selectedOption) => {
                const val = selectedOption ? selectedOption.value : "";
                setGenerateSimilarForm((prev) => {
                  const subject = val;
                  const grade = prev.grade;
                  const libraryName =
                    grade && subject
                      ? `${grade} - ${subject}`
                      : grade || subject || "";
                  return {
                    ...prev,
                    subject,
                    libraryName,
                  };
                });
              }}
              placeholder="Select subject..."
              isDisabled={isGeneratingSimilar}
            />
          </div>

          {/* Topic */}
          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label className="form-label">
              Topic{" "}
              <span style={{ color: "#888", fontSize: "0.82rem" }}>
                (Optional)
              </span>
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., Quadratic Equations"
              value={generateSimilarForm.topic}
              onChange={(e) =>
                setGenerateSimilarForm((prev) => ({
                  ...prev,
                  topic: e.target.value,
                }))
              }
              disabled={isGeneratingSimilar}
            />
          </div>

          {/* Year Dropdown (Optional) */}
          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label className="form-label">
              Year{" "}
              <span style={{ color: "#888", fontSize: "0.82rem" }}>
                (Optional)
              </span>
            </label>
            <Select
              options={yearOptions}
              value={
                yearOptions.find(
                  (option) => option.value === generateSimilarForm.year,
                ) || null
              }
              onChange={(selectedOption) =>
                setGenerateSimilarForm((prev) => ({
                  ...prev,
                  year: selectedOption ? selectedOption.value : "",
                }))
              }
              placeholder="Select Year..."
              isClearable={true}
              isDisabled={isGeneratingSimilar}
            />
          </div>

          {/* Library Name */}
          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label className="form-label">
              Library Name <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., Algebra Similar Practice"
              value={generateSimilarForm.libraryName}
              onChange={(e) =>
                setGenerateSimilarForm((prev) => ({
                  ...prev,
                  libraryName: e.target.value,
                }))
              }
              disabled={isGeneratingSimilar}
            />
          </div>

          {/* Number of Questions */}
          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label className="form-label">
              Number of Questions to generate{" "}
              <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="number"
              className="form-input"
              placeholder="No of Question to Generate from pdf"
              value={generateSimilarForm.numberOfQs}
              onChange={(e) =>
                setGenerateSimilarForm((prev) => ({
                  ...prev,
                  numberOfQs: e.target.value,
                }))
              }
              disabled={isGeneratingSimilar}
            />
          </div>

          {/* Test Paper PDF */}
          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label className="form-label">
              Reference PDF <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <FileUploadSection
              currentFileUrl={null}
              currentFileName={
                generateSimilarForm.sourceTestPaper
                  ? generateSimilarForm.sourceTestPaper.name
                  : null
              }
              isSubmitting={isGeneratingSimilar}
              accept=".pdf"
              showCapture={false}
              onFileChange={(file) =>
                setGenerateSimilarForm((prev) => {
                  const nameWithoutExt = file
                    ? file.name.substring(0, file.name.lastIndexOf(".")) ||
                    file.name
                    : "";
                  return {
                    ...prev,
                    sourceTestPaper: file,
                    libraryName: nameWithoutExt || prev.libraryName,
                  };
                })
              }
              onRemoveSelectedFile={() =>
                setGenerateSimilarForm((prev) => ({
                  ...prev,
                  sourceTestPaper: null,
                }))
              }
            />
          </div>

          {/* Custom Instructions */}
          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label className="form-label">Custom Instructions (Optional)</label>
            <textarea
              className="form-input"
              style={{ width: "100%", height: "80px", resize: "vertical" }}
              placeholder="e.g., Generate JEE level questions based on these concepts"
              value={generateSimilarForm.customInstructions}
              onChange={(e) =>
                setGenerateSimilarForm((prev) => ({
                  ...prev,
                  customInstructions: e.target.value,
                }))
              }
              disabled={isGeneratingSimilar}
            />
          </div>

          {/* Progress */}
          {isGeneratingSimilar && (
            <div className="extract-pdf-progress" style={{ margin: "20px 0" }}>
              <div
                className="extract-pdf-progress-visual"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <i
                    className="fa-solid fa-circle-notch fa-spin"
                    style={{ color: "#0d9488" }}
                  />
                  <span>{generateSimilarProgress}</span>
                </div>
                <div
                  className="oeb-generate-progress-bar"
                  style={{
                    background: "#cbd5e1",
                    borderRadius: "100px",
                    height: "8px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    className="oeb-generate-progress-fill"
                    style={{
                      background: "linear-gradient(90deg, #14b8a6, #0d9488)",
                      width: "60%",
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div
            className="modal-actions"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              marginTop: "20px",
            }}
          >
            <button
              type="button"
              className="btn-cancel"
              onClick={() => setIsGenerateSimilarOpen(false)}
              disabled={isGeneratingSimilar}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-submit"
              onClick={handleGenerateSimilar}
              disabled={
                isGeneratingSimilar ||
                !String(generateSimilarForm.subject || "").trim() ||
                !String(generateSimilarForm.curriculum || "").trim() ||
                !String(generateSimilarForm.grade || "").trim() ||
                !String(generateSimilarForm.topic || "").trim() ||
                !String(generateSimilarForm.libraryName || "").trim() ||
                !String(generateSimilarForm.numberOfQs || "").trim() ||
                !generateSimilarForm.sourceTestPaper
              }
              style={{
                background:
                  !String(generateSimilarForm.subject || "").trim() ||
                    !String(generateSimilarForm.curriculum || "").trim() ||
                    !String(generateSimilarForm.grade || "").trim() ||
                    !String(generateSimilarForm.topic || "").trim() ||
                    !String(generateSimilarForm.libraryName || "").trim() ||
                    !String(generateSimilarForm.numberOfQs || "").trim() ||
                    !generateSimilarForm.sourceTestPaper ||
                    isGeneratingSimilar
                    ? "#cbd5e1"
                    : "linear-gradient(135deg, #14b8a6, #0d9488)",
                border: "none",
                color: "white",
              }}
            >
              Submit
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Answer Validation Modal ── */}
      <Modal
        isOpen={isValModalOpen}
        onClose={() => setIsValModalOpen(false)}
        maxWidth="100%"
      >
        <div style={{ padding: "0.5rem 0.25rem" }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #e2e8f0",
              paddingBottom: "12px",
              marginBottom: "16px",
            }}
          >
            <h3
              style={{
                fontSize: "1.25rem",
                fontWeight: 700,
                color: "#1e293b",
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i
                className="fa-solid fa-square-check"
                style={{ color: "#16a34a" }}
              />
              Answer Validation
            </h3>
          </div>

          {/* Error Message */}
          {valError && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fee2e2",
                color: "#991b1b",
                padding: "10px 14px",
                borderRadius: "8px",
                fontSize: "0.85rem",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i className="fa-solid fa-triangle-exclamation" />
              <span>{valError}</span>
            </div>
          )}

          {valResults === null ? (
            /* --- STEP 1: Paste JSON --- */
            <div>
              <div style={{ marginBottom: "20px", padding: "12px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
                <p style={{ fontSize: "0.9rem", color: "#334155", marginBottom: "8px", fontWeight: "600" }}>
                  Optional: Upload AI Solutions (CSV)
                </p>
                <p style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: "12px", lineHeight: "1.4" }}>
                  By default, validation compares against the AI Solutions from the DB. 
                  If you upload a CSV file here, the JSON below will be compared against this CSV instead.
                  <br/>
                  <em>Format: First column is 'Question Number', Second column is 'Answer'.</em>
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleValCsvUpload}
                  style={{
                    fontSize: "0.85rem",
                    color: "#475569"
                  }}
                />
                {valCsvFileName && (
                  <p style={{ fontSize: "0.8rem", color: "#16a34a", marginTop: "8px", fontWeight: "500" }}>
                    <i className="fa-solid fa-file-csv" style={{ marginRight: "4px" }} />
                    Loaded: {valCsvFileName} ({Object.keys(valCsvData || {}).length} questions)
                  </p>
                )}
              </div>

              <p style={{ fontSize: "0.9rem", color: "#475569", marginBottom: "12px", lineHeight: "1.5" }}>
                Paste the answer key in JSON format. The keys should be the question numbers (e.g. <code>"1"</code>, <code>"2"</code>) and the values should be the correct answers (e.g. <code>"c"</code>, <code>"11"</code>).
              </p>
              
              <textarea
                value={valJsonInput}
                onChange={(e) => setValJsonInput(e.target.value)}
                placeholder={`{\n  "1": "c",\n  "2": "d",\n  "3": "c"\n}`}
                rows={12}
                style={{
                  width: "100%",
                  fontFamily: "monospace",
                  fontSize: "0.85rem",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  outline: "none",
                  resize: "vertical",
                  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                  backgroundColor: "#f8fafc",
                }}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  marginTop: "16px",
                }}
              >
                <button
                  onClick={() => setIsValModalOpen(false)}
                  style={{
                    backgroundColor: "transparent",
                    border: "1px solid #cbd5e1",
                    color: "#64748b",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "0.85rem",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleValidateAnswerKey}
                  style={{
                    backgroundColor: "#16a34a",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "0.85rem",
                  }}
                >
                  Validate Answers
                </button>
              </div>
            </div>
          ) : (
            /* --- STEP 2: Validation Results --- */
            <div>
              {/* Stats Grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: "12px",
                  marginBottom: "20px",
                }}
              >
                {/* Total Questions */}
                <div
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    padding: "12px",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 500, marginBottom: "4px" }}>
                    Total Questions
                  </div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1e293b" }}>
                    {valResults.stats.total}
                  </div>
                </div>

                {/* Matches */}
                <div
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: "10px",
                    padding: "12px",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "0.8rem", color: "#166534", fontWeight: 500, marginBottom: "4px" }}>
                    Matches
                  </div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#15803d" }}>
                    {valResults.stats.matched}
                  </div>
                </div>

                {/* Mismatches */}
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "10px",
                    padding: "12px",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "0.8rem", color: "#991b1b", fontWeight: 500, marginBottom: "4px" }}>
                    Mismatches
                  </div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#b91c1c" }}>
                    {valResults.stats.mismatched}
                  </div>
                </div>

                {/* Missing in JSON */}
                <div
                  style={{
                    background: "#fffbeb",
                    border: "1px solid #fde68a",
                    borderRadius: "10px",
                    padding: "12px",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "0.8rem", color: "#92400e", fontWeight: 500, marginBottom: "4px" }}>
                    Missing in JSON
                  </div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#d97706" }}>
                    {valResults.stats.missingInJson}
                  </div>
                </div>

                {/* Extra in JSON */}
                {valResults.stats.missingInLibrary > 0 && (
                  <div
                    style={{
                      background: "#faf5ff",
                      border: "1px solid #e9d5ff",
                      borderRadius: "10px",
                      padding: "12px",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "0.8rem", color: "#6b21a8", fontWeight: 500, marginBottom: "4px" }}>
                      Extra in JSON
                    </div>
                    <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#7e22ce" }}>
                      {valResults.stats.missingInLibrary}
                    </div>
                  </div>
                )}
              </div>

              {/* Filters & Actions */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {[
                    { value: "all", label: `All (${valResults.details.length})` },
                    { value: "match", label: `Matches (${valResults.stats.matched})`, color: "#16a34a" },
                    { value: "mismatch", label: `Mismatches (${valResults.stats.mismatched})`, color: "#ef4444" },
                    { value: "missing", label: `Missing in JSON (${valResults.stats.missingInJson})`, color: "#d97706" },
                    { value: "extra", label: `Extra in JSON (${valResults.stats.missingInLibrary})`, color: "#7e22ce", hidden: valResults.stats.missingInLibrary === 0 }
                  ].map((item) => {
                    if (item.hidden) return null;
                    const active = valFilter === item.value;
                    return (
                      <button
                        key={item.value}
                        onClick={() => setValFilter(item.value)}
                        style={{
                          padding: "4px 10px",
                          borderRadius: "20px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          border: "1px solid",
                          borderColor: active ? (item.color || "#475569") : "#cbd5e1",
                          background: active ? (item.color || "#475569") : "transparent",
                          color: active ? "white" : "#64748b",
                          cursor: "pointer",
                          transition: "all 0.1s",
                        }}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => {
                      if (valSelectedQuestions.size === 0) {
                        swalWarning("Please select at least one question.");
                        return;
                      }
                      setInvalidToggleQuestion({ isBatch: true });
                      setInvalidToggleValue(true);
                      setInvalidReasonValue("");
                      setOtherInvalidReasonValue("");
                      setIsInvalidToggleModalOpen(true);
                    }}
                    style={{
                      backgroundColor: "#ef4444",
                      color: "white",
                      border: "none",
                      padding: "6px 14px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "0.85rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <i className="fa-solid fa-triangle-exclamation" />
                    Mark As Invalid
                  </button>
                  <button
                    onClick={handleBatchMarkValid}
                    disabled={isSavingInvalidToggle}
                    style={{
                      backgroundColor: "#16a34a",
                      color: "white",
                      border: "none",
                      padding: "6px 14px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "0.85rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      opacity: isSavingInvalidToggle ? 0.7 : 1,
                    }}
                  >
                    {isSavingInvalidToggle ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-check" />}
                    Mark As Valid
                  </button>
                </div>
              </div>

              {/* Scrollable Results Table */}
              <div
                style={{
                  maxHeight: "350px",
                  overflowY: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "0.85rem",
                    textAlign: "left",
                  }}
                >
                  <thead style={{ background: "#f8fafc", position: "sticky", top: 0, zIndex: 1 }}>
                    <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <th style={{ padding: "10px 12px", width: "40px" }}>
                        <input
                          type="checkbox"
                          checked={
                            valResults.details.filter(d => valFilter === "all" || d.status === valFilter).length > 0 &&
                            valResults.details.filter(d => valFilter === "all" || d.status === valFilter).every(d => !d.qId || valSelectedQuestions.has(d.qId))
                          }
                          onChange={(e) => {
                            const filtered = valResults.details.filter(d => valFilter === "all" || d.status === valFilter);
                            const newSet = new Set(valSelectedQuestions);
                            if (e.target.checked) {
                              filtered.forEach(d => { if (d.qId) newSet.add(d.qId); });
                            } else {
                              filtered.forEach(d => { if (d.qId) newSet.delete(d.qId); });
                            }
                            setValSelectedQuestions(newSet);
                          }}
                        />
                      </th>
                      <th style={{ padding: "10px 12px", width: "80px", color: "#475569", fontWeight: 600 }}>Q. No.</th>
                      <th style={{ padding: "10px 12px", color: "#475569", fontWeight: 600 }}>Type</th>
                      <th style={{ padding: "10px 12px", color: "#475569", fontWeight: 600 }}>Valid</th>
                      <th style={{ padding: "10px 12px", color: "#475569", fontWeight: 600 }}>AI Solution Answer</th>
                      <th style={{ padding: "10px 12px", color: "#475569", fontWeight: 600 }}>Pasted Answer</th>
                      <th style={{ padding: "10px 12px", width: "100px", color: "#475569", fontWeight: 600 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {valResults.details
                      .filter((d) => {
                        if (valFilter === "all") return true;
                        return d.status === valFilter;
                      })
                      .map((d, index) => {
                        let statusBg = "#f8fafc";
                        let statusColor = "#64748b";
                        let statusText = "Unknown";
                        let rowBg = "transparent";

                        if (d.status === "match") {
                          statusBg = "#dcfce7";
                          statusColor = "#166534";
                          statusText = "Match";
                        } else if (d.status === "mismatch") {
                          statusBg = "#fecaca";
                          statusColor = "#991b1b";
                          statusText = "Mismatch";
                          rowBg = "#fff5f5";
                        } else if (d.status === "missing") {
                          statusBg = "#fef3c7";
                          statusColor = "#92400e";
                          statusText = "Missing";
                        } else if (d.status === "extra") {
                          statusBg = "#f3e8ff";
                          statusColor = "#6b21a8";
                          statusText = "Extra";
                        }

                        return (
                          <tr
                            key={index}
                            style={{
                              borderBottom: "1px solid #f1f5f9",
                              background: rowBg,
                              transition: "background 0.2s"
                            }}
                          >
                            <td style={{ padding: "10px 12px" }}>
                              <input
                                type="checkbox"
                                checked={d.qId ? valSelectedQuestions.has(d.qId) : false}
                                disabled={!d.qId}
                                onChange={(e) => {
                                  if (!d.qId) return;
                                  const newSet = new Set(valSelectedQuestions);
                                  if (e.target.checked) {
                                    newSet.add(d.qId);
                                  } else {
                                    newSet.delete(d.qId);
                                  }
                                  setValSelectedQuestions(newSet);
                                }}
                              />
                            </td>
                            <td style={{ padding: "10px 12px", fontWeight: "600", color: "#334155" }}>
                              {d.qNum}
                            </td>
                            <td style={{ padding: "10px 12px", color: "#64748b", textTransform: "uppercase", fontSize: "0.75rem" }}>
                              {d.type}
                            </td>
                            <td style={{ padding: "10px 12px" }}>
                              {d.isInvalid ? (
                                <span title={d.invalidReason} style={{ color: "#ef4444", fontWeight: 600, fontSize: "0.75rem", display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 6px", background: "#fef2f2", borderRadius: "12px", border: "1px solid #fecaca", cursor: "help" }}>
                                  <i className="fa-solid fa-xmark"></i> Invalid
                                </span>
                              ) : (
                                <span style={{ color: "#16a34a", fontWeight: 600, fontSize: "0.75rem", display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 6px", background: "#f0fdf4", borderRadius: "12px", border: "1px solid #bbf7d0" }}>
                                  <i className="fa-solid fa-check"></i> Valid
                                </span>
                              )}
                            </td>
                            <td style={{ padding: "10px 12px", color: "#1e293b", fontWeight: d.status === "match" ? 500 : 400 }}>
                              {d.aiAns}
                            </td>
                            <td style={{ padding: "10px 12px", color: "#1e293b", fontWeight: 500 }}>
                              {d.userAns}
                            </td>
                            <td style={{ padding: "10px 12px" }}>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  background: statusBg,
                                  color: statusColor,
                                }}
                              >
                                {d.status === "match" && <i className="fa-solid fa-circle-check" />}
                                {d.status === "mismatch" && <i className="fa-solid fa-circle-xmark" />}
                                {d.status === "missing" && <i className="fa-solid fa-circle-question" />}
                                {d.status === "extra" && <i className="fa-solid fa-circle-plus" />}
                                {statusText}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    {valResults.details.filter((d) => {
                      if (valFilter === "all") return true;
                      return d.status === valFilter;
                    }).length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ padding: "20px", textAlign: "center", color: "#64748b" }}>
                          No questions matching the selected filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "20px",
                  borderTop: "1px solid #e2e8f0",
                  paddingTop: "16px",
                }}
              >
                <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                  Accuracy: <strong style={{ color: "#1e293b" }}>{((valResults.stats.matched / Math.max(1, valResults.stats.total)) * 100).toFixed(1)}%</strong>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                  }}
                >
                  <button
                    onClick={() => setValResults(null)}
                    style={{
                      backgroundColor: "transparent",
                      border: "1px solid #cbd5e1",
                      color: "#64748b",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "0.85rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <i className="fa-solid fa-arrow-left" />
                    Back
                  </button>
                  <button
                    onClick={() => {
                      setIsValViewActive(true);
                      setIsViewQuestionsModalOpen(true);
                    }}
                    style={{
                      backgroundColor: "var(--color-1)",
                      color: "white",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "0.85rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <i className="fa-solid fa-eye" />
                    View Questions
                  </button>
                  <button
                    onClick={() => setIsValModalOpen(false)}
                    style={{
                      backgroundColor: "#1e293b",
                      color: "white",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "0.85rem",
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* ── View Questions Modal ── */}
      <Modal
        isOpen={isViewQuestionsModalOpen}
        onClose={() => {
          setIsViewQuestionsModalOpen(false);
          setIsValViewActive(false);
          setIsBulkEditMode(false);
          setBulkEditData({});
        }}
        maxWidth="100%"
      >
        <div className="online-exam-builder" style={{ marginTop: "0.5rem" }}>
          {(() => {
            const displayedQs = (() => {
              let qs = selectedQuestionIds.size > 0
                ? libraryQuestions.filter((q) => selectedQuestionIds.has(q._id))
                : libraryQuestions;

              if (isValViewActive && valResults) {
                qs = qs.filter((q) => {
                  const status = valStatusMap.get(q._id);
                  if (valFilter === "all") {
                    return status !== undefined;
                  }
                  return status === valFilter;
                });
              }
              return qs;
            })();
            return (
              <>
                <div className="oeb-header" style={{ marginBottom: "1rem" }}>
                  <h3
                    className="oeb-title"
                    style={{ fontSize: "1.3rem", fontWeight: 700 }}
                  >
                    <i
                      className="fa-solid fa-clipboard-question"
                      style={{ marginRight: "8px", color: "var(--color-1)" }}
                    ></i>
                    Library Questions
                    <span
                      className="oeb-count"
                      style={{
                        marginLeft: "8px",
                        fontSize: "0.95rem",
                        color: "#64748b",
                      }}
                    >
                      ({displayedQs.length} question
                      {displayedQs.length !== 1 ? "s" : ""})
                    </span>
                  </h3>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {isBulkEditMode ? (
                      <>
                        <button
                          onClick={() => {
                            setIsBulkEditMode(false);
                            setBulkEditData({});
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            background: "#ffffff",
                            border: "1px solid #cbd5e1",
                            borderRadius: "8px",
                            padding: "8px 14px",
                            fontSize: "0.85rem",
                            color: "#64748b",
                            fontWeight: 600,
                            cursor: "pointer",
                            transition: "all 0.2s",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f1f5f9";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#ffffff";
                          }}
                        >
                          <i className="fa-solid fa-xmark"></i> Cancel
                        </button>
                        <button
                          onClick={handleSaveBulkQuestions}
                          disabled={!hasBulkChanges}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            background: hasBulkChanges ? "#16a34a" : "#cbd5e1",
                            border: "1px solid transparent",
                            borderRadius: "8px",
                            padding: "8px 14px",
                            fontSize: "0.85rem",
                            color: "#ffffff",
                            fontWeight: 600,
                            cursor: hasBulkChanges ? "pointer" : "not-allowed",
                            transition: "all 0.2s",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                          }}
                        >
                          <i className="fa-solid fa-floppy-disk"></i> Bulk Save
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleStartBulkEdit(displayedQs)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          background: "#ffffff",
                          border: "1px solid #cbd5e1",
                          borderRadius: "8px",
                          padding: "8px 14px",
                          fontSize: "0.85rem",
                          color: "#475569",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f1f5f9";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#ffffff";
                        }}
                      >
                        <i className="fa-solid fa-pen-to-square"></i> Bulk Edit
                      </button>
                    )}

                    {viewingLibrary?.questionSourceFileURL && (
                      <a
                        href={viewingLibrary.questionSourceFileURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          background: "#eff6ff",
                          border: "1px solid rgba(59, 130, 246, 0.2)",
                          borderRadius: "8px",
                          padding: "8px 14px",
                          fontSize: "0.85rem",
                          textDecoration: "none",
                          color: "#2563eb",
                          fontWeight: 600,
                          transition: "all 0.2s",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#dbeafe";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#eff6ff";
                        }}
                      >
                        <i className="fa-solid fa-file-lines"></i> Source File
                      </a>
                    )}
                  </div>
                </div>


                <div
                  style={{
                    paddingRight: "4px",
                    marginBottom: "1rem",
                    maxHeight: "calc(100vh - 200px)",
                    overflowY: "auto",
                  }}
                >
                  {displayedQs.map((q, idx) => (
                    <div
                      key={idx}
                      className="oe-question-card"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.style.border = "2px dashed var(--color-1)";
                        e.currentTarget.style.background = "#f8fafc";
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.currentTarget.style.border = "1px solid #e2e8f0";
                        e.currentTarget.style.background = "#fff";
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.style.border = "1px solid #e2e8f0";
                        e.currentTarget.style.background = "#fff";
                        const file = e.dataTransfer.files[0];
                        if (file && file.type.startsWith("image/")) {
                          setDiagramQuestion(q);
                          setSelectedImageQuestion(q);
                          setSelectedUploadFile(file);
                          handleUploadLibraryDiagramFile(file, q);
                        }
                      }}
                      style={{
                        position: "relative",
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "12px",
                        padding: "1rem 1.25rem",
                        marginBottom: "0.75rem",
                        transition: "all 0.2s ease",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                      }}
                    >
                      {uploadingQuestions[q._id] && (
                        <>
                          <svg
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              pointerEvents: "none",
                              zIndex: 11,
                            }}
                          >
                            <rect
                              style={{
                                x: "1.5px",
                                y: "1.5px",
                                width: "calc(100% - 3px)",
                                height: "calc(100% - 3px)",
                              }}
                              fill="none"
                              rx="12"
                              ry="12"
                              stroke="var(--color-1, #3b82f6)"
                              strokeWidth="3"
                              strokeDasharray="8, 6"
                              className="animated-dashed-border"
                            />
                          </svg>
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              background: "rgba(255, 255, 255, 0.75)",
                              borderRadius: "12px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "0.5rem",
                              zIndex: 10,
                              pointerEvents: "auto",
                            }}
                          >
                            <i
                              className="fa-solid fa-spinner fa-spin"
                              style={{ fontSize: "1.5rem", color: "var(--color-1)" }}
                            />
                            <span
                              style={{
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                color: "#475569",
                              }}
                            >
                              {uploadingQuestions[q._id].progressText}
                            </span>
                          </div>
                        </>
                      )}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: "1rem",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              marginBottom: "0.5rem",
                            }}
                          >
                            <span
                              style={{
                                color: "var(--color-1)",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                opacity: 0.6,
                                minWidth: "20px",
                                textAlign: "right",
                                flexShrink: 0,
                              }}
                            >
                              {(questionIndexMap.get(q) ?? idx) + 1}.
                            </span>
                            <span
                              style={{
                                background:
                                  "linear-gradient(135deg, var(--color-1), var(--color-2))",
                                color: "#fff",
                                borderRadius: "50%",
                                width: "28px",
                                height: "28px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                flexShrink: 0,
                                padding: "0 6px",
                              }}
                            >
                              {q.questionNumber || "" }
                            </span>

                            <div
                              style={{
                                fontSize: "0.9rem",
                                color: "#1e293b",
                                lineHeight: 1.5,
                                overflow: "hidden",
                                flex: 1,
                              }}
                            >
                              {isBulkEditMode ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "4px", width: "100%" }}>
                                  <textarea
                                    value={(bulkEditData[q._id]?.questionText !== undefined) ? bulkEditData[q._id].questionText : q.questionText || ""}
                                    onChange={(e) => {
                                      setBulkEditData((prev) => ({
                                        ...prev,
                                        [q._id]: {
                                          ...prev[q._id],
                                          questionText: e.target.value,
                                        },
                                      }));
                                    }}
                                    style={{
                                      width: "100%",
                                      minHeight: "60px",
                                      padding: "8px 12px",
                                      borderRadius: "8px",
                                      border: "2px solid #cbd5e1",
                                      fontSize: "0.9rem",
                                      fontFamily: "inherit",
                                      resize: "vertical"
                                    }}
                                  />
                                  <div
                                    style={{
                                      padding: "8px 12px",
                                      borderRadius: "8px",
                                      border: "1px dashed #cbd5e1",
                                      background: "#f8fafc",
                                      fontSize: "0.85rem",
                                      color: "#475569",
                                    }}
                                  >
                                    <ReactMarkdown
                                      children={formatLatexText((bulkEditData[q._id]?.questionText !== undefined) ? bulkEditData[q._id].questionText : q.questionText || "")}
                                      remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                      rehypePlugins={[
                                        [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
                                      ]}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <ReactMarkdown
                                  children={formatLatexText(q.questionText)}
                                  remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                  rehypePlugins={[
                                    [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
                                  ]}
                                />
                              )}
                            </div>
                          </div>

                          {/* Invalid Reason Warning */}
                          {isQuestionInvalid(q) && (
                            <div
                              style={{
                                background: q.invalidReason !== "Generated Solution mismatch with given answer key" ? "rgba(255, 237, 213, 0.85)" : "rgba(254, 242, 242, 0.7)",
                                border: q.invalidReason !== "Generated Solution mismatch with given answer key" ? "1.5px solid rgba(249, 115, 22, 0.3)" : "1.5px solid rgba(239, 68, 68, 0.2)",
                                borderRadius: "8px",
                                padding: "8px 12px",
                                marginBottom: "0.75rem",
                                marginLeft: "2.25rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontSize: "0.85rem",
                                color: q.invalidReason !== "Generated Solution mismatch with given answer key" ? "#7c2d12" : "#991b1b",
                              }}
                            >
                              <i
                                className="fa-solid fa-triangle-exclamation"
                                style={{ color: q.invalidReason !== "Generated Solution mismatch with given answer key" ? "#f97316" : "#ef4444", flexShrink: 0 }}
                              />
                              <div>
                                <strong>{q.invalidReason || "This question is marked as invalid."}</strong>
                              </div>
                            </div>
                          )}

                          <div
                            className="diagram-viewer-container"
                            style={{
                              marginTop: "0.5rem",
                              marginBottom: "0.75rem",
                              marginLeft: "2.25rem",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: "0.25rem",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "1rem",
                                flexWrap: "wrap",
                              }}
                            >
                              {q.diagramFileURL || q.svgCode ? (
                                <>
                                  <div
                                    style={{
                                      position: "relative",
                                      border: "1px solid #e2e8f0",
                                      borderRadius: "8px",
                                      overflow: "hidden",
                                      backgroundColor: "#f8fafc",
                                      maxWidth: "100%",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      boxShadow:
                                        "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
                                      cursor: "pointer",
                                      transition:
                                        "transform 0.2s ease, box-shadow 0.2s ease",
                                    }}
                                    onClick={() => {
                                      if (!q.svgCode && q.diagramFileURL)
                                        window.open(q.diagramFileURL, "_blank");
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform =
                                        "scale(1.02)";
                                      e.currentTarget.style.boxShadow =
                                        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform =
                                        "scale(1)";
                                      e.currentTarget.style.boxShadow =
                                        "0 1px 3px 0 rgba(0, 0, 0, 0.05)";
                                    }}
                                    title="Click to view full size image"
                                  >
                                    {q.svgCode ? (
                                      <>
                                        <style>{`
                                      .diagram-viewer-container svg {
                                        max-width: 100% !important;
                                        max-height: 220px !important;
                                        height: auto !important;
                                        width: auto !important;
                                      }
                                    `}</style>
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: q.svgCode,
                                          }}
                                          style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            maxWidth: "100%",
                                            maxHeight: "220px",
                                          }}
                                        />
                                      </>
                                    ) : (
                                      <img
                                        src={q.diagramFileURL}
                                        alt={`Diagram for Question ${q.questionNumber}`}
                                        style={{
                                          maxHeight: "220px",
                                          maxWidth: "100%",
                                          objectFit: "contain",
                                          display: "block",
                                        }}
                                      />
                                    )}
                                  </div>

                                  {/* Actions outside image, top-aligned */}
                                  {(q.diagramPageFileURL ||
                                    q.diagramFileURL ||
                                    q.imageURL) && (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "0.5rem",
                                        }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5rem",
                                            padding: "8px 14px",
                                            background: "#ffffff",
                                            color: "#475569",
                                            border: "1px solid #cbd5e1",
                                            borderRadius: "8px",
                                            cursor: isDeletingDiagram ? "not-allowed" : "pointer",
                                            fontWeight: "600",
                                            fontSize: "0.85rem",
                                            transition: "all 0.2s ease",
                                            opacity: isDeletingDiagram ? 0.6 : 1,
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!isDeletingDiagram) {
                                              e.currentTarget.style.background = "#f1f5f9";
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (!isDeletingDiagram) {
                                              e.currentTarget.style.background = "#ffffff";
                                            }
                                          }}
                                        >
                                          <input
                                            type="file"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            disabled={isDeletingDiagram}
                                            onChange={(e) => {
                                              const file = e.target.files[0];
                                              if (file) {
                                                setDiagramQuestion(q);
                                                setSelectedImageQuestion(q);
                                                handleUploadLibraryDiagramFile(file, q);
                                              }
                                              e.target.value = null;
                                            }}
                                          />
                                          <i className="fa-solid fa-upload"></i>{" "}
                                          Upload Another
                                        </label>
                                        <button
                                          type="button"
                                          onClick={() => handlePasteImage(q)}
                                          disabled={isDeletingDiagram}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5rem",
                                            padding: "8px 14px",
                                            background: "#ffffff",
                                            color: "#475569",
                                            border: "1px solid #cbd5e1",
                                            borderRadius: "8px",
                                            cursor: isDeletingDiagram ? "not-allowed" : "pointer",
                                            fontWeight: "600",
                                            fontSize: "0.85rem",
                                            transition: "all 0.2s ease",
                                            opacity: isDeletingDiagram ? 0.6 : 1,
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!isDeletingDiagram) {
                                              e.currentTarget.style.background = "#f1f5f9";
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (!isDeletingDiagram) {
                                              e.currentTarget.style.background = "#ffffff";
                                            }
                                          }}
                                        >
                                          <i className="fa-solid fa-paste"></i>{" "}
                                          Paste Image
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setDiagramQuestion({
                                              ...q,
                                              isEditMode: true,
                                            });
                                            setIsCropModalOpen(true);
                                          }}
                                          disabled={isDeletingDiagram}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5rem",
                                            padding: "8px 14px",
                                            background: "var(--color-1)",
                                            color: "#ffffff",
                                            border: "none",
                                            borderRadius: "8px",
                                            cursor: isDeletingDiagram ? "not-allowed" : "pointer",
                                            fontWeight: "600",
                                            fontSize: "0.85rem",
                                            boxShadow: isDeletingDiagram
                                              ? "none"
                                              : "0 2px 4px rgba(79, 70, 229, 0.2)",
                                            transition: "all 0.2s ease",
                                            opacity: isDeletingDiagram ? 0.6 : 1,
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!isDeletingDiagram) {
                                              e.currentTarget.style.transform = "translateY(-1px)";
                                              e.currentTarget.style.boxShadow = "0 4px 6px rgba(79, 70, 229, 0.3)";
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (!isDeletingDiagram) {
                                              e.currentTarget.style.transform = "translateY(0)";
                                              e.currentTarget.style.boxShadow = "0 2px 4px rgba(79, 70, 229, 0.2)";
                                            }
                                          }}
                                        >
                                          <i className="fa-solid fa-crop-simple"></i>{" "}
                                          Edit Diagram
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteDiagram(q)}
                                          disabled={isDeletingDiagram}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5rem",
                                            padding: "8px 14px",
                                            background: deletingDiagramId === q._id ? "#f1f5f9" : "#ffffff",
                                            color: deletingDiagramId === q._id ? "#94a3b8" : "#ef4444",
                                            border: deletingDiagramId === q._id ? "1px solid #cbd5e1" : "1px solid #fca5a5",
                                            borderRadius: "8px",
                                            cursor: isDeletingDiagram ? "not-allowed" : "pointer",
                                            fontWeight: "600",
                                            fontSize: "0.85rem",
                                            transition: "all 0.2s ease",
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!isDeletingDiagram) {
                                              e.currentTarget.style.background = "#fef2f2";
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (!isDeletingDiagram) {
                                              e.currentTarget.style.background = "#ffffff";
                                            }
                                          }}
                                        >
                                          <i className={deletingDiagramId === q._id ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-trash"}></i>{" "}
                                          {deletingDiagramId === q._id ? "Deleting..." : "Delete Diagram"}
                                        </button>
                                      </div>
                                    )}
                                </>
                              ) : (
                                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                  <label
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                      padding: "8px 14px",
                                      background: "#ffffff",
                                      color: "#475569",
                                      border: "1px solid #cbd5e1",
                                      borderRadius: "8px",
                                      cursor: "pointer",
                                      fontWeight: "600",
                                      fontSize: "0.85rem",
                                      transition: "all 0.2s ease",
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background =
                                        "#f1f5f9";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background =
                                        "#ffffff";
                                    }}
                                  >
                                    <input
                                      type="file"
                                      accept="image/*"
                                      style={{ display: "none" }}
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          setDiagramQuestion(q);
                                          setSelectedImageQuestion(q);
                                          handleUploadLibraryDiagramFile(file, q);
                                        }
                                        e.target.value = null;
                                      }}
                                    />
                                    <i className="fa-solid fa-upload"></i>{" "}
                                    Upload Diagram
                                  </label>

                                  <button
                                    type="button"
                                    onClick={() => handlePasteImage(q)}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                      padding: "8px 14px",
                                      background: "#ffffff",
                                      color: "#475569",
                                      border: "1px solid #cbd5e1",
                                      borderRadius: "8px",
                                      cursor: "pointer",
                                      fontWeight: "600",
                                      fontSize: "0.85rem",
                                      transition: "all 0.2s ease",
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background =
                                        "#f1f5f9";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background =
                                        "#ffffff";
                                    }}
                                  >
                                    <i className="fa-solid fa-paste"></i>{" "}
                                    Paste Image
                                  </button>

                                  {(q.diagramPageFileURL || q.imageURL) && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setDiagramQuestion({
                                          ...q,
                                          isEditMode: true,
                                        });
                                        setIsCropModalOpen(true);
                                      }}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.5rem",
                                        padding: "8px 14px",
                                        background: "var(--color-1)",
                                        color: "#ffffff",
                                        border: "none",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        fontWeight: "600",
                                        fontSize: "0.85rem",
                                        boxShadow: "0 2px 4px rgba(79, 70, 229, 0.2)",
                                        transition: "all 0.2s ease",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-1px)";
                                        e.currentTarget.style.boxShadow = "0 4px 6px rgba(79, 70, 229, 0.3)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "0 2px 4px rgba(79, 70, 229, 0.2)";
                                      }}
                                    >
                                      <i className="fa-solid fa-crop-simple"></i>{" "}
                                      Edit Diagram
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {q.options && q.options.filter(Boolean).length > 0 ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.6rem",
                                marginLeft: "2.25rem",
                                marginBottom: "1rem",
                              }}
                            >
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: "0.4rem 1rem",
                                }}
                              >
                                {(() => {
                                  const optionsList = isBulkEditMode
                                    ? (bulkEditData[q._id]?.options || q.options || [])
                                    : (q.options || []).filter(Boolean);

                                  return optionsList.map((opt, oi) => {
                                    const optLabel =
                                      opt && typeof opt === "object"
                                        ? opt.key
                                        : opt
                                          ? opt.charAt(0)
                                          : "";
                                    const optText =
                                      opt && typeof opt === "object"
                                        ? opt.text
                                        : opt || "";
                                    const isCorrect =
                                      q.correctOptions &&
                                      q.correctOptions.includes(optLabel);

                                    return (
                                      <div
                                        key={oi}
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "0.4rem",
                                          fontSize: "0.85rem",
                                          color: isCorrect
                                            ? "#16a34a"
                                            : "#475569",
                                          fontWeight: isCorrect ? 600 : 400,
                                          background: isCorrect
                                            ? "#f0fdf4"
                                            : "transparent",
                                          padding: "4px 8px",
                                          borderRadius: "6px",
                                          border: isCorrect
                                            ? "1px solid #bbf7d0"
                                            : "1px solid transparent",
                                          transition: "all 0.2s ease",
                                        }}
                                      >
                                        {isBulkEditMode ? (
                                          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", width: "100%" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                              <span style={{ fontWeight: 700, minWidth: "20px" }}>{optLabel}.</span>
                                              <input
                                                type="text"
                                                value={optText || ""}
                                                onChange={(e) => {
                                                  const nextOptions = [...(bulkEditData[q._id]?.options || [])];
                                                  nextOptions[oi] = {
                                                    ...nextOptions[oi],
                                                    text: e.target.value,
                                                  };
                                                  setBulkEditData((prev) => ({
                                                    ...prev,
                                                    [q._id]: {
                                                      ...prev[q._id],
                                                      options: nextOptions,
                                                    },
                                                  }));
                                                }}
                                                style={{
                                                  flex: 1,
                                                  padding: "6px 10px",
                                                  borderRadius: "6px",
                                                  border: "2px solid #cbd5e1",
                                                  fontSize: "0.85rem",
                                                }}
                                              />
                                            </div>
                                            <div
                                              style={{
                                                marginLeft: "25px",
                                                fontSize: "0.8rem",
                                                color: "#475569",
                                                padding: "4px 8px",
                                                border: "1px dashed #cbd5e1",
                                                background: "#f8fafc",
                                                borderRadius: "4px",
                                                overflowX: "auto"
                                              }}
                                            >
                                              <ReactMarkdown
                                                children={formatLatexText(`${optLabel}) ${optText || ""}`)}
                                                remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                                rehypePlugins={[
                                                  [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
                                                ]}
                                                components={{ p: "span" }}
                                              />
                                            </div>
                                          </div>
                                        ) : (
                                          <span
                                            style={{
                                              lineHeight: 1.4,
                                              width: "100%",
                                              overflow: "hidden",
                                            }}
                                          >
                                            <ReactMarkdown
                                              children={formatLatexText(`${optLabel}) ${optText}`)}
                                              remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                              rehypePlugins={[
                                                [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
                                              ]}
                                              components={{ p: "span" }}
                                            />
                                          </span>
                                        )}
                                      </div>
                                    );
                                  });
                                })()}
                              </div>

                              <div style={{ marginTop: "0.5rem" }}>
                                <label
                                  style={{
                                    fontSize: "0.78rem",
                                    color: "#64748b",
                                    fontWeight: 500,
                                    marginBottom: "0.4rem",
                                    display: "block",
                                  }}
                                >
                                  Correct Answer:
                                </label>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: "1rem",
                                  }}
                                >
                                  <div
                                    style={{ display: "flex", gap: "0.5rem" }}
                                  >
                                    {["A", "B", "C", "D"].map((optLabel) => {
                                      const isCorrect =
                                        q.correctOptions &&
                                        q.correctOptions.includes(optLabel);
                                      return (
                                        <button
                                          key={optLabel}
                                          type="button"
                                          disabled={true}
                                          style={{
                                            padding: "6px 14px",
                                            borderRadius: "20px",
                                            border: isCorrect
                                              ? "1px solid #16a34a"
                                              : "1px solid #cbd5e1",
                                            background: isCorrect
                                              ? "#16a34a"
                                              : "#fff",
                                            color: isCorrect
                                              ? "#fff"
                                              : "#475569",
                                            fontSize: "0.85rem",
                                            fontWeight: 600,
                                            cursor: "default",
                                            transition: "all 0.2s",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.3rem",
                                            opacity: isCorrect ? 1 : 0.6,
                                          }}
                                        >
                                          {optLabel}
                                          {isCorrect && (
                                            <i
                                              className="fa-solid fa-check"
                                              style={{ fontSize: "0.7rem" }}
                                            />
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>

                                  {/* Question metadata tags */}
                                  {(q.type ||
                                    q.subject ||
                                    q.topic ||
                                    q.subTopic ||
                                    q.difficulty ||
                                    q.source) && (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: "0.4rem",
                                          alignItems: "center",
                                        }}
                                      >
                                        {q.type && (
                                          <span
                                            style={{
                                              background: "#f1f5f9",
                                              color: "#475569",
                                              border: "1px solid #cbd5e1",
                                              borderRadius: "16px",
                                              padding: "2px 8px",
                                              fontSize: "0.72rem",
                                              fontWeight: "600",
                                            }}
                                          >
                                            {q.type.toUpperCase()}
                                          </span>
                                        )}
                                        {q.subject && (
                                          <span
                                            style={{
                                              background: "#eff6ff",
                                              color: "#2563eb",
                                              border: "1px solid #bfdbfe",
                                              borderRadius: "16px",
                                              padding: "2px 8px",
                                              fontSize: "0.72rem",
                                              fontWeight: "600",
                                            }}
                                            title={q.subject}
                                          >
                                            {q.subject}
                                          </span>
                                        )}
                                        {q.topic && (
                                          <span
                                            style={{
                                              background: "#faf5ff",
                                              color: "#7c3aed",
                                              border: "1px solid #e9d5ff",
                                              borderRadius: "16px",
                                              padding: "2px 8px",
                                              fontSize: "0.72rem",
                                              fontWeight: "600",
                                            }}
                                            title={q.topic}
                                          >
                                            {q.topic}
                                          </span>
                                        )}
                                        {q.subTopic && (
                                          <span
                                            style={{
                                              background: "#ecfeff",
                                              color: "#0891b2",
                                              border: "1px solid #cffafe",
                                              borderRadius: "16px",
                                              padding: "2px 8px",
                                              fontSize: "0.72rem",
                                              fontWeight: "600",
                                            }}
                                            title={q.subTopic}
                                          >
                                            {q.subTopic}
                                          </span>
                                        )}
                                        {q.difficulty &&
                                          (() => {
                                            const diff = q.difficulty
                                              .toLowerCase()
                                              .trim();
                                            const isEasy = diff === "easy";
                                            const isHard = diff === "hard";
                                            const bg = isEasy
                                              ? "#f0fdf4"
                                              : isHard
                                                ? "#fef2f2"
                                                : "#fff7ed";
                                            const color = isEasy
                                              ? "#16a34a"
                                              : isHard
                                                ? "#dc2626"
                                                : "#ea580c";
                                            const border = isEasy
                                              ? "#bbf7d0"
                                              : isHard
                                                ? "#fecaca"
                                                : "#ffedd5";
                                            return (
                                              <span
                                                style={{
                                                  background: bg,
                                                  color: color,
                                                  border: `1px solid ${border}`,
                                                  borderRadius: "16px",
                                                  padding: "2px 8px",
                                                  fontSize: "0.72rem",
                                                  fontWeight: "600",
                                                }}
                                                title={q.difficulty}
                                              >
                                                {q.difficulty
                                                  .charAt(0)
                                                  .toUpperCase() +
                                                  q.difficulty.slice(1)}
                                              </span>
                                            );
                                          })()}
                                        {q.source && (
                                          <span
                                            style={{
                                              background: "#fdf2f8",
                                              color: "#db2777",
                                              border: "1px solid #fbcfe8",
                                              borderRadius: "16px",
                                              padding: "2px 8px",
                                              fontSize: "0.72rem",
                                              fontWeight: "600",
                                            }}
                                            title={q.source}
                                          >
                                            {q.source}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            // Display numerical/other questions in view mode
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.6rem",
                                marginLeft: "2.25rem",
                                marginBottom: "1rem",
                              }}
                            >
                              <div style={{ marginTop: "0.5rem" }}>
                                <label
                                  style={{
                                    fontSize: "0.78rem",
                                    color: "#64748b",
                                    fontWeight: 500,
                                    marginBottom: "0.4rem",
                                    display: "block",
                                  }}
                                >
                                  Correct Answer:
                                </label>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: "1rem",
                                  }}
                                >
                                  <div
                                    style={{
                                      padding: "6px 14px",
                                      borderRadius: "8px",
                                      border: "1px solid #cbd5e1",
                                      background: "#f8fafc",
                                      color: "#1e293b",
                                      fontSize: "0.85rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {q.correctAnswer || "—"}
                                  </div>

                                  {/* Question metadata tags */}
                                  {(q.type ||
                                    q.subject ||
                                    q.topic ||
                                    q.subTopic ||
                                    q.difficulty ||
                                    q.source) && (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: "0.4rem",
                                          alignItems: "center",
                                        }}
                                      >
                                        {q.type && (
                                          <span
                                            style={{
                                              background: "#f1f5f9",
                                              color: "#475569",
                                              border: "1px solid #cbd5e1",
                                              borderRadius: "16px",
                                              padding: "2px 8px",
                                              fontSize: "0.72rem",
                                              fontWeight: "600",
                                            }}
                                          >
                                            {q.type.toUpperCase()}
                                          </span>
                                        )}
                                        {q.subject && (
                                          <span
                                            style={{
                                              background: "#eff6ff",
                                              color: "#2563eb",
                                              border: "1px solid #bfdbfe",
                                              borderRadius: "16px",
                                              padding: "2px 8px",
                                              fontSize: "0.72rem",
                                              fontWeight: "600",
                                            }}
                                            title={q.subject}
                                          >
                                            {q.subject}
                                          </span>
                                        )}
                                        {q.topic && (
                                          <span
                                            style={{
                                              background: "#faf5ff",
                                              color: "#7c3aed",
                                              border: "1px solid #e9d5ff",
                                              borderRadius: "16px",
                                              padding: "2px 8px",
                                              fontSize: "0.72rem",
                                              fontWeight: "600",
                                            }}
                                            title={q.topic}
                                          >
                                            {q.topic}
                                          </span>
                                        )}
                                        {q.subTopic && (
                                          <span
                                            style={{
                                              background: "#ecfeff",
                                              color: "#0891b2",
                                              border: "1px solid #cffafe",
                                              borderRadius: "16px",
                                              padding: "2px 8px",
                                              fontSize: "0.72rem",
                                              fontWeight: "600",
                                            }}
                                            title={q.subTopic}
                                          >
                                            {q.subTopic}
                                          </span>
                                        )}
                                        {q.difficulty &&
                                          (() => {
                                            const diff = q.difficulty
                                              .toLowerCase()
                                              .trim();
                                            const isEasy = diff === "easy";
                                            const isHard = diff === "hard";
                                            const bg = isEasy
                                              ? "#f0fdf4"
                                              : isHard
                                                ? "#fef2f2"
                                                : "#fff7ed";
                                            const color = isEasy
                                              ? "#16a34a"
                                              : isHard
                                                ? "#dc2626"
                                                : "#ea580c";
                                            const border = isEasy
                                              ? "#bbf7d0"
                                              : isHard
                                                ? "#fecaca"
                                                : "#ffedd5";
                                            return (
                                              <span
                                                style={{
                                                  background: bg,
                                                  color: color,
                                                  border: `1px solid ${border}`,
                                                  borderRadius: "16px",
                                                  padding: "2px 8px",
                                                  fontSize: "0.72rem",
                                                  fontWeight: "600",
                                                }}
                                                title={q.difficulty}
                                              >
                                                {q.difficulty
                                                  .charAt(0)
                                                  .toUpperCase() +
                                                  q.difficulty.slice(1)}
                                              </span>
                                            );
                                          })()}
                                        {q.source && (
                                          <span
                                            style={{
                                              background: "#fdf2f8",
                                              color: "#db2777",
                                              border: "1px solid #fbcfe8",
                                              borderRadius: "16px",
                                              padding: "2px 8px",
                                              fontSize: "0.72rem",
                                              fontWeight: "600",
                                            }}
                                            title={q.source}
                                          >
                                            {q.source}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                </div>
                              </div>
                            </div>
                          )}

                          {isValViewActive && valResults && (() => {
                            const valDetail = valResults.details.find(d => d.qId === q._id);
                            if (valDetail) {
                              return (
                                <div style={{ 
                                  marginTop: "0.75rem", 
                                  marginLeft: "2.25rem",
                                  padding: "0.75rem",
                                  background: "#f8fafc",
                                  borderRadius: "8px",
                                  border: "1px solid #e2e8f0"
                                }}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                    <div style={{ display: "flex", gap: "1rem" }}>
                                      <div style={{ flex: 1 }}>
                                        <span style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>AI Solution Answer</span>
                                        <div style={{ fontSize: "0.9rem", color: "#1e293b", fontWeight: 500 }}>{valDetail.aiAns || "—"}</div>
                                      </div>
                                      <div style={{ flex: 1 }}>
                                        <span style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Pasted Answer</span>
                                        <div style={{ fontSize: "0.9rem", color: valDetail.isMatch ? "#16a34a" : "#dc2626", fontWeight: 600 }}>{valDetail.userAns || "—"}</div>
                                      </div>
                                    </div>
                                    {!valDetail.isMatch && (
                                      <div style={{ marginTop: "0.25rem" }}>
                                        <span style={{ 
                                          background: "#fef2f2", 
                                          color: "#ef4444", 
                                          padding: "2px 8px", 
                                          borderRadius: "12px", 
                                          fontSize: "0.75rem", 
                                          fontWeight: 600,
                                          border: "1px solid #fecaca"
                                        }}>
                                          Mismatch
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        {!isBulkEditMode && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.5rem",
                              flexShrink: 0,
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => handleOpenEditModal(q)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                padding: "8px 14px",
                                background: "#ffffff",
                                color: "#475569",
                                border: "1px solid #cbd5e1",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontWeight: "600",
                                fontSize: "0.85rem",
                                transition: "all 0.2s ease",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#f1f5f9";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#ffffff";
                              }}
                            >
                              <i className="fa-solid fa-pen-to-square"></i> Edit
                            </button>
                            {q.diagramPageFileURL && (
                              <button
                                type="button"
                                onClick={() => window.open(q.diagramPageFileURL, "_blank")}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.5rem",
                                  padding: "8px 14px",
                                  background: "#ffffff",
                                  color: "#475569",
                                  border: "1px solid #cbd5e1",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  fontWeight: "600",
                                  fontSize: "0.85rem",
                                  transition: "all 0.2s ease",
                                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#f1f5f9";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "#ffffff";
                                }}
                              >
                                <i className="fa-solid fa-file-image"></i> Diagram Page
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleOpenInvalidToggleModal(q)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                padding: "8px 14px",
                                background: q.isInvalid ? "rgba(239, 68, 68, 0.1)" : "#ffffff",
                                color: q.isInvalid ? "#ef4444" : "#475569",
                                border: q.isInvalid ? "1px solid #fca5a5" : "1px solid #cbd5e1",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontWeight: "600",
                                fontSize: "0.85rem",
                                transition: "all 0.2s ease",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = q.isInvalid ? "rgba(239, 68, 68, 0.15)" : "#f1f5f9";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = q.isInvalid ? "rgba(239, 68, 68, 0.1)" : "#ffffff";
                              }}
                            >
                              <i className="fa-solid fa-triangle-exclamation"></i> Invalid
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}

          <div
            className="modal-actions"
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "flex-end",
              marginTop: "1.5rem",
              borderTop: "1px solid #e2e8f0",
              paddingTop: "1rem",
            }}
          >
            <button
              type="button"
              className="btn-submit"
              onClick={() => {
                setIsViewQuestionsModalOpen(false);
                setIsValViewActive(false);
                setIsBulkEditMode(false);
                setBulkEditData({});
              }}
            >
              <i className="fa-solid fa-xmark"></i> Close
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Library Progress Modal ── */}
      <Modal
        isOpen={isProgressModalOpen}
        onClose={() => {
          if (!isSavingProgress) {
            setIsProgressModalOpen(false);
            setProgressLibrary(null);
          }
        }}
        maxWidth="550px"
      >
        {progressLibrary && (
          <div>
            <h2
              style={{
                marginBottom: "1.2rem",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <i
                className="fa-solid fa-bars-progress"
                style={{ color: "var(--color-1)" }}
              />
              Audit Progress:{" "}
              {progressLibrary.libraryName || progressLibrary.subject}
            </h2>

            {/* Audit switches */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                background: "#f8fafc",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                marginBottom: "1.5rem",
              }}
            >
              {/* Switch 1: Invalid Questions Resolved */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    color: "#334155",
                  }}
                >
                  Invalid Questions Resolved
                </span>
                {loadingToggles.isInvalidQuestionResolved ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: "24px",
                      paddingRight: "10px",
                    }}
                  >
                    <i
                      className="fa-solid fa-spinner fa-spin"
                      style={{ color: "var(--color-1)", fontSize: "1.1rem" }}
                    />
                  </div>
                ) : (
                  <label className="solution-toggle" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={
                        progressLibrary.progress?.isInvalidQuestionResolved ??
                        false
                      }
                      onChange={() =>
                        handleToggleProgressStage(
                          "isInvalidQuestionResolved",
                          progressLibrary.progress?.isInvalidQuestionResolved ??
                          false,
                        )
                      }
                    />
                    <span className="toggle-track">
                      <span className="toggle-indicator"></span>
                    </span>
                    <span className="toggle-label-text">
                      {progressLibrary.progress?.isInvalidQuestionResolved
                        ? "Yes"
                        : "No"}
                    </span>
                  </label>
                )}
              </div>

              {/* Switch 2: Question Diagrams Reviewed */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    color: "#334155",
                  }}
                >
                  Question Diagrams Reviewed
                </span>
                {loadingToggles.isQuestionDiagramsReviewed ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: "24px",
                      paddingRight: "10px",
                    }}
                  >
                    <i
                      className="fa-solid fa-spinner fa-spin"
                      style={{ color: "var(--color-1)", fontSize: "1.1rem" }}
                    />
                  </div>
                ) : (
                  <label className="solution-toggle" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={
                        progressLibrary.progress?.isQuestionDiagramsReviewed ??
                        false
                      }
                      onChange={() =>
                        handleToggleProgressStage(
                          "isQuestionDiagramsReviewed",
                          progressLibrary.progress
                            ?.isQuestionDiagramsReviewed ?? false,
                        )
                      }
                    />
                    <span className="toggle-track">
                      <span className="toggle-indicator"></span>
                    </span>
                    <span className="toggle-label-text">
                      {progressLibrary.progress?.isQuestionDiagramsReviewed
                        ? "Yes"
                        : "No"}
                    </span>
                  </label>
                )}
              </div>

              {/* Switch 3: Library Ready */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    color: "#334155",
                  }}
                >
                  Library Ready
                </span>
                {loadingToggles.isLibraryReady ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: "24px",
                      paddingRight: "10px",
                    }}
                  >
                    <i
                      className="fa-solid fa-spinner fa-spin"
                      style={{ color: "var(--color-1)", fontSize: "1.1rem" }}
                    />
                  </div>
                ) : (
                  <label className="solution-toggle" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={
                        progressLibrary.progress?.isLibraryReady ?? false
                      }
                      onChange={() =>
                        handleToggleProgressStage(
                          "isLibraryReady",
                          progressLibrary.progress?.isLibraryReady ?? false,
                        )
                      }
                    />
                    <span className="toggle-track">
                      <span className="toggle-indicator"></span>
                    </span>
                    <span className="toggle-label-text">
                      {progressLibrary.progress?.isLibraryReady ? "Yes" : "No"}
                    </span>
                  </label>
                )}
              </div>
            </div>

            {/* Issues Title */}
            <h3
              style={{
                fontSize: "1.05rem",
                fontWeight: 700,
                color: "#1e293b",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i
                className="fa-solid fa-triangle-exclamation"
                style={{ color: "var(--color-1)" }}
              />
              Audit Issues
            </h3>

            {/* Issues feed */}
            <div
              style={{
                maxHeight: "220px",
                overflowY: "auto",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                padding: "12px",
                marginBottom: "1rem",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              {progressLibrary.progress?.issues &&
                progressLibrary.progress.issues.length > 0 ? (
                progressLibrary.progress.issues.map((issue, index) => (
                  <div
                    key={issue._id || index}
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "10px",
                      padding: "12px 16px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "16px",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {/* Left side: Issue Text and Icon */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        flex: 1,
                      }}
                    >
                      <i
                        className={
                          issue.solved
                            ? "fa-solid fa-circle-check"
                            : "fa-regular fa-circle"
                        }
                        style={{
                          color: issue.solved ? "#10b981" : "#ef4444",
                          marginTop: "3px",
                          fontSize: "1.1rem",
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: "0.92rem",
                          color: issue.solved ? "#94a3b8" : "#334155",
                          textDecoration: issue.solved
                            ? "line-through"
                            : "none",
                          whiteSpace: "pre-wrap",
                          lineHeight: "1.4",
                        }}
                      >
                        {issue.text}
                      </span>
                    </div>

                    {/* Right side: Resolve check container */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          margin: 0,
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={issue.solved || false}
                          disabled={isSavingProgress}
                          onChange={() =>
                            handleToggleIssueSolved(
                              issue._id || index,
                              issue.solved || false,
                            )
                          }
                          style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                            accentColor: "#10b981",
                            borderRadius: "4px",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            color: issue.solved ? "#10b981" : "#64748b",
                          }}
                        >
                          {issue.solved ? "Resolved" : "Resolve"}
                        </span>
                      </label>
                    </div>
                  </div>
                ))
              ) : (
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    textAlign: "center",
                    padding: "10px 0",
                  }}
                >
                  No issues added yet.
                </p>
              )}
            </div>

            {/* Add issue input */}
            <form
              onSubmit={handleCreateIssue}
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <textarea
                rows={2}
                value={newIssueText}
                onChange={(e) => setNewIssueText(e.target.value)}
                placeholder="Describe the issue here... Issues can be marked resolved by clicking the checkbox."
                disabled={isSavingProgress}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "2px solid #e2e8f0",
                  fontSize: "0.9rem",
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                }}
              >
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setIsProgressModalOpen(false);
                    setProgressLibrary(null);
                  }}
                  disabled={isSavingProgress}
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={!newIssueText.trim() || isSavingProgress}
                >
                  {isSavingProgress ? "Adding Issue..." : "Add Issue"}
                </button>
              </div>
            </form>
          </div>
        )}
      </Modal>

      {/* Download Options Modal */}
      <Modal
        isOpen={downloadModal.isOpen}
        onClose={() => setDownloadModal({ isOpen: false, libraryId: null, libraryName: null, type: null })}
        title={`Download ${downloadModal.type}`}
      >
        <div style={{ padding: "20px", display: "flex", gap: "15px", justifyContent: "center" }}>
          <button
            className="btn-submit"
            onClick={() => {
              if (downloadModal.type === "AI Solution") {
                handleGenerateAISolutionsPDF(null, downloadModal.libraryId, downloadModal.libraryName);
              } else {
                handleGenerateAnswerKeyPDF(null, downloadModal.libraryId, downloadModal.libraryName);
              }
              setDownloadModal({ isOpen: false, libraryId: null, libraryName: null, type: null });
            }}
          >
            <i className="fa-solid fa-file-pdf" style={{ marginRight: "8px" }}></i>
            PDF Format
          </button>
          <button
            className="btn-submit"
            style={{ background: "#10b981", borderColor: "#10b981" }}
            onClick={() => handleDownloadCSV(downloadModal.libraryId, downloadModal.libraryName, downloadModal.type)}
          >
            <i className="fa-solid fa-file-csv" style={{ marginRight: "8px" }}></i>
            CSV Format
          </button>
        </div>
      </Modal>
    </>
  );
}
